export enum AddressableFolderType {
  BUILDING = 'BUILDING',
  CONSUMPTION_INVOICES = 'CONSUMPTION_INVOICES',
  ECONOMIC_UNIT = 'ECONOMIC_UNIT',
  EMISSION_CERTIFICATES = 'EMISSION_CERTIFICATES',
  ENERGY_CERTIFICATES = 'ENERGY_CERTIFICATES',
  PORTFOLIO = 'PORTFOLIO',
  PORTFOLIOS = 'PORTFOLIOS',
  ROOT = 'ROOT',
}

export enum BuildingActiveStateFilter {
  BOTH = 'BOTH',
  ONLY_ACTIVE = 'ONLY_ACTIVE',
  ONLY_INACTIVE = 'ONLY_INACTIVE',
}

export enum BuildingExportOrderByInputField {
  city = 'city',
  economic_unit_created_at = 'economic_unit_created_at',
  economic_unit_customer_identifier = 'economic_unit_customer_identifier',
  economic_unit_name = 'economic_unit_name',
  has_gml_ids = 'has_gml_ids',
  postal_code = 'postal_code',
  state = 'state',
  street = 'street',
  year_constructed = 'year_constructed',
}

export enum BuildingOrderBy {
  city = 'city',
  co2_costs = 'co2_costs',
  co2_costs_total = 'co2_costs_total',
  co2_emissions = 'co2_emissions',
  co2_emissions_total = 'co2_emissions_total',
  economic_unit_created_at = 'economic_unit_created_at',
  economic_unit_customer_identifier = 'economic_unit_customer_identifier',
  economic_unit_name = 'economic_unit_name',
  efficiency_class_order = 'efficiency_class_order',
  energy_final = 'energy_final',
  energy_final_total = 'energy_final_total',
  energy_primary = 'energy_primary',
  energy_primary_total = 'energy_primary_total',
  eu_taxonomy_compliance_order = 'eu_taxonomy_compliance_order',
  first_stranding_year = 'first_stranding_year',
  has_gml_ids = 'has_gml_ids',
  postal_code = 'postal_code',
  slf_compliant = 'slf_compliant',
  state = 'state',
  street = 'street',
  year_constructed = 'year_constructed',
}

/** Invoice fields that a CBI validation rule can flag. */
export enum CBIInvoiceField {
  amountPayable = 'amountPayable',
  co2FeeGross = 'co2FeeGross',
  invoiceNumber = 'invoiceNumber',
  invoiceReference = 'invoiceReference',
  invoiceType = 'invoiceType',
  lines = 'lines',
  operationalControl = 'operationalControl',
  provider = 'provider',
  totalCostGross = 'totalCostGross',
}

/** Names of invoice-level CBI validation rules. */
export enum CBIInvoiceRuleName {
  amountPayable = 'amountPayable',
  atLeastOneLine = 'atLeastOneLine',
  co2CostsForHeating = 'co2CostsForHeating',
  invoiceNumber = 'invoiceNumber',
  invoiceReferenceForbiddenForRegularInvoice = 'invoiceReferenceForbiddenForRegularInvoice',
  invoiceReferenceRequiredForCancellationOrCorrection = 'invoiceReferenceRequiredForCancellationOrCorrection',
  invoiceType = 'invoiceType',
  noCancellationOrCorrection = 'noCancellationOrCorrection',
  noFeeNoticeCorrection = 'noFeeNoticeCorrection',
  operationalControl = 'operationalControl',
  provider = 'provider',
  totalCostGross = 'totalCostGross',
}

/** Outcome of a single direct-upload ingest attempt. */
export enum CBIInvoiceUploadStatus {
  ALREADY_INGESTED = 'ALREADY_INGESTED',
  DUPLICATE = 'DUPLICATE',
  FAILED = 'FAILED',
  INGESTED = 'INGESTED',
  INVALID_PDF = 'INVALID_PDF',
}

/** Consumption-line fields that a CBI validation rule can flag. */
export enum CBILineField {
  address = 'address',
  consumptionAmount = 'consumptionAmount',
  consumptionEndDate = 'consumptionEndDate',
  consumptionStartDate = 'consumptionStartDate',
  consumptionType = 'consumptionType',
  energySource = 'energySource',
  marketLocationId = 'marketLocationId',
  meterNumber = 'meterNumber',
  referenceArea = 'referenceArea',
  renewableEnergyPercentage = 'renewableEnergyPercentage',
  unit = 'unit',
  wasteSubType = 'wasteSubType',
  waterSubType = 'waterSubType',
}

/** Names of consumption-line-level CBI validation rules. */
export enum CBILineRuleName {
  addressAlwaysThereRule = 'addressAlwaysThereRule',
  consumptionAmountThere = 'consumptionAmountThere',
  consumptionPeriodNotInverted = 'consumptionPeriodNotInverted',
  consumptionTypeThere = 'consumptionTypeThere',
  consumptionUnitMatchesEnergySource = 'consumptionUnitMatchesEnergySource',
  consumptionUnitThere = 'consumptionUnitThere',
  electricityEnergySource = 'electricityEnergySource',
  electricityEnergySourceThere = 'electricityEnergySourceThere',
  electricityUnitCompatible = 'electricityUnitCompatible',
  endDateThere = 'endDateThere',
  heatingEnergySource = 'heatingEnergySource',
  maloIdForElectricity = 'maloIdForElectricity',
  maloIdLength = 'maloIdLength',
  meterIdIsThere = 'meterIdIsThere',
  pieceOnlyForBulkWaste = 'pieceOnlyForBulkWaste',
  referenceAreaThere = 'referenceAreaThere',
  renewableEnergyForDistrictHeating = 'renewableEnergyForDistrictHeating',
  startDateThere = 'startDateThere',
  wasteCollection = 'wasteCollection',
  wasteSubType = 'wasteSubType',
  waterSubType = 'waterSubType',
}

export enum CBISkipKind {
  AREA_RESOLUTION = 'AREA_RESOLUTION',
  EXCLUDED = 'EXCLUDED',
  INVALID_VALUE = 'INVALID_VALUE',
  MISSING_FIELD = 'MISSING_FIELD',
}

/** Whether a CBI validation issue is about a missing or an unexpected value. */
export enum CBIValidationIssueType {
  missing = 'missing',
  unexpected = 'unexpected',
}

export enum CheckAccess {
  READ = 'READ',
  WRITE = 'WRITE',
}

export enum ConsumptionBuildingOrderBy {
  city = 'city',
  co2_intensity = 'co2_intensity',
  co2_tax = 'co2_tax',
  deviation_from_demand = 'deviation_from_demand',
  final_energy = 'final_energy',
  postal_code = 'postal_code',
  primary_energy = 'primary_energy',
  street = 'street',
}

export enum ConsumptionOverviewOrderBy {
  consumption_type = 'consumption_type',
  file_name = 'file_name',
  from = 'from',
  last_edited = 'last_edited',
  provider = 'provider',
  reference_area = 'reference_area',
  source_name = 'source_name',
  status = 'status',
  street = 'street',
  sub_type = 'sub_type',
  to = 'to',
  value = 'value',
}

export enum EconomicUnitsOrderByFields {
  city = 'city',
  economic_unit_created_at = 'economic_unit_created_at',
  economic_unit_customer_identifier = 'economic_unit_customer_identifier',
  economic_unit_name = 'economic_unit_name',
  postal_code = 'postal_code',
  state = 'state',
}

export enum EmissionCertificateDraftOrderBy {
  created_at = 'created_at',
  issuer = 'issuer',
}

export enum EmissionCertificateOrderBy {
  created_at = 'created_at',
  emission_certificate_energy_source_type_id = 'emission_certificate_energy_source_type_id',
  emission_factor = 'emission_factor',
  issuer = 'issuer',
  primary_energy_factor = 'primary_energy_factor',
  valid_until = 'valid_until',
}

export enum EmissionCertificateStatusFilter {
  ALL = 'ALL',
  EXPIRED = 'EXPIRED',
  VALID = 'VALID',
}

/** The type of sheet to export */
export enum ExcelSheetType {
  CRREM_PATH = 'CRREM_PATH',
  ENVELOPE = 'ENVELOPE',
  ESG_ANALYSIS_CONSUMPTION = 'ESG_ANALYSIS_CONSUMPTION',
  ESG_ANALYSIS_DEMAND = 'ESG_ANALYSIS_DEMAND',
  SYSTEM = 'SYSTEM',
}

/** The type of entity to export */
export enum ExportEntityType {
  ACTION_PLAN = 'ACTION_PLAN',
  BUILDING = 'BUILDING',
  SCENARIO = 'SCENARIO',
}

/** The format of the export */
export enum ExportFormat {
  EXCEL = 'EXCEL',
  PDF = 'PDF',
}

export enum ManualConsumptionSourceTypeEnum {
  ESTIMATION = 'ESTIMATION',
  MANUAL_ENTRY = 'MANUAL_ENTRY',
}

export enum OrderDirection {
  asc = 'asc',
  desc = 'desc',
}

/** Admin-settable run state for a smart meter integration. PAUSED stops all scheduled and manual syncing/pulling; ACTIVE resumes it. Distinct from health status, which is only ever set by a real sync attempt. */
export enum SmartMeterIntegrationRunState {
  ACTIVE = 'ACTIVE',
  PAUSED = 'PAUSED',
}

/** Machine-readable reason a window-to-wall ratio pipeline run failed to produce a result, or a non-fatal reason one branch fell short. Surfaced on the pipeline status `errorCode` when `state` is `FAILED`, and on each `result.warnings[].code` when a branch fell short but the run still succeeded. */
export enum WindowToWallRatioErrorCode {
  INVALID_POLYGON = 'INVALID_POLYGON',
  MESH_FETCH_FAILED = 'MESH_FETCH_FAILED',
  NO_MESH_OR_STREETVIEW_FOUND = 'NO_MESH_OR_STREETVIEW_FOUND',
  NO_STREET_VIEW_COVERAGE = 'NO_STREET_VIEW_COVERAGE',
  NO_VIABLE_MESH_FOUND = 'NO_VIABLE_MESH_FOUND',
  STREET_VIEW_FETCH_FAILED = 'STREET_VIEW_FETCH_FAILED',
  UNKNOWN = 'UNKNOWN',
  WINDOW_APPLICATION_FAILED = 'WINDOW_APPLICATION_FAILED',
  WINDOW_DETECTION_FAILED = 'WINDOW_DETECTION_FAILED',
}

/** Lifecycle state of the window-to-wall ratio pipeline's BullMQ child job, forwarded verbatim from BullMQ. `COMPLETED` carries `result`, `FAILED` carries `errorCode`/`errorMessage`, and the rest mean the run is still queued / in flight. */
export enum WindowToWallRatioJobState {
  ACTIVE = 'ACTIVE',
  COMPLETED = 'COMPLETED',
  DELAYED = 'DELAYED',
  FAILED = 'FAILED',
  PRIORITIZED = 'PRIORITIZED',
  WAITING = 'WAITING',
  WAITING_CHILDREN = 'WAITING_CHILDREN',
}

/** unique or primary key constraints on table "action" */
export enum action_constraint {
  /** unique or primary key constraint on columns "id" */
  action_pkey = 'action_pkey',
}

export enum action_mutation_error_enum {
  ACTION_PLAN_INCONSISTENT = 'ACTION_PLAN_INCONSISTENT',
  MUTATION_NOT_POSSIBLE = 'MUTATION_NOT_POSSIBLE',
}

/** unique or primary key constraints on table "action_plan" */
export enum action_plan_constraint {
  /** unique or primary key constraint on columns "scenario_version_id", "building_id" */
  action_plan_building_id_scenario_version_id_key = 'action_plan_building_id_scenario_version_id_key',
  /** unique or primary key constraint on columns "id" */
  action_plan_pkey = 'action_plan_pkey',
  /** unique or primary key constraint on columns "scenario_version_id", "stable_id" */
  action_plan_scenario_version_id_stable_id_key = 'action_plan_scenario_version_id_stable_id_key',
}

/** unique or primary key constraints on table "action_plan_energy_path" */
export enum action_plan_energy_path_constraint {
  /** unique or primary key constraint on columns "action_plan_id", "year" */
  action_plan_energy_path_action_plan_id_year_key = 'action_plan_energy_path_action_plan_id_year_key',
  /** unique or primary key constraint on columns "id" */
  action_plan_energy_path_pkey = 'action_plan_energy_path_pkey',
}

/** select columns of table "action_plan_energy_path" */
export enum action_plan_energy_path_select_column {
  /** column name */
  action_plan_id = 'action_plan_id',
  /** column name */
  area = 'area',
  /** column name */
  created_at = 'created_at',
  /** column name */
  id = 'id',
  /** column name */
  last_action_crrem_co2_emissions = 'last_action_crrem_co2_emissions',
  /** column name */
  last_action_crrem_final_energy = 'last_action_crrem_final_energy',
  /** column name */
  last_action_crrem_primary_energy = 'last_action_crrem_primary_energy',
  /** column name */
  last_action_crrem_stranded = 'last_action_crrem_stranded',
  /** column name */
  last_action_efficiency_class_id = 'last_action_efficiency_class_id',
  /** column name */
  last_action_main_energy_source_type_id = 'last_action_main_energy_source_type_id',
  /** column name */
  last_action_national_energy_law_arbeitshilfe_85_co2_emissions = 'last_action_national_energy_law_arbeitshilfe_85_co2_emissions',
  /** column name */
  last_action_national_energy_law_co2_emissions = 'last_action_national_energy_law_co2_emissions',
  /** column name */
  last_action_national_energy_law_final_energy = 'last_action_national_energy_law_final_energy',
  /** column name */
  last_action_national_energy_law_gemis_co2_emissions = 'last_action_national_energy_law_gemis_co2_emissions',
  /** column name */
  last_action_national_energy_law_primary_energy = 'last_action_national_energy_law_primary_energy',
  /** column name */
  last_action_national_energy_law_uba_co2_emissions = 'last_action_national_energy_law_uba_co2_emissions',
  /** column name */
  renovated_crrem_co2_emissions = 'renovated_crrem_co2_emissions',
  /** column name */
  renovated_crrem_final_energy = 'renovated_crrem_final_energy',
  /** column name */
  renovated_crrem_primary_energy = 'renovated_crrem_primary_energy',
  /** column name */
  renovated_crrem_stranded = 'renovated_crrem_stranded',
  /** column name */
  renovated_efficiency_class_id = 'renovated_efficiency_class_id',
  /** column name */
  renovated_main_energy_source_type_id = 'renovated_main_energy_source_type_id',
  /** column name */
  renovated_national_energy_law_arbeitshilfe_85_co2_emissions = 'renovated_national_energy_law_arbeitshilfe_85_co2_emissions',
  /** column name */
  renovated_national_energy_law_co2_emissions = 'renovated_national_energy_law_co2_emissions',
  /** column name */
  renovated_national_energy_law_final_energy = 'renovated_national_energy_law_final_energy',
  /** column name */
  renovated_national_energy_law_gemis_co2_emissions = 'renovated_national_energy_law_gemis_co2_emissions',
  /** column name */
  renovated_national_energy_law_primary_energy = 'renovated_national_energy_law_primary_energy',
  /** column name */
  renovated_national_energy_law_uba_co2_emissions = 'renovated_national_energy_law_uba_co2_emissions',
  /** column name */
  updated_at = 'updated_at',
  /** column name */
  year = 'year',
}

/** update columns of table "action_plan_energy_path" */
export enum action_plan_energy_path_update_column {
  /** column name */
  action_plan_id = 'action_plan_id',
  /** column name */
  area = 'area',
  /** column name */
  created_at = 'created_at',
  /** column name */
  id = 'id',
  /** column name */
  last_action_crrem_co2_emissions = 'last_action_crrem_co2_emissions',
  /** column name */
  last_action_crrem_final_energy = 'last_action_crrem_final_energy',
  /** column name */
  last_action_crrem_primary_energy = 'last_action_crrem_primary_energy',
  /** column name */
  last_action_crrem_stranded = 'last_action_crrem_stranded',
  /** column name */
  last_action_efficiency_class_id = 'last_action_efficiency_class_id',
  /** column name */
  last_action_main_energy_source_type_id = 'last_action_main_energy_source_type_id',
  /** column name */
  last_action_national_energy_law_arbeitshilfe_85_co2_emissions = 'last_action_national_energy_law_arbeitshilfe_85_co2_emissions',
  /** column name */
  last_action_national_energy_law_co2_emissions = 'last_action_national_energy_law_co2_emissions',
  /** column name */
  last_action_national_energy_law_final_energy = 'last_action_national_energy_law_final_energy',
  /** column name */
  last_action_national_energy_law_gemis_co2_emissions = 'last_action_national_energy_law_gemis_co2_emissions',
  /** column name */
  last_action_national_energy_law_primary_energy = 'last_action_national_energy_law_primary_energy',
  /** column name */
  last_action_national_energy_law_uba_co2_emissions = 'last_action_national_energy_law_uba_co2_emissions',
  /** column name */
  renovated_crrem_co2_emissions = 'renovated_crrem_co2_emissions',
  /** column name */
  renovated_crrem_final_energy = 'renovated_crrem_final_energy',
  /** column name */
  renovated_crrem_primary_energy = 'renovated_crrem_primary_energy',
  /** column name */
  renovated_crrem_stranded = 'renovated_crrem_stranded',
  /** column name */
  renovated_efficiency_class_id = 'renovated_efficiency_class_id',
  /** column name */
  renovated_main_energy_source_type_id = 'renovated_main_energy_source_type_id',
  /** column name */
  renovated_national_energy_law_arbeitshilfe_85_co2_emissions = 'renovated_national_energy_law_arbeitshilfe_85_co2_emissions',
  /** column name */
  renovated_national_energy_law_co2_emissions = 'renovated_national_energy_law_co2_emissions',
  /** column name */
  renovated_national_energy_law_final_energy = 'renovated_national_energy_law_final_energy',
  /** column name */
  renovated_national_energy_law_gemis_co2_emissions = 'renovated_national_energy_law_gemis_co2_emissions',
  /** column name */
  renovated_national_energy_law_primary_energy = 'renovated_national_energy_law_primary_energy',
  /** column name */
  renovated_national_energy_law_uba_co2_emissions = 'renovated_national_energy_law_uba_co2_emissions',
  /** column name */
  updated_at = 'updated_at',
  /** column name */
  year = 'year',
}

/** Action Plan Impact Trends Annotations */
export enum action_plan_impact_trends_annotation_enum {
  ACTION = 'ACTION',
  AFTER_ACTIONS_STRANDING_YEAR = 'AFTER_ACTIONS_STRANDING_YEAR',
  BEFORE_ACTIONS_STRANDING_YEAR = 'BEFORE_ACTIONS_STRANDING_YEAR',
}

/** Type of action plan impact trends path (either with or without target path and stranding ranges) */
export enum action_plan_impact_type_enum {
  WITHOUT_TARGET_AND_STRANDING_RANGES = 'WITHOUT_TARGET_AND_STRANDING_RANGES',
  WITH_TARGET_AND_STRANDING_RANGES = 'WITH_TARGET_AND_STRANDING_RANGES',
}

/** select columns of table "action_plan" */
export enum action_plan_select_column {
  /** column name */
  building_id = 'building_id',
  /** column name */
  created_at = 'created_at',
  /** column name */
  id = 'id',
  /** column name */
  processing = 'processing',
  /** column name */
  scenario_version_id = 'scenario_version_id',
  /** column name */
  stable_id = 'stable_id',
}

/** select "action_plan_aggregate_bool_exp_bool_and_arguments_columns" columns of table "action_plan" */
export enum action_plan_select_column_action_plan_aggregate_bool_exp_bool_and_arguments_columns {
  /** column name */
  processing = 'processing',
}

/** select "action_plan_aggregate_bool_exp_bool_or_arguments_columns" columns of table "action_plan" */
export enum action_plan_select_column_action_plan_aggregate_bool_exp_bool_or_arguments_columns {
  /** column name */
  processing = 'processing',
}

/** update columns of table "action_plan" */
export enum action_plan_update_column {
  /** column name */
  building_id = 'building_id',
  /** column name */
  created_at = 'created_at',
  /** column name */
  id = 'id',
  /** column name */
  processing = 'processing',
  /** column name */
  scenario_version_id = 'scenario_version_id',
  /** column name */
  stable_id = 'stable_id',
}

/** select columns of table "action" */
export enum action_select_column {
  /** column name */
  action_plan_id = 'action_plan_id',
  /** column name */
  bulk_action_id = 'bulk_action_id',
  /** column name */
  created_at = 'created_at',
  /** column name */
  custom_action_type_id = 'custom_action_type_id',
  /** column name */
  custom_name = 'custom_name',
  /** column name */
  custom_saving_fraction = 'custom_saving_fraction',
  /** column name */
  id = 'id',
  /** column name */
  implementation_from = 'implementation_from',
  /** column name */
  implementation_to = 'implementation_to',
  /** column name */
  order = 'order',
  /** column name */
  params = 'params',
  /** column name */
  updated_at = 'updated_at',
}

/** unique or primary key constraints on table "action_subsidy" */
export enum action_subsidy_constraint {
  /** unique or primary key constraint on columns "id" */
  action_subsidy_pkey = 'action_subsidy_pkey',
}

/** select columns of table "action_subsidy" */
export enum action_subsidy_select_column {
  /** column name */
  action_id = 'action_id',
  /** column name */
  id = 'id',
  /** column name */
  subsidy_id = 'subsidy_id',
  /** column name */
  type = 'type',
  /** column name */
  value = 'value',
}

/** update columns of table "action_subsidy" */
export enum action_subsidy_update_column {
  /** column name */
  action_id = 'action_id',
  /** column name */
  id = 'id',
  /** column name */
  subsidy_id = 'subsidy_id',
  /** column name */
  type = 'type',
  /** column name */
  value = 'value',
}

/** unique or primary key constraints on table "action_subsidy_value_type" */
export enum action_subsidy_value_type_constraint {
  /** unique or primary key constraint on columns "id" */
  action_subsidy_value_type_pkey = 'action_subsidy_value_type_pkey',
}

export enum action_subsidy_value_type_enum {
  absolute = 'absolute',
  percentage = 'percentage',
}

/** select columns of table "action_subsidy_value_type" */
export enum action_subsidy_value_type_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "action_subsidy_value_type" */
export enum action_subsidy_value_type_update_column {
  /** column name */
  id = 'id',
}

/** update columns of table "action" */
export enum action_update_column {
  /** column name */
  action_plan_id = 'action_plan_id',
  /** column name */
  bulk_action_id = 'bulk_action_id',
  /** column name */
  created_at = 'created_at',
  /** column name */
  custom_action_type_id = 'custom_action_type_id',
  /** column name */
  custom_name = 'custom_name',
  /** column name */
  custom_saving_fraction = 'custom_saving_fraction',
  /** column name */
  id = 'id',
  /** column name */
  implementation_from = 'implementation_from',
  /** column name */
  implementation_to = 'implementation_to',
  /** column name */
  order = 'order',
  /** column name */
  params = 'params',
  /** column name */
  updated_at = 'updated_at',
}

/** select columns of table "active_action_plans" */
export enum active_action_plans_select_column {
  /** column name */
  action_plan_id = 'action_plan_id',
  /** column name */
  building_id = 'building_id',
  /** column name */
  scenario_id = 'scenario_id',
}

/** select columns of table "active_bulk_actions" */
export enum active_bulk_actions_select_column {
  /** column name */
  bulk_action_id = 'bulk_action_id',
  /** column name */
  scenario_id = 'scenario_id',
}

/** unique or primary key constraints on table "address" */
export enum address_constraint {
  /** unique or primary key constraint on columns "id" */
  address_pkey = 'address_pkey',
}

/** select columns of table "address" */
export enum address_select_column {
  /** column name */
  building_citydb_gml_ids = 'building_citydb_gml_ids',
  /** column name */
  building_lod1_ids = 'building_lod1_ids',
  /** column name */
  city = 'city',
  /** column name */
  climate_region_id = 'climate_region_id',
  /** column name */
  country_id = 'country_id',
  /** column name */
  country_state_id = 'country_state_id',
  /** column name */
  id = 'id',
  /** column name */
  latitude = 'latitude',
  /** column name */
  longitude = 'longitude',
  /** column name */
  mapbox_bearing = 'mapbox_bearing',
  /** column name */
  mapbox_building_ids = 'mapbox_building_ids',
  /** column name */
  mapbox_pitch = 'mapbox_pitch',
  /** column name */
  mapbox_zoom = 'mapbox_zoom',
  /** column name */
  neighbor_citydb_gml_ids = 'neighbor_citydb_gml_ids',
  /** column name */
  neighbor_lod1_ids = 'neighbor_lod1_ids',
  /** column name */
  postal_code = 'postal_code',
  /** column name */
  street = 'street',
}

/** update columns of table "address" */
export enum address_update_column {
  /** column name */
  building_citydb_gml_ids = 'building_citydb_gml_ids',
  /** column name */
  building_lod1_ids = 'building_lod1_ids',
  /** column name */
  city = 'city',
  /** column name */
  climate_region_id = 'climate_region_id',
  /** column name */
  country_id = 'country_id',
  /** column name */
  country_state_id = 'country_state_id',
  /** column name */
  id = 'id',
  /** column name */
  latitude = 'latitude',
  /** column name */
  longitude = 'longitude',
  /** column name */
  mapbox_bearing = 'mapbox_bearing',
  /** column name */
  mapbox_building_ids = 'mapbox_building_ids',
  /** column name */
  mapbox_pitch = 'mapbox_pitch',
  /** column name */
  mapbox_zoom = 'mapbox_zoom',
  /** column name */
  neighbor_citydb_gml_ids = 'neighbor_citydb_gml_ids',
  /** column name */
  neighbor_lod1_ids = 'neighbor_lod1_ids',
  /** column name */
  postal_code = 'postal_code',
  /** column name */
  street = 'street',
}

/** unique or primary key constraints on table "alpha_prompt_interaction" */
export enum alpha_prompt_interaction_constraint {
  /** unique or primary key constraint on columns "id" */
  alpha_prompt_interaction_pkey = 'alpha_prompt_interaction_pkey',
}

/** select columns of table "alpha_prompt_interaction" */
export enum alpha_prompt_interaction_select_column {
  /** column name */
  analysis_id = 'analysis_id',
  /** column name */
  analysis_started_at = 'analysis_started_at',
  /** column name */
  created_at = 'created_at',
  /** column name */
  extracted_raw_data = 'extracted_raw_data',
  /** column name */
  filename = 'filename',
  /** column name */
  id = 'id',
  /** column name */
  snapshot_id = 'snapshot_id',
  /** column name */
  started_at = 'started_at',
  /** column name */
  uploaded_at = 'uploaded_at',
}

/** update columns of table "alpha_prompt_interaction" */
export enum alpha_prompt_interaction_update_column {
  /** column name */
  analysis_id = 'analysis_id',
  /** column name */
  analysis_started_at = 'analysis_started_at',
  /** column name */
  created_at = 'created_at',
  /** column name */
  extracted_raw_data = 'extracted_raw_data',
  /** column name */
  filename = 'filename',
  /** column name */
  id = 'id',
  /** column name */
  snapshot_id = 'snapshot_id',
  /** column name */
  started_at = 'started_at',
  /** column name */
  uploaded_at = 'uploaded_at',
}

export enum analysis_reference_area_enum {
  BGF = 'BGF',
  EBF = 'EBF',
  MF = 'MF',
  NGF = 'NGF',
}

/** unique or primary key constraints on table "api_response_status" */
export enum api_response_status_constraint {
  /** unique or primary key constraint on columns "id" */
  api_response_status_pkey = 'api_response_status_pkey',
}

export enum api_response_status_enum {
  /** The 3rd party service has determined the final response */
  COMPLETED = 'COMPLETED',
  /** The request has somehow failed. */
  FAILED = 'FAILED',
  /** The asset has been created but no asynchronous request has yet been initialized. */
  NOT_INITIALIZED = 'NOT_INITIALIZED',
  /** The request has been issued but no final response or error has been returned yet. */
  PENDING = 'PENDING',
}

/** select columns of table "api_response_status" */
export enum api_response_status_select_column {
  /** column name */
  comment = 'comment',
  /** column name */
  id = 'id',
}

/** update columns of table "api_response_status" */
export enum api_response_status_update_column {
  /** column name */
  comment = 'comment',
  /** column name */
  id = 'id',
}

/** unique or primary key constraints on table "api_user" */
export enum api_user_constraint {
  /** unique or primary key constraint on columns "auth0_client_id" */
  api_user_auth0_client_id_key = 'api_user_auth0_client_id_key',
  /** unique or primary key constraint on columns "id" */
  api_user_pkey = 'api_user_pkey',
}

/** select columns of table "api_user" */
export enum api_user_select_column {
  /** column name */
  auth0_client_id = 'auth0_client_id',
  /** column name */
  id = 'id',
  /** column name */
  org_id = 'org_id',
  /** column name */
  role_id = 'role_id',
}

/** update columns of table "api_user" */
export enum api_user_update_column {
  /** column name */
  auth0_client_id = 'auth0_client_id',
  /** column name */
  id = 'id',
  /** column name */
  org_id = 'org_id',
  /** column name */
  role_id = 'role_id',
}

/** unique or primary key constraints on table "area" */
export enum area_constraint {
  /** unique or primary key constraint on columns "building_id", "description" */
  area_description_building_id_key = 'area_description_building_id_key',
  /** unique or primary key constraint on columns "id", "building_id" */
  area_id_building_id_unique = 'area_id_building_id_unique',
  /** unique or primary key constraint on columns "id" */
  area_pkey = 'area_pkey',
}

/** select columns of table "area" */
export enum area_select_column {
  /** column name */
  area_system_id = 'area_system_id',
  /** column name */
  area_type_id = 'area_type_id',
  /** column name */
  building_id = 'building_id',
  /** column name */
  can_be_rented = 'can_be_rented',
  /** column name */
  class_of_use_id = 'class_of_use_id',
  /** column name */
  data_source_id = 'data_source_id',
  /** column name */
  description = 'description',
  /** column name */
  energy_certificate_id = 'energy_certificate_id',
  /** column name */
  energy_rating_score = 'energy_rating_score',
  /** column name */
  enev_year = 'enev_year',
  /** column name */
  external_id = 'external_id',
  /** column name */
  file_id = 'file_id',
  /** column name */
  geg_reference_value = 'geg_reference_value',
  /** column name */
  id = 'id',
  /** column name */
  is_rented = 'is_rented',
  /** column name */
  reference_value_electricity = 'reference_value_electricity',
  /** column name */
  reference_value_heating_hot_water = 'reference_value_heating_hot_water',
  /** column name */
  type_of_use_id = 'type_of_use_id',
  /** column name */
  value = 'value',
}

/** select "area_aggregate_bool_exp_avg_arguments_columns" columns of table "area" */
export enum area_select_column_area_aggregate_bool_exp_avg_arguments_columns {
  /** column name */
  value = 'value',
}

/** select "area_aggregate_bool_exp_bool_and_arguments_columns" columns of table "area" */
export enum area_select_column_area_aggregate_bool_exp_bool_and_arguments_columns {
  /** column name */
  can_be_rented = 'can_be_rented',
  /** column name */
  is_rented = 'is_rented',
}

/** select "area_aggregate_bool_exp_bool_or_arguments_columns" columns of table "area" */
export enum area_select_column_area_aggregate_bool_exp_bool_or_arguments_columns {
  /** column name */
  can_be_rented = 'can_be_rented',
  /** column name */
  is_rented = 'is_rented',
}

/** select "area_aggregate_bool_exp_corr_arguments_columns" columns of table "area" */
export enum area_select_column_area_aggregate_bool_exp_corr_arguments_columns {
  /** column name */
  value = 'value',
}

/** select "area_aggregate_bool_exp_covar_samp_arguments_columns" columns of table "area" */
export enum area_select_column_area_aggregate_bool_exp_covar_samp_arguments_columns {
  /** column name */
  value = 'value',
}

/** select "area_aggregate_bool_exp_max_arguments_columns" columns of table "area" */
export enum area_select_column_area_aggregate_bool_exp_max_arguments_columns {
  /** column name */
  value = 'value',
}

/** select "area_aggregate_bool_exp_min_arguments_columns" columns of table "area" */
export enum area_select_column_area_aggregate_bool_exp_min_arguments_columns {
  /** column name */
  value = 'value',
}

/** select "area_aggregate_bool_exp_stddev_samp_arguments_columns" columns of table "area" */
export enum area_select_column_area_aggregate_bool_exp_stddev_samp_arguments_columns {
  /** column name */
  value = 'value',
}

/** select "area_aggregate_bool_exp_sum_arguments_columns" columns of table "area" */
export enum area_select_column_area_aggregate_bool_exp_sum_arguments_columns {
  /** column name */
  value = 'value',
}

/** select "area_aggregate_bool_exp_var_samp_arguments_columns" columns of table "area" */
export enum area_select_column_area_aggregate_bool_exp_var_samp_arguments_columns {
  /** column name */
  value = 'value',
}

/** unique or primary key constraints on table "area_system_type" */
export enum area_system_type_constraint {
  /** unique or primary key constraint on columns "id" */
  area_system_type_pkey = 'area_system_type_pkey',
}

export enum area_system_type_enum {
  PREDIUM_2024 = 'PREDIUM_2024',
}

/** select columns of table "area_system_type" */
export enum area_system_type_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "area_system_type" */
export enum area_system_type_update_column {
  /** column name */
  id = 'id',
}

/** unique or primary key constraints on table "area_type" */
export enum area_type_constraint {
  /** unique or primary key constraint on columns "id" */
  area_type_pkey = 'area_type_pkey',
}

export enum area_type_enum {
  AF = 'AF',
  BGF = 'BGF',
  EBF = 'EBF',
  IPMS_2 = 'IPMS_2',
  MF = 'MF',
  NGF = 'NGF',
}

/** select columns of table "area_type" */
export enum area_type_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "area_type" */
export enum area_type_update_column {
  /** column name */
  id = 'id',
}

/** update columns of table "area" */
export enum area_update_column {
  /** column name */
  area_system_id = 'area_system_id',
  /** column name */
  area_type_id = 'area_type_id',
  /** column name */
  building_id = 'building_id',
  /** column name */
  can_be_rented = 'can_be_rented',
  /** column name */
  class_of_use_id = 'class_of_use_id',
  /** column name */
  data_source_id = 'data_source_id',
  /** column name */
  description = 'description',
  /** column name */
  energy_certificate_id = 'energy_certificate_id',
  /** column name */
  energy_rating_score = 'energy_rating_score',
  /** column name */
  enev_year = 'enev_year',
  /** column name */
  external_id = 'external_id',
  /** column name */
  file_id = 'file_id',
  /** column name */
  geg_reference_value = 'geg_reference_value',
  /** column name */
  id = 'id',
  /** column name */
  is_rented = 'is_rented',
  /** column name */
  reference_value_electricity = 'reference_value_electricity',
  /** column name */
  reference_value_heating_hot_water = 'reference_value_heating_hot_water',
  /** column name */
  type_of_use_id = 'type_of_use_id',
  /** column name */
  value = 'value',
}

/** unique or primary key constraints on table "async_job" */
export enum async_job_constraint {
  /** unique or primary key constraint on columns "id" */
  async_job_pkey = 'async_job_pkey',
}

/** select columns of table "async_job" */
export enum async_job_select_column {
  /** column name */
  created_at = 'created_at',
  /** column name */
  description = 'description',
  /** column name */
  entity_id = 'entity_id',
  /** column name */
  id = 'id',
  /** column name */
  org_id = 'org_id',
  /** column name */
  status_id = 'status_id',
  /** column name */
  title = 'title',
  /** column name */
  total_jobs_created = 'total_jobs_created',
  /** column name */
  total_jobs_finished = 'total_jobs_finished',
  /** column name */
  updated_at = 'updated_at',
  /** column name */
  user_id = 'user_id',
}

/** unique or primary key constraints on table "async_job_status_type" */
export enum async_job_status_type_constraint {
  /** unique or primary key constraint on columns "id" */
  async_job_status_type_pkey = 'async_job_status_type_pkey',
}

export enum async_job_status_type_enum {
  COMPLETED = 'COMPLETED',
  ERROR = 'ERROR',
  PENDING = 'PENDING',
}

/** select columns of table "async_job_status_type" */
export enum async_job_status_type_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "async_job_status_type" */
export enum async_job_status_type_update_column {
  /** column name */
  id = 'id',
}

/** update columns of table "async_job" */
export enum async_job_update_column {
  /** column name */
  created_at = 'created_at',
  /** column name */
  description = 'description',
  /** column name */
  entity_id = 'entity_id',
  /** column name */
  id = 'id',
  /** column name */
  org_id = 'org_id',
  /** column name */
  status_id = 'status_id',
  /** column name */
  title = 'title',
  /** column name */
  total_jobs_created = 'total_jobs_created',
  /** column name */
  total_jobs_finished = 'total_jobs_finished',
  /** column name */
  updated_at = 'updated_at',
  /** column name */
  user_id = 'user_id',
}

/** unique or primary key constraints on table "audit_trail" */
export enum audit_trail_constraint {
  /** unique or primary key constraint on columns "id" */
  audit_trail_pkey = 'audit_trail_pkey',
}

/** select columns of table "audit_trail" */
export enum audit_trail_select_column {
  /** column name */
  action_timestamp = 'action_timestamp',
  /** column name */
  changed_fields = 'changed_fields',
  /** column name */
  entity_id = 'entity_id',
  /** column name */
  id = 'id',
  /** column name */
  operation = 'operation',
  /** column name */
  org_id = 'org_id',
  /** column name */
  role_id = 'role_id',
  /** column name */
  table_name = 'table_name',
  /** column name */
  trace_id = 'trace_id',
  /** column name */
  user_id = 'user_id',
}

/** update columns of table "audit_trail" */
export enum audit_trail_update_column {
  /** column name */
  action_timestamp = 'action_timestamp',
  /** column name */
  changed_fields = 'changed_fields',
  /** column name */
  entity_id = 'entity_id',
  /** column name */
  id = 'id',
  /** column name */
  operation = 'operation',
  /** column name */
  org_id = 'org_id',
  /** column name */
  role_id = 'role_id',
  /** column name */
  table_name = 'table_name',
  /** column name */
  trace_id = 'trace_id',
  /** column name */
  user_id = 'user_id',
}

/** unique or primary key constraints on table "billable_metric" */
export enum billable_metric_constraint {
  /** unique or primary key constraint on columns "id" */
  billable_metric_pkey = 'billable_metric_pkey',
}

/** Generated enum from hasura. Look at the corresponding TS enum for description. */
export enum billable_metric_enum {
  AREA_SQM = 'AREA_SQM',
  BUILDINGS_COUNT = 'BUILDINGS_COUNT',
  RESIDENTIAL_UNIT_COUNT = 'RESIDENTIAL_UNIT_COUNT',
  TOKENS = 'TOKENS',
}

/** select columns of table "billable_metric" */
export enum billable_metric_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "billable_metric" */
export enum billable_metric_update_column {
  /** column name */
  id = 'id',
}

/** unique or primary key constraints on table "billable_module" */
export enum billable_module_constraint {
  /** unique or primary key constraint on columns "id" */
  billable_module_pkey = 'billable_module_pkey',
}

/** Generated enum from hasura. Look at the corresponding TS enum for description. */
export enum billable_module_enum {
  CLIMATE_RISK = 'CLIMATE_RISK',
  PORTFOLIO_ANALYSIS = 'PORTFOLIO_ANALYSIS',
  TRANSACTION_ANALYSIS = 'TRANSACTION_ANALYSIS',
}

/** select columns of table "billable_module" */
export enum billable_module_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "billable_module" */
export enum billable_module_update_column {
  /** column name */
  id = 'id',
}

/** unique or primary key constraints on table "billing_cadence" */
export enum billing_cadence_constraint {
  /** unique or primary key constraint on columns "id" */
  billing_cadence_pkey = 'billing_cadence_pkey',
}

/** Generated enum from hasura. Look at the corresponding TS enum for description. */
export enum billing_cadence_enum {
  ANNUAL = 'ANNUAL',
  CONTRACT = 'CONTRACT',
  MONTHLY = 'MONTHLY',
}

/** select columns of table "billing_cadence" */
export enum billing_cadence_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "billing_cadence" */
export enum billing_cadence_update_column {
  /** column name */
  id = 'id',
}

/** unique or primary key constraints on table "billing_period" */
export enum billing_period_constraint {
  /** unique or primary key constraint on columns "period_index", "module_config_id" */
  billing_period_module_config_id_period_index_key = 'billing_period_module_config_id_period_index_key',
  /** unique or primary key constraint on columns "id" */
  billing_period_pkey = 'billing_period_pkey',
}

/** select columns of table "billing_period" */
export enum billing_period_select_column {
  /** column name */
  base_limit = 'base_limit',
  /** column name */
  created_at = 'created_at',
  /** column name */
  ends_at = 'ends_at',
  /** column name */
  id = 'id',
  /** column name */
  module_config_id = 'module_config_id',
  /** column name */
  period_index = 'period_index',
  /** column name */
  starts_at = 'starts_at',
  /** column name */
  updated_at = 'updated_at',
}

/** update columns of table "billing_period" */
export enum billing_period_update_column {
  /** column name */
  base_limit = 'base_limit',
  /** column name */
  created_at = 'created_at',
  /** column name */
  ends_at = 'ends_at',
  /** column name */
  id = 'id',
  /** column name */
  module_config_id = 'module_config_id',
  /** column name */
  period_index = 'period_index',
  /** column name */
  starts_at = 'starts_at',
  /** column name */
  updated_at = 'updated_at',
}

/** unique or primary key constraints on table "building" */
export enum building_constraint {
  /** unique or primary key constraint on columns "id" */
  building_pkey = 'building_pkey',
}

/** unique or primary key constraints on table "building_district_heating_availability" */
export enum building_district_heating_availability_constraint {
  /** unique or primary key constraint on columns "building_id" */
  building_district_heating_availability_pkey = 'building_district_heating_availability_pkey',
}

/** select columns of table "building_district_heating_availability" */
export enum building_district_heating_availability_select_column {
  /** column name */
  availability_year = 'availability_year',
  /** column name */
  building_id = 'building_id',
  /** column name */
  created_at = 'created_at',
  /** column name */
  updated_at = 'updated_at',
}

/** update columns of table "building_district_heating_availability" */
export enum building_district_heating_availability_update_column {
  /** column name */
  availability_year = 'availability_year',
  /** column name */
  building_id = 'building_id',
  /** column name */
  created_at = 'created_at',
  /** column name */
  updated_at = 'updated_at',
}

/** unique or primary key constraints on table "building_draft_area" */
export enum building_draft_area_constraint {
  /** unique or primary key constraint on columns "id" */
  building_draft_area_pkey = 'building_draft_area_pkey',
}

/** select columns of table "building_draft_area" */
export enum building_draft_area_select_column {
  /** column name */
  area_type_id = 'area_type_id',
  /** column name */
  building_draft_id = 'building_draft_id',
  /** column name */
  energy_certificate_id = 'energy_certificate_id',
  /** column name */
  energy_rating_score = 'energy_rating_score',
  /** column name */
  enev_year = 'enev_year',
  /** column name */
  geg_reference_value = 'geg_reference_value',
  /** column name */
  id = 'id',
  /** column name */
  name = 'name',
  /** column name */
  reference_value_electricity = 'reference_value_electricity',
  /** column name */
  reference_value_heating_hot_water = 'reference_value_heating_hot_water',
  /** column name */
  type_of_use_id = 'type_of_use_id',
  /** column name */
  value = 'value',
}

/** select "building_draft_area_aggregate_bool_exp_avg_arguments_columns" columns of table "building_draft_area" */
export enum building_draft_area_select_column_building_draft_area_aggregate_bool_exp_avg_arguments_columns {
  /** column name */
  value = 'value',
}

/** select "building_draft_area_aggregate_bool_exp_corr_arguments_columns" columns of table "building_draft_area" */
export enum building_draft_area_select_column_building_draft_area_aggregate_bool_exp_corr_arguments_columns {
  /** column name */
  value = 'value',
}

/** select "building_draft_area_aggregate_bool_exp_covar_samp_arguments_columns" columns of table "building_draft_area" */
export enum building_draft_area_select_column_building_draft_area_aggregate_bool_exp_covar_samp_arguments_columns {
  /** column name */
  value = 'value',
}

/** select "building_draft_area_aggregate_bool_exp_max_arguments_columns" columns of table "building_draft_area" */
export enum building_draft_area_select_column_building_draft_area_aggregate_bool_exp_max_arguments_columns {
  /** column name */
  value = 'value',
}

/** select "building_draft_area_aggregate_bool_exp_min_arguments_columns" columns of table "building_draft_area" */
export enum building_draft_area_select_column_building_draft_area_aggregate_bool_exp_min_arguments_columns {
  /** column name */
  value = 'value',
}

/** select "building_draft_area_aggregate_bool_exp_stddev_samp_arguments_columns" columns of table "building_draft_area" */
export enum building_draft_area_select_column_building_draft_area_aggregate_bool_exp_stddev_samp_arguments_columns {
  /** column name */
  value = 'value',
}

/** select "building_draft_area_aggregate_bool_exp_sum_arguments_columns" columns of table "building_draft_area" */
export enum building_draft_area_select_column_building_draft_area_aggregate_bool_exp_sum_arguments_columns {
  /** column name */
  value = 'value',
}

/** select "building_draft_area_aggregate_bool_exp_var_samp_arguments_columns" columns of table "building_draft_area" */
export enum building_draft_area_select_column_building_draft_area_aggregate_bool_exp_var_samp_arguments_columns {
  /** column name */
  value = 'value',
}

/** update columns of table "building_draft_area" */
export enum building_draft_area_update_column {
  /** column name */
  area_type_id = 'area_type_id',
  /** column name */
  building_draft_id = 'building_draft_id',
  /** column name */
  energy_certificate_id = 'energy_certificate_id',
  /** column name */
  energy_rating_score = 'energy_rating_score',
  /** column name */
  enev_year = 'enev_year',
  /** column name */
  geg_reference_value = 'geg_reference_value',
  /** column name */
  id = 'id',
  /** column name */
  name = 'name',
  /** column name */
  reference_value_electricity = 'reference_value_electricity',
  /** column name */
  reference_value_heating_hot_water = 'reference_value_heating_hot_water',
  /** column name */
  type_of_use_id = 'type_of_use_id',
  /** column name */
  value = 'value',
}

/** unique or primary key constraints on table "building_draft" */
export enum building_draft_constraint {
  /** unique or primary key constraint on columns "id" */
  building_draft_pkey = 'building_draft_pkey',
}

/** unique or primary key constraints on table "building_draft_consumer" */
export enum building_draft_consumer_constraint {
  /** unique or primary key constraint on columns "id" */
  building_draft_consumer_pkey = 'building_draft_consumer_pkey',
}

/** select columns of table "building_draft_consumer" */
export enum building_draft_consumer_select_column {
  /** column name */
  building_draft_area_id = 'building_draft_area_id',
  /** column name */
  building_draft_id = 'building_draft_id',
  /** column name */
  energy_final = 'energy_final',
  /** column name */
  energy_source_type_id = 'energy_source_type_id',
  /** column name */
  energy_system_type_id = 'energy_system_type_id',
  /** column name */
  id = 'id',
}

/** update columns of table "building_draft_consumer" */
export enum building_draft_consumer_update_column {
  /** column name */
  building_draft_area_id = 'building_draft_area_id',
  /** column name */
  building_draft_id = 'building_draft_id',
  /** column name */
  energy_final = 'energy_final',
  /** column name */
  energy_source_type_id = 'energy_source_type_id',
  /** column name */
  energy_system_type_id = 'energy_system_type_id',
  /** column name */
  id = 'id',
}

/** select columns of table "building_draft" */
export enum building_draft_select_column {
  /** column name */
  assign_responsible_user_as_transaction_manager = 'assign_responsible_user_as_transaction_manager',
  /** column name */
  building_citydb_gml_ids = 'building_citydb_gml_ids',
  /** column name */
  building_lod1_ids = 'building_lod1_ids',
  /** column name */
  building_state_id = 'building_state_id',
  /** column name */
  city = 'city',
  /** column name */
  country_id = 'country_id',
  /** column name */
  created_at = 'created_at',
  /** column name */
  custom_data = 'custom_data',
  /** column name */
  economic_unit_id = 'economic_unit_id',
  /** column name */
  external_id = 'external_id',
  /** column name */
  floors = 'floors',
  /** column name */
  heritage_district = 'heritage_district',
  /** column name */
  id = 'id',
  /** column name */
  latitude = 'latitude',
  /** column name */
  leasehold = 'leasehold',
  /** column name */
  longitude = 'longitude',
  /** column name */
  mapbox_building_ids = 'mapbox_building_ids',
  /** column name */
  milieu_protection = 'milieu_protection',
  /** column name */
  monument_protection = 'monument_protection',
  /** column name */
  neighbor_citydb_gml_ids = 'neighbor_citydb_gml_ids',
  /** column name */
  neighbor_lod1_ids = 'neighbor_lod1_ids',
  /** column name */
  portfolio_id = 'portfolio_id',
  /** column name */
  postal_code = 'postal_code',
  /** column name */
  responsible_user_id = 'responsible_user_id',
  /** column name */
  street = 'street',
  /** column name */
  units = 'units',
  /** column name */
  updated_at = 'updated_at',
  /** column name */
  year_constructed = 'year_constructed',
}

/** select "building_draft_aggregate_bool_exp_bool_and_arguments_columns" columns of table "building_draft" */
export enum building_draft_select_column_building_draft_aggregate_bool_exp_bool_and_arguments_columns {
  /** column name */
  assign_responsible_user_as_transaction_manager = 'assign_responsible_user_as_transaction_manager',
  /** column name */
  heritage_district = 'heritage_district',
  /** column name */
  leasehold = 'leasehold',
  /** column name */
  milieu_protection = 'milieu_protection',
  /** column name */
  monument_protection = 'monument_protection',
}

/** select "building_draft_aggregate_bool_exp_bool_or_arguments_columns" columns of table "building_draft" */
export enum building_draft_select_column_building_draft_aggregate_bool_exp_bool_or_arguments_columns {
  /** column name */
  assign_responsible_user_as_transaction_manager = 'assign_responsible_user_as_transaction_manager',
  /** column name */
  heritage_district = 'heritage_district',
  /** column name */
  leasehold = 'leasehold',
  /** column name */
  milieu_protection = 'milieu_protection',
  /** column name */
  monument_protection = 'monument_protection',
}

/** update columns of table "building_draft" */
export enum building_draft_update_column {
  /** column name */
  assign_responsible_user_as_transaction_manager = 'assign_responsible_user_as_transaction_manager',
  /** column name */
  building_citydb_gml_ids = 'building_citydb_gml_ids',
  /** column name */
  building_lod1_ids = 'building_lod1_ids',
  /** column name */
  building_state_id = 'building_state_id',
  /** column name */
  city = 'city',
  /** column name */
  country_id = 'country_id',
  /** column name */
  created_at = 'created_at',
  /** column name */
  custom_data = 'custom_data',
  /** column name */
  economic_unit_id = 'economic_unit_id',
  /** column name */
  external_id = 'external_id',
  /** column name */
  floors = 'floors',
  /** column name */
  heritage_district = 'heritage_district',
  /** column name */
  id = 'id',
  /** column name */
  latitude = 'latitude',
  /** column name */
  leasehold = 'leasehold',
  /** column name */
  longitude = 'longitude',
  /** column name */
  mapbox_building_ids = 'mapbox_building_ids',
  /** column name */
  milieu_protection = 'milieu_protection',
  /** column name */
  monument_protection = 'monument_protection',
  /** column name */
  neighbor_citydb_gml_ids = 'neighbor_citydb_gml_ids',
  /** column name */
  neighbor_lod1_ids = 'neighbor_lod1_ids',
  /** column name */
  portfolio_id = 'portfolio_id',
  /** column name */
  postal_code = 'postal_code',
  /** column name */
  responsible_user_id = 'responsible_user_id',
  /** column name */
  street = 'street',
  /** column name */
  units = 'units',
  /** column name */
  updated_at = 'updated_at',
  /** column name */
  year_constructed = 'year_constructed',
}

/** unique or primary key constraints on table "building_draft_valuation" */
export enum building_draft_valuation_constraint {
  /** unique or primary key constraint on columns "id" */
  building_draft_valuation_pkey = 'building_draft_valuation_pkey',
}

/** select columns of table "building_draft_valuation" */
export enum building_draft_valuation_select_column {
  /** column name */
  building_draft_id = 'building_draft_id',
  /** column name */
  energy_certificate_draft_id = 'energy_certificate_draft_id',
  /** column name */
  id = 'id',
  /** column name */
  valuation_at = 'valuation_at',
  /** column name */
  value = 'value',
}

/** update columns of table "building_draft_valuation" */
export enum building_draft_valuation_update_column {
  /** column name */
  building_draft_id = 'building_draft_id',
  /** column name */
  energy_certificate_draft_id = 'energy_certificate_draft_id',
  /** column name */
  id = 'id',
  /** column name */
  valuation_at = 'valuation_at',
  /** column name */
  value = 'value',
}

/** unique or primary key constraints on table "building_model" */
export enum building_model_constraint {
  /** unique or primary key constraint on columns "building_id", "valid_from" */
  building_model_history_unique_valid_from = 'building_model_history_unique_valid_from',
  /** unique or primary key constraint on columns "id" */
  building_model_pkey = 'building_model_pkey',
}

/** select columns of table "building_model" */
export enum building_model_select_column {
  /** column name */
  action_id = 'action_id',
  /** column name */
  action_planning_available = 'action_planning_available',
  /** column name */
  air_change_rate_by_infiltration = 'air_change_rate_by_infiltration',
  /** column name */
  annual_heating_days = 'annual_heating_days',
  /** column name */
  building_id = 'building_id',
  /** column name */
  created_at = 'created_at',
  /** column name */
  description = 'description',
  /** column name */
  energy_effective = 'energy_effective',
  /** column name */
  energy_final = 'energy_final',
  /** column name */
  energy_primary = 'energy_primary',
  /** column name */
  energy_scaled_deviation = 'energy_scaled_deviation',
  /** column name */
  external_temperature = 'external_temperature',
  /** column name */
  heating_demand = 'heating_demand',
  /** column name */
  heating_demand_scaled_deviation = 'heating_demand_scaled_deviation',
  /** column name */
  id = 'id',
  /** column name */
  internal_temperature = 'internal_temperature',
  /** column name */
  is_draft = 'is_draft',
  /** column name */
  processing = 'processing',
  /** column name */
  reference_building_id = 'reference_building_id',
  /** column name */
  reference_value_class = 'reference_value_class',
  /** column name */
  scaling_constraint_range = 'scaling_constraint_range',
  /** column name */
  standard_emission_rate = 'standard_emission_rate',
  /** column name */
  thermal_bridging = 'thermal_bridging',
  /** column name */
  updated_at = 'updated_at',
  /** column name */
  valid_from = 'valid_from',
}

/** select "building_model_aggregate_bool_exp_bool_and_arguments_columns" columns of table "building_model" */
export enum building_model_select_column_building_model_aggregate_bool_exp_bool_and_arguments_columns {
  /** column name */
  action_planning_available = 'action_planning_available',
  /** column name */
  is_draft = 'is_draft',
  /** column name */
  processing = 'processing',
}

/** select "building_model_aggregate_bool_exp_bool_or_arguments_columns" columns of table "building_model" */
export enum building_model_select_column_building_model_aggregate_bool_exp_bool_or_arguments_columns {
  /** column name */
  action_planning_available = 'action_planning_available',
  /** column name */
  is_draft = 'is_draft',
  /** column name */
  processing = 'processing',
}

/** update columns of table "building_model" */
export enum building_model_update_column {
  /** column name */
  action_id = 'action_id',
  /** column name */
  action_planning_available = 'action_planning_available',
  /** column name */
  air_change_rate_by_infiltration = 'air_change_rate_by_infiltration',
  /** column name */
  annual_heating_days = 'annual_heating_days',
  /** column name */
  building_id = 'building_id',
  /** column name */
  created_at = 'created_at',
  /** column name */
  description = 'description',
  /** column name */
  energy_effective = 'energy_effective',
  /** column name */
  energy_final = 'energy_final',
  /** column name */
  energy_primary = 'energy_primary',
  /** column name */
  energy_scaled_deviation = 'energy_scaled_deviation',
  /** column name */
  external_temperature = 'external_temperature',
  /** column name */
  heating_demand = 'heating_demand',
  /** column name */
  heating_demand_scaled_deviation = 'heating_demand_scaled_deviation',
  /** column name */
  id = 'id',
  /** column name */
  internal_temperature = 'internal_temperature',
  /** column name */
  is_draft = 'is_draft',
  /** column name */
  processing = 'processing',
  /** column name */
  reference_building_id = 'reference_building_id',
  /** column name */
  reference_value_class = 'reference_value_class',
  /** column name */
  scaling_constraint_range = 'scaling_constraint_range',
  /** column name */
  standard_emission_rate = 'standard_emission_rate',
  /** column name */
  thermal_bridging = 'thermal_bridging',
  /** column name */
  updated_at = 'updated_at',
  /** column name */
  valid_from = 'valid_from',
}

/** select columns of table "building_permissions" */
export enum building_permissions_select_column {
  /** column name */
  building_id = 'building_id',
  /** column name */
  portfolio_id = 'portfolio_id',
  /** column name */
  read = 'read',
  /** column name */
  user_id = 'user_id',
  /** column name */
  write = 'write',
}

/** select "building_permissions_aggregate_bool_exp_bool_and_arguments_columns" columns of table "building_permissions" */
export enum building_permissions_select_column_building_permissions_aggregate_bool_exp_bool_and_arguments_columns {
  /** column name */
  read = 'read',
  /** column name */
  write = 'write',
}

/** select "building_permissions_aggregate_bool_exp_bool_or_arguments_columns" columns of table "building_permissions" */
export enum building_permissions_select_column_building_permissions_aggregate_bool_exp_bool_or_arguments_columns {
  /** column name */
  read = 'read',
  /** column name */
  write = 'write',
}

/** select columns of table "building" */
export enum building_select_column {
  /** column name */
  active_building_model_id = 'active_building_model_id',
  /** column name */
  address_id = 'address_id',
  /** column name */
  building_state_id = 'building_state_id',
  /** column name */
  created_at = 'created_at',
  /** column name */
  custom_data = 'custom_data',
  /** column name */
  customer_external_identifier = 'customer_external_identifier',
  /** column name */
  description = 'description',
  /** column name */
  economic_unit_id = 'economic_unit_id',
  /** column name */
  heritage_district = 'heritage_district',
  /** column name */
  id = 'id',
  /** column name */
  is_new_construction = 'is_new_construction',
  /** column name */
  leasehold = 'leasehold',
  /** column name */
  milieu_protection = 'milieu_protection',
  /** column name */
  monument_protection = 'monument_protection',
  /** column name */
  responsible_user_id = 'responsible_user_id',
  /** column name */
  state = 'state',
  /** column name */
  subsidies_updated_at = 'subsidies_updated_at',
  /** column name */
  units = 'units',
  /** column name */
  units_data_source = 'units_data_source',
  /** column name */
  updated_at = 'updated_at',
  /** column name */
  year_constructed = 'year_constructed',
  /** column name */
  year_data_source = 'year_data_source',
}

/** select "building_aggregate_bool_exp_bool_and_arguments_columns" columns of table "building" */
export enum building_select_column_building_aggregate_bool_exp_bool_and_arguments_columns {
  /** column name */
  heritage_district = 'heritage_district',
  /** column name */
  is_new_construction = 'is_new_construction',
  /** column name */
  leasehold = 'leasehold',
  /** column name */
  milieu_protection = 'milieu_protection',
  /** column name */
  monument_protection = 'monument_protection',
}

/** select "building_aggregate_bool_exp_bool_or_arguments_columns" columns of table "building" */
export enum building_select_column_building_aggregate_bool_exp_bool_or_arguments_columns {
  /** column name */
  heritage_district = 'heritage_district',
  /** column name */
  is_new_construction = 'is_new_construction',
  /** column name */
  leasehold = 'leasehold',
  /** column name */
  milieu_protection = 'milieu_protection',
  /** column name */
  monument_protection = 'monument_protection',
}

/** unique or primary key constraints on table "building_state" */
export enum building_state_constraint {
  /** unique or primary key constraint on columns "id" */
  building_state_pkey = 'building_state_pkey',
}

export enum building_state_enum {
  ACQUISITION = 'ACQUISITION',
  INVENTORY = 'INVENTORY',
}

/** select columns of table "building_state" */
export enum building_state_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "building_state" */
export enum building_state_update_column {
  /** column name */
  id = 'id',
}

/** unique or primary key constraints on table "building_subsidy" */
export enum building_subsidy_constraint {
  /** unique or primary key constraint on columns "building_id", "subsidy_id" */
  building_subsidy_building_id_subsidy_id_key = 'building_subsidy_building_id_subsidy_id_key',
  /** unique or primary key constraint on columns "id" */
  building_subsidy_pkey = 'building_subsidy_pkey',
}

/** select columns of table "building_subsidy" */
export enum building_subsidy_select_column {
  /** column name */
  building_id = 'building_id',
  /** column name */
  id = 'id',
  /** column name */
  subsidy_id = 'subsidy_id',
}

/** update columns of table "building_subsidy" */
export enum building_subsidy_update_column {
  /** column name */
  building_id = 'building_id',
  /** column name */
  id = 'id',
  /** column name */
  subsidy_id = 'subsidy_id',
}

/** update columns of table "building" */
export enum building_update_column {
  /** column name */
  active_building_model_id = 'active_building_model_id',
  /** column name */
  address_id = 'address_id',
  /** column name */
  building_state_id = 'building_state_id',
  /** column name */
  created_at = 'created_at',
  /** column name */
  custom_data = 'custom_data',
  /** column name */
  customer_external_identifier = 'customer_external_identifier',
  /** column name */
  description = 'description',
  /** column name */
  economic_unit_id = 'economic_unit_id',
  /** column name */
  heritage_district = 'heritage_district',
  /** column name */
  id = 'id',
  /** column name */
  is_new_construction = 'is_new_construction',
  /** column name */
  leasehold = 'leasehold',
  /** column name */
  milieu_protection = 'milieu_protection',
  /** column name */
  monument_protection = 'monument_protection',
  /** column name */
  responsible_user_id = 'responsible_user_id',
  /** column name */
  state = 'state',
  /** column name */
  subsidies_updated_at = 'subsidies_updated_at',
  /** column name */
  units = 'units',
  /** column name */
  units_data_source = 'units_data_source',
  /** column name */
  updated_at = 'updated_at',
  /** column name */
  year_constructed = 'year_constructed',
  /** column name */
  year_data_source = 'year_data_source',
}

/** unique or primary key constraints on table "building_valuation" */
export enum building_valuation_constraint {
  /** unique or primary key constraint on columns "id" */
  building_valuation_pkey = 'building_valuation_pkey',
  /** unique or primary key constraint on columns "building_id", "value", "valuation_at" */
  unique_building_valuation = 'unique_building_valuation',
}

/** select columns of table "building_valuation" */
export enum building_valuation_select_column {
  /** column name */
  building_id = 'building_id',
  /** column name */
  id = 'id',
  /** column name */
  valuation_at = 'valuation_at',
  /** column name */
  value = 'value',
}

/** update columns of table "building_valuation" */
export enum building_valuation_update_column {
  /** column name */
  building_id = 'building_id',
  /** column name */
  id = 'id',
  /** column name */
  valuation_at = 'valuation_at',
  /** column name */
  value = 'value',
}

/** select columns of table "building_with_district_heating_status" */
export enum building_with_district_heating_status_select_column {
  /** column name */
  building_id = 'building_id',
  /** column name */
  district_heating_status = 'district_heating_status',
  /** column name */
  district_heating_year = 'district_heating_year',
  /** column name */
  updated_at = 'updated_at',
}

/** unique or primary key constraints on table "bulk_action_action_plan_status" */
export enum bulk_action_action_plan_status_constraint {
  /** unique or primary key constraint on columns "id" */
  bulk_action_action_plan_status_pkey = 'bulk_action_action_plan_status_pkey',
}

export enum bulk_action_action_plan_status_enum {
  DELETION_FAILED = 'DELETION_FAILED',
  DELETION_PENDING = 'DELETION_PENDING',
  FAILED = 'FAILED',
  PENDING = 'PENDING',
  PLANNED = 'PLANNED',
}

/** select columns of table "bulk_action_action_plan_status" */
export enum bulk_action_action_plan_status_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "bulk_action_action_plan_status" */
export enum bulk_action_action_plan_status_update_column {
  /** column name */
  id = 'id',
}

/** unique or primary key constraints on table "bulk_action" */
export enum bulk_action_constraint {
  /** unique or primary key constraint on columns "id" */
  bulk_actions_pkey = 'bulk_actions_pkey',
}

/** select columns of table "bulk_action" */
export enum bulk_action_select_column {
  /** column name */
  created_at = 'created_at',
  /** column name */
  custom_costs = 'custom_costs',
  /** column name */
  custom_costs_per_m2 = 'custom_costs_per_m2',
  /** column name */
  id = 'id',
  /** column name */
  implementation_date = 'implementation_date',
  /** column name */
  name = 'name',
  /** column name */
  parameters = 'parameters',
  /** column name */
  scenario_version_id = 'scenario_version_id',
  /** column name */
  updated_at = 'updated_at',
}

/** select columns of table "bulk_action_summary" */
export enum bulk_action_summary_select_column {
  /** column name */
  bulk_action_id = 'bulk_action_id',
  /** column name */
  deletion_failed_count = 'deletion_failed_count',
  /** column name */
  deletion_pending_count = 'deletion_pending_count',
  /** column name */
  failed_count = 'failed_count',
  /** column name */
  pending_count = 'pending_count',
  /** column name */
  planned_count = 'planned_count',
  /** column name */
  total_cost = 'total_cost',
  /** column name */
  total_count = 'total_count',
}

/** unique or primary key constraints on table "bulk_action_to_action_plan" */
export enum bulk_action_to_action_plan_constraint {
  /** unique or primary key constraint on columns "action_plan_id", "bulk_action_id" */
  bulk_action_to_action_plan_pkey = 'bulk_action_to_action_plan_pkey',
}

/** select columns of table "bulk_action_to_action_plan" */
export enum bulk_action_to_action_plan_select_column {
  /** column name */
  action_plan_id = 'action_plan_id',
  /** column name */
  bulk_action_id = 'bulk_action_id',
  /** column name */
  status = 'status',
}

/** update columns of table "bulk_action_to_action_plan" */
export enum bulk_action_to_action_plan_update_column {
  /** column name */
  action_plan_id = 'action_plan_id',
  /** column name */
  bulk_action_id = 'bulk_action_id',
  /** column name */
  status = 'status',
}

/** update columns of table "bulk_action" */
export enum bulk_action_update_column {
  /** column name */
  created_at = 'created_at',
  /** column name */
  custom_costs = 'custom_costs',
  /** column name */
  custom_costs_per_m2 = 'custom_costs_per_m2',
  /** column name */
  id = 'id',
  /** column name */
  implementation_date = 'implementation_date',
  /** column name */
  name = 'name',
  /** column name */
  parameters = 'parameters',
  /** column name */
  scenario_version_id = 'scenario_version_id',
  /** column name */
  updated_at = 'updated_at',
}

/** select columns of table "bulk_actions_overview" */
export enum bulk_actions_overview_select_column {
  /** column name */
  action_plan_id = 'action_plan_id',
  /** column name */
  action_plan_status = 'action_plan_status',
  /** column name */
  building_id = 'building_id',
  /** column name */
  bulk_action_id = 'bulk_action_id',
}

/** unique or primary key constraints on table "cbi_address_assignment" */
export enum cbi_address_assignment_constraint {
  /** unique or primary key constraint on columns "id" */
  cbi_address_assignment_pkey = 'cbi_address_assignment_pkey',
  /** unique or primary key constraint on columns "building_id", "area_id", "cbi_address_id" */
  cbi_address_assignment_unique_idx = 'cbi_address_assignment_unique_idx',
  /** unique or primary key constraint on columns "building_id", "cbi_address_id" */
  cbi_address_assignment_unique_null_area_idx = 'cbi_address_assignment_unique_null_area_idx',
}

/** select columns of table "cbi_address_assignment" */
export enum cbi_address_assignment_select_column {
  /** column name */
  area_id = 'area_id',
  /** column name */
  building_id = 'building_id',
  /** column name */
  cbi_address_id = 'cbi_address_id',
  /** column name */
  created_at = 'created_at',
  /** column name */
  id = 'id',
  /** column name */
  updated_at = 'updated_at',
}

/** update columns of table "cbi_address_assignment" */
export enum cbi_address_assignment_update_column {
  /** column name */
  area_id = 'area_id',
  /** column name */
  building_id = 'building_id',
  /** column name */
  cbi_address_id = 'cbi_address_id',
  /** column name */
  created_at = 'created_at',
  /** column name */
  id = 'id',
  /** column name */
  updated_at = 'updated_at',
}

/** unique or primary key constraints on table "cbi_address" */
export enum cbi_address_constraint {
  /** unique or primary key constraint on columns "address_string", "org_id" */
  cbi_address_org_id_address_string_idx = 'cbi_address_org_id_address_string_idx',
  /** unique or primary key constraint on columns "id" */
  cbi_address_pkey = 'cbi_address_pkey',
}

/** select columns of table "cbi_address" */
export enum cbi_address_select_column {
  /** column name */
  address_string = 'address_string',
  /** column name */
  created_at = 'created_at',
  /** column name */
  id = 'id',
  /** column name */
  org_id = 'org_id',
  /** column name */
  updated_at = 'updated_at',
}

/** update columns of table "cbi_address" */
export enum cbi_address_update_column {
  /** column name */
  address_string = 'address_string',
  /** column name */
  created_at = 'created_at',
  /** column name */
  id = 'id',
  /** column name */
  org_id = 'org_id',
  /** column name */
  updated_at = 'updated_at',
}

/** unique or primary key constraints on table "cbi_assignment_state" */
export enum cbi_assignment_state_constraint {
  /** unique or primary key constraint on columns "id" */
  cbi_assignment_state_pkey = 'cbi_assignment_state_pkey',
}

/** select columns of table "cbi_assignment_state" */
export enum cbi_assignment_state_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "cbi_assignment_state" */
export enum cbi_assignment_state_update_column {
  /** column name */
  id = 'id',
}

/** unique or primary key constraints on table "cbi_billable_event" */
export enum cbi_billable_event_constraint {
  /** unique or primary key constraint on columns "id" */
  cbi_billable_event_pkey = 'cbi_billable_event_pkey',
}

/** select columns of table "cbi_billable_event" */
export enum cbi_billable_event_select_column {
  /** column name */
  billing_period_id = 'billing_period_id',
  /** column name */
  created_at = 'created_at',
  /** column name */
  event_type = 'event_type',
  /** column name */
  id = 'id',
  /** column name */
  invoice_id = 'invoice_id',
  /** column name */
  invoice_id_persistent = 'invoice_id_persistent',
  /** column name */
  snapshot_id = 'snapshot_id',
  /** column name */
  snapshot_id_persistent = 'snapshot_id_persistent',
  /** column name */
  updated_at = 'updated_at',
  /** column name */
  void_reason_type = 'void_reason_type',
  /** column name */
  voided_at = 'voided_at',
}

/** unique or primary key constraints on table "cbi_billable_event_type" */
export enum cbi_billable_event_type_constraint {
  /** unique or primary key constraint on columns "value" */
  cbi_billable_event_type_pkey = 'cbi_billable_event_type_pkey',
}

export enum cbi_billable_event_type_enum {
  DIRECT_UPLOAD_RECEIVED = 'DIRECT_UPLOAD_RECEIVED',
  EMAIL_RECEIVED = 'EMAIL_RECEIVED',
  USER_BATCH_RETRY = 'USER_BATCH_RETRY',
  USER_RETRY = 'USER_RETRY',
}

/** select columns of table "cbi_billable_event_type" */
export enum cbi_billable_event_type_select_column {
  /** column name */
  value = 'value',
}

/** update columns of table "cbi_billable_event_type" */
export enum cbi_billable_event_type_update_column {
  /** column name */
  value = 'value',
}

/** update columns of table "cbi_billable_event" */
export enum cbi_billable_event_update_column {
  /** column name */
  billing_period_id = 'billing_period_id',
  /** column name */
  created_at = 'created_at',
  /** column name */
  event_type = 'event_type',
  /** column name */
  id = 'id',
  /** column name */
  invoice_id = 'invoice_id',
  /** column name */
  invoice_id_persistent = 'invoice_id_persistent',
  /** column name */
  snapshot_id = 'snapshot_id',
  /** column name */
  snapshot_id_persistent = 'snapshot_id_persistent',
  /** column name */
  updated_at = 'updated_at',
  /** column name */
  void_reason_type = 'void_reason_type',
  /** column name */
  voided_at = 'voided_at',
}

/** unique or primary key constraints on table "cbi_billable_event_void_type" */
export enum cbi_billable_event_void_type_constraint {
  /** unique or primary key constraint on columns "value" */
  cbi_billable_event_void_type_pkey = 'cbi_billable_event_void_type_pkey',
}

export enum cbi_billable_event_void_type_enum {
  EXTRACTION_FAILED = 'EXTRACTION_FAILED',
  MANUALLY_VOIDED = 'MANUALLY_VOIDED',
}

/** select columns of table "cbi_billable_event_void_type" */
export enum cbi_billable_event_void_type_select_column {
  /** column name */
  value = 'value',
}

/** update columns of table "cbi_billable_event_void_type" */
export enum cbi_billable_event_void_type_update_column {
  /** column name */
  value = 'value',
}

/** unique or primary key constraints on table "cbi_billing_increment_event" */
export enum cbi_billing_increment_event_constraint {
  /** unique or primary key constraint on columns "id" */
  cbi_billing_increment_event_pkey = 'cbi_billing_increment_event_pkey',
}

/** select columns of table "cbi_billing_increment_event" */
export enum cbi_billing_increment_event_select_column {
  /** column name */
  billing_period_id = 'billing_period_id',
  /** column name */
  created_at = 'created_at',
  /** column name */
  id = 'id',
  /** column name */
  new_limit = 'new_limit',
  /** column name */
  previous_limit = 'previous_limit',
}

/** update columns of table "cbi_billing_increment_event" */
export enum cbi_billing_increment_event_update_column {
  /** column name */
  billing_period_id = 'billing_period_id',
  /** column name */
  created_at = 'created_at',
  /** column name */
  id = 'id',
  /** column name */
  new_limit = 'new_limit',
  /** column name */
  previous_limit = 'previous_limit',
}

/** unique or primary key constraints on table "cbi_billing_period" */
export enum cbi_billing_period_constraint {
  /** unique or primary key constraint on columns "org_id" */
  cbi_billing_period_org_active_key = 'cbi_billing_period_org_active_key',
  /** unique or primary key constraint on columns "id" */
  cbi_billing_period_pkey = 'cbi_billing_period_pkey',
}

/** select columns of table "cbi_billing_period" */
export enum cbi_billing_period_select_column {
  /** column name */
  current_quota_limit = 'current_quota_limit',
  /** column name */
  id = 'id',
  /** column name */
  initial_quota = 'initial_quota',
  /** column name */
  is_active = 'is_active',
  /** column name */
  org_id = 'org_id',
  /** column name */
  percentage_increment = 'percentage_increment',
  /** column name */
  period_end = 'period_end',
  /** column name */
  period_start = 'period_start',
}

/** update columns of table "cbi_billing_period" */
export enum cbi_billing_period_update_column {
  /** column name */
  current_quota_limit = 'current_quota_limit',
  /** column name */
  id = 'id',
  /** column name */
  initial_quota = 'initial_quota',
  /** column name */
  is_active = 'is_active',
  /** column name */
  org_id = 'org_id',
  /** column name */
  percentage_increment = 'percentage_increment',
  /** column name */
  period_end = 'period_end',
  /** column name */
  period_start = 'period_start',
}

/** unique or primary key constraints on table "cbi_consumption_line" */
export enum cbi_consumption_line_constraint {
  /** unique or primary key constraint on columns "id" */
  cbi_consumption_line_pkey = 'cbi_consumption_line_pkey',
  /** unique or primary key constraint on columns "cbi_invoice_snapshot_id", "line_index" */
  cbi_consumption_line_snapshot_line_index_unique = 'cbi_consumption_line_snapshot_line_index_unique',
}

/** select columns of table "cbi_consumption_line" */
export enum cbi_consumption_line_select_column {
  /** column name */
  advance_payments = 'advance_payments',
  /** column name */
  amount_payable = 'amount_payable',
  /** column name */
  base_price_net = 'base_price_net',
  /** column name */
  building_address = 'building_address',
  /** column name */
  cbi_address_id = 'cbi_address_id',
  /** column name */
  cbi_invoice_snapshot_id = 'cbi_invoice_snapshot_id',
  /** column name */
  co2_fee_gross = 'co2_fee_gross',
  /** column name */
  co2_fee_net = 'co2_fee_net',
  /** column name */
  consumption_amount = 'consumption_amount',
  /** column name */
  consumption_end_date = 'consumption_end_date',
  /** column name */
  consumption_start_date = 'consumption_start_date',
  /** column name */
  consumption_type = 'consumption_type',
  /** column name */
  cost_gross = 'cost_gross',
  /** column name */
  cost_net = 'cost_net',
  /** column name */
  created_at = 'created_at',
  /** column name */
  credit_amount = 'credit_amount',
  /** column name */
  emission_factor_market_based = 'emission_factor_market_based',
  /** column name */
  energy_source = 'energy_source',
  /** column name */
  field_confidence_scores = 'field_confidence_scores',
  /** column name */
  id = 'id',
  /** column name */
  is_advance_payment = 'is_advance_payment',
  /** column name */
  is_estimated = 'is_estimated',
  /** column name */
  line_index = 'line_index',
  /** column name */
  market_location_id = 'market_location_id',
  /** column name */
  meter_number = 'meter_number',
  /** column name */
  other_charges_net = 'other_charges_net',
  /** column name */
  outstanding_balance = 'outstanding_balance',
  /** column name */
  position = 'position',
  /** column name */
  reference_area = 'reference_area',
  /** column name */
  renewable_energy_percentage = 'renewable_energy_percentage',
  /** column name */
  unit = 'unit',
  /** column name */
  updated_at = 'updated_at',
  /** column name */
  vat_amount = 'vat_amount',
  /** column name */
  vat_percentage = 'vat_percentage',
  /** column name */
  waste_additional_collections_total = 'waste_additional_collections_total',
  /** column name */
  waste_collection_frequency = 'waste_collection_frequency',
  /** column name */
  waste_collection_frequency_period = 'waste_collection_frequency_period',
  /** column name */
  waste_collection_frequency_raw = 'waste_collection_frequency_raw',
  /** column name */
  waste_collections_total = 'waste_collections_total',
  /** column name */
  waste_container_count = 'waste_container_count',
  /** column name */
  waste_container_size = 'waste_container_size',
  /** column name */
  waste_container_size_unit = 'waste_container_size_unit',
  /** column name */
  waste_ewc_code = 'waste_ewc_code',
  /** column name */
  waste_quantity = 'waste_quantity',
  /** column name */
  waste_quantity_unit = 'waste_quantity_unit',
  /** column name */
  waste_sub_type = 'waste_sub_type',
  /** column name */
  water_sub_type = 'water_sub_type',
  /** column name */
  working_price_net = 'working_price_net',
}

/** select "cbi_consumption_line_aggregate_bool_exp_bool_and_arguments_columns" columns of table "cbi_consumption_line" */
export enum cbi_consumption_line_select_column_cbi_consumption_line_aggregate_bool_exp_bool_and_arguments_columns {
  /** column name */
  is_advance_payment = 'is_advance_payment',
  /** column name */
  is_estimated = 'is_estimated',
}

/** select "cbi_consumption_line_aggregate_bool_exp_bool_or_arguments_columns" columns of table "cbi_consumption_line" */
export enum cbi_consumption_line_select_column_cbi_consumption_line_aggregate_bool_exp_bool_or_arguments_columns {
  /** column name */
  is_advance_payment = 'is_advance_payment',
  /** column name */
  is_estimated = 'is_estimated',
}

/** update columns of table "cbi_consumption_line" */
export enum cbi_consumption_line_update_column {
  /** column name */
  advance_payments = 'advance_payments',
  /** column name */
  amount_payable = 'amount_payable',
  /** column name */
  base_price_net = 'base_price_net',
  /** column name */
  building_address = 'building_address',
  /** column name */
  cbi_address_id = 'cbi_address_id',
  /** column name */
  cbi_invoice_snapshot_id = 'cbi_invoice_snapshot_id',
  /** column name */
  co2_fee_gross = 'co2_fee_gross',
  /** column name */
  co2_fee_net = 'co2_fee_net',
  /** column name */
  consumption_amount = 'consumption_amount',
  /** column name */
  consumption_end_date = 'consumption_end_date',
  /** column name */
  consumption_start_date = 'consumption_start_date',
  /** column name */
  consumption_type = 'consumption_type',
  /** column name */
  cost_gross = 'cost_gross',
  /** column name */
  cost_net = 'cost_net',
  /** column name */
  created_at = 'created_at',
  /** column name */
  credit_amount = 'credit_amount',
  /** column name */
  emission_factor_market_based = 'emission_factor_market_based',
  /** column name */
  energy_source = 'energy_source',
  /** column name */
  field_confidence_scores = 'field_confidence_scores',
  /** column name */
  id = 'id',
  /** column name */
  is_advance_payment = 'is_advance_payment',
  /** column name */
  is_estimated = 'is_estimated',
  /** column name */
  line_index = 'line_index',
  /** column name */
  market_location_id = 'market_location_id',
  /** column name */
  meter_number = 'meter_number',
  /** column name */
  other_charges_net = 'other_charges_net',
  /** column name */
  outstanding_balance = 'outstanding_balance',
  /** column name */
  position = 'position',
  /** column name */
  reference_area = 'reference_area',
  /** column name */
  renewable_energy_percentage = 'renewable_energy_percentage',
  /** column name */
  unit = 'unit',
  /** column name */
  updated_at = 'updated_at',
  /** column name */
  vat_amount = 'vat_amount',
  /** column name */
  vat_percentage = 'vat_percentage',
  /** column name */
  waste_additional_collections_total = 'waste_additional_collections_total',
  /** column name */
  waste_collection_frequency = 'waste_collection_frequency',
  /** column name */
  waste_collection_frequency_period = 'waste_collection_frequency_period',
  /** column name */
  waste_collection_frequency_raw = 'waste_collection_frequency_raw',
  /** column name */
  waste_collections_total = 'waste_collections_total',
  /** column name */
  waste_container_count = 'waste_container_count',
  /** column name */
  waste_container_size = 'waste_container_size',
  /** column name */
  waste_container_size_unit = 'waste_container_size_unit',
  /** column name */
  waste_ewc_code = 'waste_ewc_code',
  /** column name */
  waste_quantity = 'waste_quantity',
  /** column name */
  waste_quantity_unit = 'waste_quantity_unit',
  /** column name */
  waste_sub_type = 'waste_sub_type',
  /** column name */
  water_sub_type = 'water_sub_type',
  /** column name */
  working_price_net = 'working_price_net',
}

/** unique or primary key constraints on table "cbi_consumption_unit" */
export enum cbi_consumption_unit_constraint {
  /** unique or primary key constraint on columns "id" */
  cbi_consumption_unit_pkey = 'cbi_consumption_unit_pkey',
}

export enum cbi_consumption_unit_enum {
  GJ = 'GJ',
  J = 'J',
  KG = 'KG',
  KJ = 'KJ',
  KWH = 'KWH',
  L = 'L',
  M2 = 'M2',
  M3 = 'M3',
  MJ = 'MJ',
  MWH = 'MWH',
  T = 'T',
}

/** select columns of table "cbi_consumption_unit" */
export enum cbi_consumption_unit_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "cbi_consumption_unit" */
export enum cbi_consumption_unit_update_column {
  /** column name */
  id = 'id',
}

/** unique or primary key constraints on table "cbi_duplicate_reason_type" */
export enum cbi_duplicate_reason_type_constraint {
  /** unique or primary key constraint on columns "id" */
  cbi_duplicate_reason_type_pkey = 'cbi_duplicate_reason_type_pkey',
}

export enum cbi_duplicate_reason_type_enum {
  CHECKSUM = 'CHECKSUM',
  INVOICE_NUMBER = 'INVOICE_NUMBER',
  MANUAL = 'MANUAL',
}

/** select columns of table "cbi_duplicate_reason_type" */
export enum cbi_duplicate_reason_type_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "cbi_duplicate_reason_type" */
export enum cbi_duplicate_reason_type_update_column {
  /** column name */
  id = 'id',
}

/** unique or primary key constraints on table "cbi_email" */
export enum cbi_email_constraint {
  /** unique or primary key constraint on columns "id", "org_id" */
  cbi_email_org_id_id_idx = 'cbi_email_org_id_id_idx',
  /** unique or primary key constraint on columns "id" */
  cbi_email_pkey = 'cbi_email_pkey',
  /** unique or primary key constraint on columns "s3_key" */
  cbi_email_s3_key_idx = 'cbi_email_s3_key_idx',
  /** unique or primary key constraint on columns "s3_key" */
  cbi_email_s3_key_key = 'cbi_email_s3_key_key',
}

/** select columns of table "cbi_email" */
export enum cbi_email_select_column {
  /** column name */
  cbi_inbox_id = 'cbi_inbox_id',
  /** column name */
  created_at = 'created_at',
  /** column name */
  from = 'from',
  /** column name */
  id = 'id',
  /** column name */
  message_id = 'message_id',
  /** column name */
  org_id = 'org_id',
  /** column name */
  received_at = 'received_at',
  /** column name */
  s3_key = 's3_key',
  /** column name */
  subject = 'subject',
  /** column name */
  text = 'text',
  /** column name */
  to = 'to',
  /** column name */
  updated_at = 'updated_at',
}

/** update columns of table "cbi_email" */
export enum cbi_email_update_column {
  /** column name */
  cbi_inbox_id = 'cbi_inbox_id',
  /** column name */
  created_at = 'created_at',
  /** column name */
  from = 'from',
  /** column name */
  id = 'id',
  /** column name */
  message_id = 'message_id',
  /** column name */
  org_id = 'org_id',
  /** column name */
  received_at = 'received_at',
  /** column name */
  s3_key = 's3_key',
  /** column name */
  subject = 'subject',
  /** column name */
  text = 'text',
  /** column name */
  to = 'to',
  /** column name */
  updated_at = 'updated_at',
}

/** unique or primary key constraints on table "cbi_energy_source_type" */
export enum cbi_energy_source_type_constraint {
  /** unique or primary key constraint on columns "id" */
  cbi_energy_source_type_pkey = 'cbi_energy_source_type_pkey',
}

export enum cbi_energy_source_type_enum {
  BIO_GAS = 'BIO_GAS',
  COAL = 'COAL',
  DISTRICT_HEATING = 'DISTRICT_HEATING',
  ELECTRICITY_GREEN = 'ELECTRICITY_GREEN',
  ELECTRICITY_MIX = 'ELECTRICITY_MIX',
  FUEL_OIL = 'FUEL_OIL',
  LIGNITE = 'LIGNITE',
  LPG = 'LPG',
  NATURAL_GAS = 'NATURAL_GAS',
  SOLAR = 'SOLAR',
  WOOD = 'WOOD',
  WOODEN_PELLETS = 'WOODEN_PELLETS',
}

/** select columns of table "cbi_energy_source_type" */
export enum cbi_energy_source_type_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "cbi_energy_source_type" */
export enum cbi_energy_source_type_update_column {
  /** column name */
  id = 'id',
}

/** unique or primary key constraints on table "cbi_export" */
export enum cbi_export_constraint {
  /** unique or primary key constraint on columns "id" */
  cbi_export_pkey = 'cbi_export_pkey',
}

/** select columns of table "cbi_export" */
export enum cbi_export_select_column {
  /** column name */
  completed_at = 'completed_at',
  /** column name */
  created_at = 'created_at',
  /** column name */
  file_id = 'file_id',
  /** column name */
  id = 'id',
  /** column name */
  org_id = 'org_id',
  /** column name */
  status_id = 'status_id',
  /** column name */
  user_id = 'user_id',
}

/** unique or primary key constraints on table "cbi_export_status_type" */
export enum cbi_export_status_type_constraint {
  /** unique or primary key constraint on columns "id" */
  cbi_export_status_type_pkey = 'cbi_export_status_type_pkey',
}

export enum cbi_export_status_type_enum {
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  PENDING = 'PENDING',
  PROCESSING = 'PROCESSING',
}

/** select columns of table "cbi_export_status_type" */
export enum cbi_export_status_type_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "cbi_export_status_type" */
export enum cbi_export_status_type_update_column {
  /** column name */
  id = 'id',
}

/** update columns of table "cbi_export" */
export enum cbi_export_update_column {
  /** column name */
  completed_at = 'completed_at',
  /** column name */
  created_at = 'created_at',
  /** column name */
  file_id = 'file_id',
  /** column name */
  id = 'id',
  /** column name */
  org_id = 'org_id',
  /** column name */
  status_id = 'status_id',
  /** column name */
  user_id = 'user_id',
}

/** unique or primary key constraints on table "cbi_extraction_state" */
export enum cbi_extraction_state_constraint {
  /** unique or primary key constraint on columns "id" */
  cbi_extraction_state_pkey = 'cbi_extraction_state_pkey',
}

export enum cbi_extraction_state_enum {
  AUTOMATIC_APPROVAL = 'AUTOMATIC_APPROVAL',
  AUTOMATIC_REJECTION = 'AUTOMATIC_REJECTION',
  FAILED = 'FAILED',
  MANUAL_APPROVAL = 'MANUAL_APPROVAL',
  MANUAL_REJECTION = 'MANUAL_REJECTION',
  NEEDS_REVIEW = 'NEEDS_REVIEW',
  PENDING = 'PENDING',
  RUNNING = 'RUNNING',
}

/** select columns of table "cbi_extraction_state" */
export enum cbi_extraction_state_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "cbi_extraction_state" */
export enum cbi_extraction_state_update_column {
  /** column name */
  id = 'id',
}

/** unique or primary key constraints on table "cbi_extraction_type" */
export enum cbi_extraction_type_constraint {
  /** unique or primary key constraint on columns "id" */
  cbi_extraction_type_pkey = 'cbi_extraction_type_pkey',
}

export enum cbi_extraction_type_enum {
  ALPHA_PROMPT = 'ALPHA_PROMPT',
  MANUAL = 'MANUAL',
  PREDIUM = 'PREDIUM',
}

/** select columns of table "cbi_extraction_type" */
export enum cbi_extraction_type_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "cbi_extraction_type" */
export enum cbi_extraction_type_update_column {
  /** column name */
  id = 'id',
}

/** unique or primary key constraints on table "cbi_inbox" */
export enum cbi_inbox_constraint {
  /** unique or primary key constraint on columns "id", "org_id" */
  cbi_inbox_org_id_id_idx = 'cbi_inbox_org_id_id_idx',
  /** unique or primary key constraint on columns "id" */
  cbi_inbox_pkey = 'cbi_inbox_pkey',
  /** unique or primary key constraint on columns "receiver_email" */
  cbi_inbox_receiver_email_idx = 'cbi_inbox_receiver_email_idx',
  /** unique or primary key constraint on columns "receiver_email" */
  cbi_inbox_receiver_email_key = 'cbi_inbox_receiver_email_key',
}

/** select columns of table "cbi_inbox" */
export enum cbi_inbox_select_column {
  /** column name */
  allowed_senders = 'allowed_senders',
  /** column name */
  created_at = 'created_at',
  /** column name */
  id = 'id',
  /** column name */
  org_id = 'org_id',
  /** column name */
  receiver_email = 'receiver_email',
  /** column name */
  updated_at = 'updated_at',
}

/** update columns of table "cbi_inbox" */
export enum cbi_inbox_update_column {
  /** column name */
  allowed_senders = 'allowed_senders',
  /** column name */
  created_at = 'created_at',
  /** column name */
  id = 'id',
  /** column name */
  org_id = 'org_id',
  /** column name */
  receiver_email = 'receiver_email',
  /** column name */
  updated_at = 'updated_at',
}

/** unique or primary key constraints on table "cbi_invoice" */
export enum cbi_invoice_constraint {
  /** unique or primary key constraint on columns "id" */
  cbi_invoice_pkey = 'cbi_invoice_pkey',
}

/** select columns of table "cbi_invoice" */
export enum cbi_invoice_select_column {
  /** column name */
  cbi_email_id = 'cbi_email_id',
  /** column name */
  cbi_invoice_source_id = 'cbi_invoice_source_id',
  /** column name */
  checksum = 'checksum',
  /** column name */
  created_at = 'created_at',
  /** column name */
  duplicate_of_id = 'duplicate_of_id',
  /** column name */
  duplicate_reason = 'duplicate_reason',
  /** column name */
  duplicate_reason_type = 'duplicate_reason_type',
  /** column name */
  file_id = 'file_id',
  /** column name */
  id = 'id',
  /** column name */
  org_id = 'org_id',
  /** column name */
  updated_at = 'updated_at',
}

/** unique or primary key constraints on table "cbi_invoice_snapshot" */
export enum cbi_invoice_snapshot_constraint {
  /** unique or primary key constraint on columns "cbi_invoice_id" */
  cbi_invoice_snapshot_active_unique_idx = 'cbi_invoice_snapshot_active_unique_idx',
  /** unique or primary key constraint on columns "id" */
  cbi_invoice_snapshot_pkey = 'cbi_invoice_snapshot_pkey',
  /** unique or primary key constraint on columns "id", "cbi_invoice_id" */
  uq_cbi_invoice_snapshot_composite = 'uq_cbi_invoice_snapshot_composite',
}

/** select columns of table "cbi_invoice_snapshot" */
export enum cbi_invoice_snapshot_select_column {
  /** column name */
  advance_payments = 'advance_payments',
  /** column name */
  amount_payable = 'amount_payable',
  /** column name */
  cbi_invoice_id = 'cbi_invoice_id',
  /** column name */
  co2_fee_gross = 'co2_fee_gross',
  /** column name */
  co2_fee_net = 'co2_fee_net',
  /** column name */
  created_at = 'created_at',
  /** column name */
  created_by_user_id = 'created_by_user_id',
  /** column name */
  credit_amount = 'credit_amount',
  /** column name */
  edit_reason = 'edit_reason',
  /** column name */
  extraction_needs_review_reason = 'extraction_needs_review_reason',
  /** column name */
  extraction_score = 'extraction_score',
  /** column name */
  extraction_state = 'extraction_state',
  /** column name */
  extraction_type = 'extraction_type',
  /** column name */
  id = 'id',
  /** column name */
  invoice_date = 'invoice_date',
  /** column name */
  invoice_number = 'invoice_number',
  /** column name */
  invoice_reference = 'invoice_reference',
  /** column name */
  invoice_type = 'invoice_type',
  /** column name */
  is_active = 'is_active',
  /** column name */
  operational_control = 'operational_control',
  /** column name */
  original_snapshot_id = 'original_snapshot_id',
  /** column name */
  outstanding_balance = 'outstanding_balance',
  /** column name */
  provider = 'provider',
  /** column name */
  total_cost_gross = 'total_cost_gross',
  /** column name */
  total_cost_net = 'total_cost_net',
  /** column name */
  updated_at = 'updated_at',
  /** column name */
  validation_issues = 'validation_issues',
  /** column name */
  vat_amount = 'vat_amount',
  /** column name */
  vat_percentage = 'vat_percentage',
}

/** select "cbi_invoice_snapshot_aggregate_bool_exp_avg_arguments_columns" columns of table "cbi_invoice_snapshot" */
export enum cbi_invoice_snapshot_select_column_cbi_invoice_snapshot_aggregate_bool_exp_avg_arguments_columns {
  /** column name */
  extraction_score = 'extraction_score',
}

/** select "cbi_invoice_snapshot_aggregate_bool_exp_bool_and_arguments_columns" columns of table "cbi_invoice_snapshot" */
export enum cbi_invoice_snapshot_select_column_cbi_invoice_snapshot_aggregate_bool_exp_bool_and_arguments_columns {
  /** column name */
  is_active = 'is_active',
}

/** select "cbi_invoice_snapshot_aggregate_bool_exp_bool_or_arguments_columns" columns of table "cbi_invoice_snapshot" */
export enum cbi_invoice_snapshot_select_column_cbi_invoice_snapshot_aggregate_bool_exp_bool_or_arguments_columns {
  /** column name */
  is_active = 'is_active',
}

/** select "cbi_invoice_snapshot_aggregate_bool_exp_corr_arguments_columns" columns of table "cbi_invoice_snapshot" */
export enum cbi_invoice_snapshot_select_column_cbi_invoice_snapshot_aggregate_bool_exp_corr_arguments_columns {
  /** column name */
  extraction_score = 'extraction_score',
}

/** select "cbi_invoice_snapshot_aggregate_bool_exp_covar_samp_arguments_columns" columns of table "cbi_invoice_snapshot" */
export enum cbi_invoice_snapshot_select_column_cbi_invoice_snapshot_aggregate_bool_exp_covar_samp_arguments_columns {
  /** column name */
  extraction_score = 'extraction_score',
}

/** select "cbi_invoice_snapshot_aggregate_bool_exp_max_arguments_columns" columns of table "cbi_invoice_snapshot" */
export enum cbi_invoice_snapshot_select_column_cbi_invoice_snapshot_aggregate_bool_exp_max_arguments_columns {
  /** column name */
  extraction_score = 'extraction_score',
}

/** select "cbi_invoice_snapshot_aggregate_bool_exp_min_arguments_columns" columns of table "cbi_invoice_snapshot" */
export enum cbi_invoice_snapshot_select_column_cbi_invoice_snapshot_aggregate_bool_exp_min_arguments_columns {
  /** column name */
  extraction_score = 'extraction_score',
}

/** select "cbi_invoice_snapshot_aggregate_bool_exp_stddev_samp_arguments_columns" columns of table "cbi_invoice_snapshot" */
export enum cbi_invoice_snapshot_select_column_cbi_invoice_snapshot_aggregate_bool_exp_stddev_samp_arguments_columns {
  /** column name */
  extraction_score = 'extraction_score',
}

/** select "cbi_invoice_snapshot_aggregate_bool_exp_sum_arguments_columns" columns of table "cbi_invoice_snapshot" */
export enum cbi_invoice_snapshot_select_column_cbi_invoice_snapshot_aggregate_bool_exp_sum_arguments_columns {
  /** column name */
  extraction_score = 'extraction_score',
}

/** select "cbi_invoice_snapshot_aggregate_bool_exp_var_samp_arguments_columns" columns of table "cbi_invoice_snapshot" */
export enum cbi_invoice_snapshot_select_column_cbi_invoice_snapshot_aggregate_bool_exp_var_samp_arguments_columns {
  /** column name */
  extraction_score = 'extraction_score',
}

/** update columns of table "cbi_invoice_snapshot" */
export enum cbi_invoice_snapshot_update_column {
  /** column name */
  advance_payments = 'advance_payments',
  /** column name */
  amount_payable = 'amount_payable',
  /** column name */
  cbi_invoice_id = 'cbi_invoice_id',
  /** column name */
  co2_fee_gross = 'co2_fee_gross',
  /** column name */
  co2_fee_net = 'co2_fee_net',
  /** column name */
  created_at = 'created_at',
  /** column name */
  created_by_user_id = 'created_by_user_id',
  /** column name */
  credit_amount = 'credit_amount',
  /** column name */
  edit_reason = 'edit_reason',
  /** column name */
  extraction_needs_review_reason = 'extraction_needs_review_reason',
  /** column name */
  extraction_score = 'extraction_score',
  /** column name */
  extraction_state = 'extraction_state',
  /** column name */
  extraction_type = 'extraction_type',
  /** column name */
  id = 'id',
  /** column name */
  invoice_date = 'invoice_date',
  /** column name */
  invoice_number = 'invoice_number',
  /** column name */
  invoice_reference = 'invoice_reference',
  /** column name */
  invoice_type = 'invoice_type',
  /** column name */
  is_active = 'is_active',
  /** column name */
  operational_control = 'operational_control',
  /** column name */
  original_snapshot_id = 'original_snapshot_id',
  /** column name */
  outstanding_balance = 'outstanding_balance',
  /** column name */
  provider = 'provider',
  /** column name */
  total_cost_gross = 'total_cost_gross',
  /** column name */
  total_cost_net = 'total_cost_net',
  /** column name */
  updated_at = 'updated_at',
  /** column name */
  validation_issues = 'validation_issues',
  /** column name */
  vat_amount = 'vat_amount',
  /** column name */
  vat_percentage = 'vat_percentage',
}

/** unique or primary key constraints on table "cbi_invoice_source" */
export enum cbi_invoice_source_constraint {
  /** unique or primary key constraint on columns "id" */
  cbi_invoice_source_pkey = 'cbi_invoice_source_pkey',
}

export enum cbi_invoice_source_enum {
  DIRECT_UPLOAD = 'DIRECT_UPLOAD',
  EMAIL = 'EMAIL',
}

/** select columns of table "cbi_invoice_source" */
export enum cbi_invoice_source_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "cbi_invoice_source" */
export enum cbi_invoice_source_update_column {
  /** column name */
  id = 'id',
}

/** unique or primary key constraints on table "cbi_invoice_type" */
export enum cbi_invoice_type_constraint {
  /** unique or primary key constraint on columns "id" */
  cbi_invoice_type_pkey = 'cbi_invoice_type_pkey',
}

export enum cbi_invoice_type_enum {
  CANCELLATION = 'CANCELLATION',
  CORRECTION = 'CORRECTION',
  FEE_NOTICE = 'FEE_NOTICE',
  FEE_NOTICE_CORRECTION = 'FEE_NOTICE_CORRECTION',
  INVOICE = 'INVOICE',
}

/** select columns of table "cbi_invoice_type" */
export enum cbi_invoice_type_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "cbi_invoice_type" */
export enum cbi_invoice_type_update_column {
  /** column name */
  id = 'id',
}

/** update columns of table "cbi_invoice" */
export enum cbi_invoice_update_column {
  /** column name */
  cbi_email_id = 'cbi_email_id',
  /** column name */
  cbi_invoice_source_id = 'cbi_invoice_source_id',
  /** column name */
  checksum = 'checksum',
  /** column name */
  created_at = 'created_at',
  /** column name */
  duplicate_of_id = 'duplicate_of_id',
  /** column name */
  duplicate_reason = 'duplicate_reason',
  /** column name */
  duplicate_reason_type = 'duplicate_reason_type',
  /** column name */
  file_id = 'file_id',
  /** column name */
  id = 'id',
  /** column name */
  org_id = 'org_id',
  /** column name */
  updated_at = 'updated_at',
}

/** Enumeration for manual review states of invoice snapshots. Subset of cbi_extraction_state_enum */
export enum cbi_manual_review_state_enum {
  MANUAL_APPROVAL = 'MANUAL_APPROVAL',
  MANUAL_REJECTION = 'MANUAL_REJECTION',
  NEEDS_REVIEW = 'NEEDS_REVIEW',
}

/** unique or primary key constraints on table "cbi_operational_control" */
export enum cbi_operational_control_constraint {
  /** unique or primary key constraint on columns "id" */
  cbi_operational_control_pkey = 'cbi_operational_control_pkey',
}

export enum cbi_operational_control_enum {
  LANDLORD_CONTROLLED = 'LANDLORD_CONTROLLED',
  TENANT_CONTROLLED = 'TENANT_CONTROLLED',
}

/** select columns of table "cbi_operational_control" */
export enum cbi_operational_control_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "cbi_operational_control" */
export enum cbi_operational_control_update_column {
  /** column name */
  id = 'id',
}

/** unique or primary key constraints on table "cbi_potential_building_match" */
export enum cbi_potential_building_match_constraint {
  /** unique or primary key constraint on columns "id" */
  cbi_potential_building_match_pkey = 'cbi_potential_building_match_pkey',
}

/** select columns of table "cbi_potential_building_match" */
export enum cbi_potential_building_match_select_column {
  /** column name */
  area_id = 'area_id',
  /** column name */
  building_id = 'building_id',
  /** column name */
  cbi_address_id = 'cbi_address_id',
  /** column name */
  created_at = 'created_at',
  /** column name */
  id = 'id',
  /** column name */
  is_auto_assigned = 'is_auto_assigned',
  /** column name */
  score = 'score',
  /** column name */
  updated_at = 'updated_at',
}

/** select "cbi_potential_building_match_aggregate_bool_exp_avg_arguments_columns" columns of table "cbi_potential_building_match" */
export enum cbi_potential_building_match_select_column_cbi_potential_building_match_aggregate_bool_exp_avg_arguments_columns {
  /** column name */
  score = 'score',
}

/** select "cbi_potential_building_match_aggregate_bool_exp_bool_and_arguments_columns" columns of table "cbi_potential_building_match" */
export enum cbi_potential_building_match_select_column_cbi_potential_building_match_aggregate_bool_exp_bool_and_arguments_columns {
  /** column name */
  is_auto_assigned = 'is_auto_assigned',
}

/** select "cbi_potential_building_match_aggregate_bool_exp_bool_or_arguments_columns" columns of table "cbi_potential_building_match" */
export enum cbi_potential_building_match_select_column_cbi_potential_building_match_aggregate_bool_exp_bool_or_arguments_columns {
  /** column name */
  is_auto_assigned = 'is_auto_assigned',
}

/** select "cbi_potential_building_match_aggregate_bool_exp_corr_arguments_columns" columns of table "cbi_potential_building_match" */
export enum cbi_potential_building_match_select_column_cbi_potential_building_match_aggregate_bool_exp_corr_arguments_columns {
  /** column name */
  score = 'score',
}

/** select "cbi_potential_building_match_aggregate_bool_exp_covar_samp_arguments_columns" columns of table "cbi_potential_building_match" */
export enum cbi_potential_building_match_select_column_cbi_potential_building_match_aggregate_bool_exp_covar_samp_arguments_columns {
  /** column name */
  score = 'score',
}

/** select "cbi_potential_building_match_aggregate_bool_exp_max_arguments_columns" columns of table "cbi_potential_building_match" */
export enum cbi_potential_building_match_select_column_cbi_potential_building_match_aggregate_bool_exp_max_arguments_columns {
  /** column name */
  score = 'score',
}

/** select "cbi_potential_building_match_aggregate_bool_exp_min_arguments_columns" columns of table "cbi_potential_building_match" */
export enum cbi_potential_building_match_select_column_cbi_potential_building_match_aggregate_bool_exp_min_arguments_columns {
  /** column name */
  score = 'score',
}

/** select "cbi_potential_building_match_aggregate_bool_exp_stddev_samp_arguments_columns" columns of table "cbi_potential_building_match" */
export enum cbi_potential_building_match_select_column_cbi_potential_building_match_aggregate_bool_exp_stddev_samp_arguments_columns {
  /** column name */
  score = 'score',
}

/** select "cbi_potential_building_match_aggregate_bool_exp_sum_arguments_columns" columns of table "cbi_potential_building_match" */
export enum cbi_potential_building_match_select_column_cbi_potential_building_match_aggregate_bool_exp_sum_arguments_columns {
  /** column name */
  score = 'score',
}

/** select "cbi_potential_building_match_aggregate_bool_exp_var_samp_arguments_columns" columns of table "cbi_potential_building_match" */
export enum cbi_potential_building_match_select_column_cbi_potential_building_match_aggregate_bool_exp_var_samp_arguments_columns {
  /** column name */
  score = 'score',
}

/** update columns of table "cbi_potential_building_match" */
export enum cbi_potential_building_match_update_column {
  /** column name */
  area_id = 'area_id',
  /** column name */
  building_id = 'building_id',
  /** column name */
  cbi_address_id = 'cbi_address_id',
  /** column name */
  created_at = 'created_at',
  /** column name */
  id = 'id',
  /** column name */
  is_auto_assigned = 'is_auto_assigned',
  /** column name */
  score = 'score',
  /** column name */
  updated_at = 'updated_at',
}

/** unique or primary key constraints on table "cbi_reference_area" */
export enum cbi_reference_area_constraint {
  /** unique or primary key constraint on columns "id" */
  cbi_reference_area_pkey = 'cbi_reference_area_pkey',
}

export enum cbi_reference_area_enum {
  COMMON_AREA = 'COMMON_AREA',
  TENANT_AREA = 'TENANT_AREA',
  WHOLE_BUILDING = 'WHOLE_BUILDING',
}

/** select columns of table "cbi_reference_area" */
export enum cbi_reference_area_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "cbi_reference_area" */
export enum cbi_reference_area_update_column {
  /** column name */
  id = 'id',
}

/** unique or primary key constraints on table "cbi_waste_container_frequency_period" */
export enum cbi_waste_container_frequency_period_constraint {
  /** unique or primary key constraint on columns "id" */
  cbi_waste_container_frequency_period_pkey = 'cbi_waste_container_frequency_period_pkey',
}

export enum cbi_waste_container_frequency_period_enum {
  BIWEEKLY = 'BIWEEKLY',
  DAILY = 'DAILY',
  MONTHLY = 'MONTHLY',
  ONCE = 'ONCE',
  QUARTERLY = 'QUARTERLY',
  WEEKLY = 'WEEKLY',
  YEARLY = 'YEARLY',
}

/** select columns of table "cbi_waste_container_frequency_period" */
export enum cbi_waste_container_frequency_period_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "cbi_waste_container_frequency_period" */
export enum cbi_waste_container_frequency_period_update_column {
  /** column name */
  id = 'id',
}

/** unique or primary key constraints on table "cbi_waste_container_size_unit" */
export enum cbi_waste_container_size_unit_constraint {
  /** unique or primary key constraint on columns "id" */
  cbi_waste_container_size_unit_pkey = 'cbi_waste_container_size_unit_pkey',
}

export enum cbi_waste_container_size_unit_enum {
  L = 'L',
  M3 = 'M3',
}

/** select columns of table "cbi_waste_container_size_unit" */
export enum cbi_waste_container_size_unit_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "cbi_waste_container_size_unit" */
export enum cbi_waste_container_size_unit_update_column {
  /** column name */
  id = 'id',
}

/** unique or primary key constraints on table "cbi_waste_quantity_unit" */
export enum cbi_waste_quantity_unit_constraint {
  /** unique or primary key constraint on columns "id" */
  cbi_waste_quantity_unit_pkey = 'cbi_waste_quantity_unit_pkey',
}

export enum cbi_waste_quantity_unit_enum {
  KG = 'KG',
  L = 'L',
  M3 = 'M3',
  PIECE = 'PIECE',
  T = 'T',
}

/** select columns of table "cbi_waste_quantity_unit" */
export enum cbi_waste_quantity_unit_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "cbi_waste_quantity_unit" */
export enum cbi_waste_quantity_unit_update_column {
  /** column name */
  id = 'id',
}

/** unique or primary key constraints on table "cbi_waste_sub_type" */
export enum cbi_waste_sub_type_constraint {
  /** unique or primary key constraint on columns "id" */
  cbi_waste_sub_type_pkey = 'cbi_waste_sub_type_pkey',
}

export enum cbi_waste_sub_type_enum {
  BULKY = 'BULKY',
  CONSTRUCTION_AND_EXCAVATION = 'CONSTRUCTION_AND_EXCAVATION',
  ELECTRONIC_EQUIPMENT = 'ELECTRONIC_EQUIPMENT',
  GLASS = 'GLASS',
  ORGANIC = 'ORGANIC',
  OTHER_NON_RECYCLABLES = 'OTHER_NON_RECYCLABLES',
  OTHER_RECYCLABLES = 'OTHER_RECYCLABLES',
  PACKAGING = 'PACKAGING',
  PAPER = 'PAPER',
  RESIDUAL_WASTE = 'RESIDUAL_WASTE',
  WOOD = 'WOOD',
}

/** select columns of table "cbi_waste_sub_type" */
export enum cbi_waste_sub_type_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "cbi_waste_sub_type" */
export enum cbi_waste_sub_type_update_column {
  /** column name */
  id = 'id',
}

/** unique or primary key constraints on table "cbi_water_sub_type" */
export enum cbi_water_sub_type_constraint {
  /** unique or primary key constraint on columns "id" */
  cbi_water_sub_type_pkey = 'cbi_water_sub_type_pkey',
}

export enum cbi_water_sub_type_enum {
  DRINKING_WATER = 'DRINKING_WATER',
  RAINWATER = 'RAINWATER',
  WASTEWATER = 'WASTEWATER',
}

/** select columns of table "cbi_water_sub_type" */
export enum cbi_water_sub_type_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "cbi_water_sub_type" */
export enum cbi_water_sub_type_update_column {
  /** column name */
  id = 'id',
}

/** unique or primary key constraints on table "certificate" */
export enum certificate_constraint {
  /** unique or primary key constraint on columns "id" */
  certificate_pkey = 'certificate_pkey',
}

/** unique or primary key constraints on table "certificate_level_type" */
export enum certificate_level_type_constraint {
  /** unique or primary key constraint on columns "id" */
  certificate_level_type_pkey = 'certificate_level_type_pkey',
}

export enum certificate_level_type_enum {
  /** BREEAM */
  BREEAM_EXCELLENT = 'BREEAM_EXCELLENT',
  /** BREEAM */
  BREEAM_GOOD = 'BREEAM_GOOD',
  /** BREEAM */
  BREEAM_OUTSTANDING = 'BREEAM_OUTSTANDING',
  /** BREEAM */
  BREEAM_PASS = 'BREEAM_PASS',
  /** BREEAM */
  BREEAM_UNCLASSIFIED = 'BREEAM_UNCLASSIFIED',
  /** BREEAM */
  BREEAM_VERY_GOOD = 'BREEAM_VERY_GOOD',
  /** DGNB */
  DGNB_BRONZE = 'DGNB_BRONZE',
  /** DGNB */
  DGNB_GOLD = 'DGNB_GOLD',
  /** DGNB */
  DGNB_PLATINUM = 'DGNB_PLATINUM',
  /** DGNB */
  DGNB_SILVER = 'DGNB_SILVER',
  /** HQE */
  HQE_EXCELLENT = 'HQE_EXCELLENT',
  /** HQE */
  HQE_GOOD = 'HQE_GOOD',
  /** HQE */
  HQE_OUTSTANDING = 'HQE_OUTSTANDING',
  /** HQE */
  HQE_VERY_GOOD = 'HQE_VERY_GOOD',
  /** LEED */
  LEED_CERTIFIED = 'LEED_CERTIFIED',
  /** LEED */
  LEED_GOLD = 'LEED_GOLD',
  /** LEED */
  LEED_PLATINUM = 'LEED_PLATINUM',
  /** LEED */
  LEED_SILVER = 'LEED_SILVER',
}

/** select columns of table "certificate_level_type" */
export enum certificate_level_type_select_column {
  /** column name */
  certificate_type = 'certificate_type',
  /** column name */
  id = 'id',
}

/** update columns of table "certificate_level_type" */
export enum certificate_level_type_update_column {
  /** column name */
  certificate_type = 'certificate_type',
  /** column name */
  id = 'id',
}

/** select columns of table "certificate" */
export enum certificate_select_column {
  /** column name */
  building_draft_id = 'building_draft_id',
  /** column name */
  building_id = 'building_id',
  /** column name */
  certificate_level_type = 'certificate_level_type',
  /** column name */
  certificate_score = 'certificate_score',
  /** column name */
  certificate_type = 'certificate_type',
  /** column name */
  created_at = 'created_at',
  /** column name */
  file_id = 'file_id',
  /** column name */
  id = 'id',
  /** column name */
  updated_at = 'updated_at',
}

/** unique or primary key constraints on table "certificate_type" */
export enum certificate_type_constraint {
  /** unique or primary key constraint on columns "id" */
  certificate_type_pkey = 'certificate_type_pkey',
}

export enum certificate_type_enum {
  BREEAM = 'BREEAM',
  DGNB = 'DGNB',
  ENERGY_STAR_SCORE = 'ENERGY_STAR_SCORE',
  HQE = 'HQE',
  LEED = 'LEED',
  LIVING_FUTURE_ZERO_CARBON = 'LIVING_FUTURE_ZERO_CARBON',
  LIVING_FUTURE_ZERO_ENERGY = 'LIVING_FUTURE_ZERO_ENERGY',
}

/** select columns of table "certificate_type" */
export enum certificate_type_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "certificate_type" */
export enum certificate_type_update_column {
  /** column name */
  id = 'id',
}

/** update columns of table "certificate" */
export enum certificate_update_column {
  /** column name */
  building_draft_id = 'building_draft_id',
  /** column name */
  building_id = 'building_id',
  /** column name */
  certificate_level_type = 'certificate_level_type',
  /** column name */
  certificate_score = 'certificate_score',
  /** column name */
  certificate_type = 'certificate_type',
  /** column name */
  created_at = 'created_at',
  /** column name */
  file_id = 'file_id',
  /** column name */
  id = 'id',
  /** column name */
  updated_at = 'updated_at',
}

/** unique or primary key constraints on table "class_of_use" */
export enum class_of_use_constraint {
  /** unique or primary key constraint on columns "id" */
  class_of_use_pkey = 'class_of_use_pkey',
}

export enum class_of_use_enum {
  COMMERCIAL = 'COMMERCIAL',
  RESIDENTIAL = 'RESIDENTIAL',
}

/** select columns of table "class_of_use" */
export enum class_of_use_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "class_of_use" */
export enum class_of_use_update_column {
  /** column name */
  id = 'id',
}

/** unique or primary key constraints on table "climate_region" */
export enum climate_region_constraint {
  /** unique or primary key constraint on columns "id" */
  climate_region_pkey = 'climate_region_pkey',
}

export enum climate_region_enum {
  AT_NATIONAL = 'AT_NATIONAL',
  BE_NATIONAL = 'BE_NATIONAL',
  CZ_NATIONAL = 'CZ_NATIONAL',
  DE_EASTERN = 'DE_EASTERN',
  DE_NATIONAL = 'DE_NATIONAL',
  FR_NATIONAL = 'FR_NATIONAL',
  PL_NATIONAL = 'PL_NATIONAL',
  UK_NATIONAL = 'UK_NATIONAL',
}

/** select columns of table "climate_region" */
export enum climate_region_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "climate_region" */
export enum climate_region_update_column {
  /** column name */
  id = 'id',
}

/** unique or primary key constraints on table "climate_risk" */
export enum climate_risk_constraint {
  /** unique or primary key constraint on columns "id" */
  climate_risk_pkey = 'climate_risk_pkey',
}

/** select columns of table "climate_risk" */
export enum climate_risk_select_column {
  /** column name */
  building_id = 'building_id',
  /** column name */
  created_at = 'created_at',
  /** column name */
  id = 'id',
  /** column name */
  timespan = 'timespan',
  /** column name */
  type = 'type',
  /** column name */
  uncertainty = 'uncertainty',
  /** column name */
  value = 'value',
}

/** unique or primary key constraints on table "climate_risk_type" */
export enum climate_risk_type_constraint {
  /** unique or primary key constraint on columns "id" */
  climate_risk_type_pkey = 'climate_risk_type_pkey',
}

export enum climate_risk_type_enum {
  EARTHQUAKE = 'EARTHQUAKE',
  FOREST_FIRE = 'FOREST_FIRE',
  HAIL = 'HAIL',
  HEAT = 'HEAT',
  HEAVY_RAIN = 'HEAVY_RAIN',
  LIGHTNING_STRIKE = 'LIGHTNING_STRIKE',
  SNOW_LOAD = 'SNOW_LOAD',
  WINTER_STORM = 'WINTER_STORM',
}

/** select columns of table "climate_risk_type" */
export enum climate_risk_type_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "climate_risk_type" */
export enum climate_risk_type_update_column {
  /** column name */
  id = 'id',
}

/** update columns of table "climate_risk" */
export enum climate_risk_update_column {
  /** column name */
  building_id = 'building_id',
  /** column name */
  created_at = 'created_at',
  /** column name */
  id = 'id',
  /** column name */
  timespan = 'timespan',
  /** column name */
  type = 'type',
  /** column name */
  uncertainty = 'uncertainty',
  /** column name */
  value = 'value',
}

/** unique or primary key constraints on table "climate_risk_v2" */
export enum climate_risk_v2_constraint {
  /** unique or primary key constraint on columns "api_response_file_id" */
  climate_risk_v2_api_response_file_id_key = 'climate_risk_v2_api_response_file_id_key',
  /** unique or primary key constraint on columns "id" */
  climate_risk_v2_pkey = 'climate_risk_v2_pkey',
}

/** select columns of table "climate_risk_v2" */
export enum climate_risk_v2_select_column {
  /** column name */
  api_response_file_id = 'api_response_file_id',
  /** column name */
  api_response_status_id = 'api_response_status_id',
  /** column name */
  asset_id = 'asset_id',
  /** column name */
  building_id = 'building_id',
  /** column name */
  callback_received_at = 'callback_received_at',
  /** column name */
  created_at = 'created_at',
  /** column name */
  id = 'id',
  /** column name */
  report_id = 'report_id',
  /** column name */
  request_hash = 'request_hash',
  /** column name */
  request_params = 'request_params',
  /** column name */
  updated_at = 'updated_at',
}

/** update columns of table "climate_risk_v2" */
export enum climate_risk_v2_update_column {
  /** column name */
  api_response_file_id = 'api_response_file_id',
  /** column name */
  api_response_status_id = 'api_response_status_id',
  /** column name */
  asset_id = 'asset_id',
  /** column name */
  building_id = 'building_id',
  /** column name */
  callback_received_at = 'callback_received_at',
  /** column name */
  created_at = 'created_at',
  /** column name */
  id = 'id',
  /** column name */
  report_id = 'report_id',
  /** column name */
  request_hash = 'request_hash',
  /** column name */
  request_params = 'request_params',
  /** column name */
  updated_at = 'updated_at',
}

/** unique or primary key constraints on table "co2_price_curve_kind" */
export enum co2_price_curve_kind_constraint {
  /** unique or primary key constraint on columns "id" */
  co2_price_curve_kind_pkey = 'co2_price_curve_kind_pkey',
}

export enum co2_price_curve_kind_enum {
  ANCHORS = 'ANCHORS',
  GROWTH = 'GROWTH',
}

/** select columns of table "co2_price_curve_kind" */
export enum co2_price_curve_kind_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "co2_price_curve_kind" */
export enum co2_price_curve_kind_update_column {
  /** column name */
  id = 'id',
}

/** unique or primary key constraints on table "consumption_area" */
export enum consumption_area_constraint {
  /** unique or primary key constraint on columns "consumption_id", "building_id", "area_id" */
  consumption_area_pkey = 'consumption_area_pkey',
}

/** select columns of table "consumption_area" */
export enum consumption_area_select_column {
  /** column name */
  area_id = 'area_id',
  /** column name */
  building_id = 'building_id',
  /** column name */
  consumption_id = 'consumption_id',
}

/** update columns of table "consumption_area" */
export enum consumption_area_update_column {
  /** column name */
  area_id = 'area_id',
  /** column name */
  building_id = 'building_id',
  /** column name */
  consumption_id = 'consumption_id',
}

/** unique or primary key constraints on table "consumption" */
export enum consumption_constraint {
  /** unique or primary key constraint on columns "building_id", "cbi_consumption_line_id" */
  consumption_cbi_line_building_unique = 'consumption_cbi_line_building_unique',
  /** unique or primary key constraint on columns "id", "building_id" */
  consumption_id_building_id_unique = 'consumption_id_building_id_unique',
  /** unique or primary key constraint on columns "id" */
  consumption_pkey = 'consumption_pkey',
  /** unique or primary key constraint on columns "source_type_id", "meter_id", "to", "from" */
  consumption_smart_meter_dedupe_idx = 'consumption_smart_meter_dedupe_idx',
}

/** unique or primary key constraints on table "consumption_draft_area" */
export enum consumption_draft_area_constraint {
  /** unique or primary key constraint on columns "consumption_draft_id", "building_id", "area_id" */
  consumption_draft_area_pkey = 'consumption_draft_area_pkey',
}

/** select columns of table "consumption_draft_area" */
export enum consumption_draft_area_select_column {
  /** column name */
  area_id = 'area_id',
  /** column name */
  building_id = 'building_id',
  /** column name */
  consumption_draft_id = 'consumption_draft_id',
}

/** update columns of table "consumption_draft_area" */
export enum consumption_draft_area_update_column {
  /** column name */
  area_id = 'area_id',
  /** column name */
  building_id = 'building_id',
  /** column name */
  consumption_draft_id = 'consumption_draft_id',
}

/** unique or primary key constraints on table "consumption_draft" */
export enum consumption_draft_constraint {
  /** unique or primary key constraint on columns "id", "building_id" */
  consumption_draft_id_building_id_unique = 'consumption_draft_id_building_id_unique',
  /** unique or primary key constraint on columns "id" */
  consumption_draft_pkey = 'consumption_draft_pkey',
}

/** select columns of table "consumption_draft" */
export enum consumption_draft_select_column {
  /** column name */
  building_id = 'building_id',
  /** column name */
  consumption_type_id = 'consumption_type_id',
  /** column name */
  cost = 'cost',
  /** column name */
  created_at = 'created_at',
  /** column name */
  created_by_user_id = 'created_by_user_id',
  /** column name */
  display_unit_value = 'display_unit_value',
  /** column name */
  draft_status_deprecated = 'draft_status_deprecated',
  /** column name */
  energy_source_type_id = 'energy_source_type_id',
  /** column name */
  from = 'from',
  /** column name */
  id = 'id',
  /** column name */
  invoice_date_deprecated = 'invoice_date_deprecated',
  /** column name */
  invoice_draft_id = 'invoice_draft_id',
  /** column name */
  invoice_file_id_deprecated = 'invoice_file_id_deprecated',
  /** column name */
  provider = 'provider',
  /** column name */
  source_type_id = 'source_type_id',
  /** column name */
  sub_type_id = 'sub_type_id',
  /** column name */
  to = 'to',
  /** column name */
  updated_at = 'updated_at',
  /** column name */
  value = 'value',
}

/** update columns of table "consumption_draft" */
export enum consumption_draft_update_column {
  /** column name */
  building_id = 'building_id',
  /** column name */
  consumption_type_id = 'consumption_type_id',
  /** column name */
  cost = 'cost',
  /** column name */
  created_at = 'created_at',
  /** column name */
  created_by_user_id = 'created_by_user_id',
  /** column name */
  display_unit_value = 'display_unit_value',
  /** column name */
  draft_status_deprecated = 'draft_status_deprecated',
  /** column name */
  energy_source_type_id = 'energy_source_type_id',
  /** column name */
  from = 'from',
  /** column name */
  id = 'id',
  /** column name */
  invoice_date_deprecated = 'invoice_date_deprecated',
  /** column name */
  invoice_draft_id = 'invoice_draft_id',
  /** column name */
  invoice_file_id_deprecated = 'invoice_file_id_deprecated',
  /** column name */
  provider = 'provider',
  /** column name */
  source_type_id = 'source_type_id',
  /** column name */
  sub_type_id = 'sub_type_id',
  /** column name */
  to = 'to',
  /** column name */
  updated_at = 'updated_at',
  /** column name */
  value = 'value',
}

/** unique or primary key constraints on table "consumption_frequency_type" */
export enum consumption_frequency_type_constraint {
  /** unique or primary key constraint on columns "id" */
  consumption_frequency_type_pkey = 'consumption_frequency_type_pkey',
}

export enum consumption_frequency_type_enum {
  BIWEEKLY = 'BIWEEKLY',
  DAILY = 'DAILY',
  HOURLY = 'HOURLY',
  MONTHLY = 'MONTHLY',
  ONCE = 'ONCE',
  QUARTER_HOURLY = 'QUARTER_HOURLY',
  QUARTER_YEARLY = 'QUARTER_YEARLY',
  WEEKLY = 'WEEKLY',
  YEARLY = 'YEARLY',
}

/** select columns of table "consumption_frequency_type" */
export enum consumption_frequency_type_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "consumption_frequency_type" */
export enum consumption_frequency_type_update_column {
  /** column name */
  id = 'id',
}

/** unique or primary key constraints on table "consumption_invoice" */
export enum consumption_invoice_constraint {
  /** unique or primary key constraint on columns "id" */
  consumption_invoice_pkey = 'consumption_invoice_pkey',
}

/** unique or primary key constraints on table "consumption_invoice_draft" */
export enum consumption_invoice_draft_constraint {
  /** unique or primary key constraint on columns "id" */
  consumption_invoice_draft_pkey = 'consumption_invoice_draft_pkey',
}

/** select columns of table "consumption_invoice_draft" */
export enum consumption_invoice_draft_select_column {
  /** column name */
  building_id = 'building_id',
  /** column name */
  created_at = 'created_at',
  /** column name */
  date = 'date',
  /** column name */
  draft_status = 'draft_status',
  /** column name */
  file_id = 'file_id',
  /** column name */
  id = 'id',
  /** column name */
  invoice_payer = 'invoice_payer',
  /** column name */
  updated_at = 'updated_at',
}

/** update columns of table "consumption_invoice_draft" */
export enum consumption_invoice_draft_update_column {
  /** column name */
  building_id = 'building_id',
  /** column name */
  created_at = 'created_at',
  /** column name */
  date = 'date',
  /** column name */
  draft_status = 'draft_status',
  /** column name */
  file_id = 'file_id',
  /** column name */
  id = 'id',
  /** column name */
  invoice_payer = 'invoice_payer',
  /** column name */
  updated_at = 'updated_at',
}

/** select columns of table "consumption_invoice" */
export enum consumption_invoice_select_column {
  /** column name */
  building_id = 'building_id',
  /** column name */
  created_at = 'created_at',
  /** column name */
  date = 'date',
  /** column name */
  file_id = 'file_id',
  /** column name */
  id = 'id',
  /** column name */
  invoice_payer = 'invoice_payer',
  /** column name */
  updated_at = 'updated_at',
}

/** update columns of table "consumption_invoice" */
export enum consumption_invoice_update_column {
  /** column name */
  building_id = 'building_id',
  /** column name */
  created_at = 'created_at',
  /** column name */
  date = 'date',
  /** column name */
  file_id = 'file_id',
  /** column name */
  id = 'id',
  /** column name */
  invoice_payer = 'invoice_payer',
  /** column name */
  updated_at = 'updated_at',
}

/** select columns of table "consumption" */
export enum consumption_select_column {
  /** column name */
  archived_at = 'archived_at',
  /** column name */
  building_id = 'building_id',
  /** column name */
  cbi_consumption_line_id = 'cbi_consumption_line_id',
  /** column name */
  co2_fee_gross = 'co2_fee_gross',
  /** column name */
  co2_fee_net = 'co2_fee_net',
  /** column name */
  consumption_type_id = 'consumption_type_id',
  /** column name */
  cost = 'cost',
  /** column name */
  cost_net = 'cost_net',
  /** column name */
  created_at = 'created_at',
  /** column name */
  created_by_user_id = 'created_by_user_id',
  /** column name */
  display_unit_value = 'display_unit_value',
  /** column name */
  energy_source_type_id = 'energy_source_type_id',
  /** column name */
  from = 'from',
  /** column name */
  id = 'id',
  /** column name */
  invoice_date_deprecated = 'invoice_date_deprecated',
  /** column name */
  invoice_file_id_deprecated = 'invoice_file_id_deprecated',
  /** column name */
  invoice_id = 'invoice_id',
  /** column name */
  market_location_id = 'market_location_id',
  /** column name */
  meter_id = 'meter_id',
  /** column name */
  meter_number = 'meter_number',
  /** column name */
  provider = 'provider',
  /** column name */
  source_type_id = 'source_type_id',
  /** column name */
  state = 'state',
  /** column name */
  sub_type_id = 'sub_type_id',
  /** column name */
  to = 'to',
  /** column name */
  updated_at = 'updated_at',
  /** column name */
  value = 'value',
}

/** unique or primary key constraints on table "consumption_source_type" */
export enum consumption_source_type_constraint {
  /** unique or primary key constraint on columns "id" */
  consumption_source_type_pkey = 'consumption_source_type_pkey',
}

export enum consumption_source_type_enum {
  API_ENTRY = 'API_ENTRY',
  ESTIMATION = 'ESTIMATION',
  INVOICE = 'INVOICE',
  MANUAL_ENTRY = 'MANUAL_ENTRY',
  METER_READING = 'METER_READING',
}

/** select columns of table "consumption_source_type" */
export enum consumption_source_type_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "consumption_source_type" */
export enum consumption_source_type_update_column {
  /** column name */
  id = 'id',
}

/** unique or primary key constraints on table "consumption_state" */
export enum consumption_state_constraint {
  /** unique or primary key constraint on columns "id" */
  consumption_state_pkey = 'consumption_state_pkey',
}

export enum consumption_state_enum {
  /** The consumption is included in all calculations. */
  ACTIVE = 'ACTIVE',
  /** The consumption is excluded from calculations but still visible in list views. Reversible. */
  ARCHIVED = 'ARCHIVED',
}

/** select columns of table "consumption_state" */
export enum consumption_state_select_column {
  /** column name */
  description = 'description',
  /** column name */
  id = 'id',
}

/** update columns of table "consumption_state" */
export enum consumption_state_update_column {
  /** column name */
  description = 'description',
  /** column name */
  id = 'id',
}

/** unique or primary key constraints on table "consumption_sub_type" */
export enum consumption_sub_type_constraint {
  /** unique or primary key constraint on columns "id" */
  consumption_sub_type_pkey = 'consumption_sub_type_pkey',
}

export enum consumption_sub_type_enum {
  WASTE_CONSTRUCTION_AND_EXCAVATION = 'WASTE_CONSTRUCTION_AND_EXCAVATION',
  WASTE_ELECTRONIC_EQUIPMENT = 'WASTE_ELECTRONIC_EQUIPMENT',
  WASTE_GLASS = 'WASTE_GLASS',
  WASTE_MUNICIPAL = 'WASTE_MUNICIPAL',
  WASTE_ORGANIC = 'WASTE_ORGANIC',
  WASTE_OTHER_NOT_RECYCLEABLE = 'WASTE_OTHER_NOT_RECYCLEABLE',
  WASTE_OTHER_RECYCLEABLE = 'WASTE_OTHER_RECYCLEABLE',
  WASTE_PACKAGING = 'WASTE_PACKAGING',
  WASTE_PAPER = 'WASTE_PAPER',
  WASTE_RESIDUAL = 'WASTE_RESIDUAL',
  WASTE_WOOD = 'WASTE_WOOD',
  WATER_DRINKING = 'WATER_DRINKING',
  WATER_RAINWATER = 'WATER_RAINWATER',
  WATER_SEWAGE = 'WATER_SEWAGE',
}

/** select columns of table "consumption_sub_type" */
export enum consumption_sub_type_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "consumption_sub_type" */
export enum consumption_sub_type_update_column {
  /** column name */
  id = 'id',
}

/** unique or primary key constraints on table "consumption_type" */
export enum consumption_type_constraint {
  /** unique or primary key constraint on columns "id" */
  CONSUMPTION_CATEGORY_pkey = 'CONSUMPTION_CATEGORY_pkey',
}

export enum consumption_type_enum {
  ELECTRICITY = 'ELECTRICITY',
  HEATING = 'HEATING',
  WASTE = 'WASTE',
  WATER = 'WATER',
}

/** select columns of table "consumption_type" */
export enum consumption_type_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "consumption_type" */
export enum consumption_type_update_column {
  /** column name */
  id = 'id',
}

/** unique or primary key constraints on table "consumption_unit" */
export enum consumption_unit_constraint {
  /** unique or primary key constraint on columns "id" */
  CONSUMPTION_UNIT_pkey = 'CONSUMPTION_UNIT_pkey',
}

export enum consumption_unit_enum {
  KWH = 'KWH',
  M3 = 'M3',
  MWh = 'MWh',
  T = 'T',
  WH = 'WH',
  kg = 'kg',
  l = 'l',
}

/** select columns of table "consumption_unit" */
export enum consumption_unit_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "consumption_unit" */
export enum consumption_unit_update_column {
  /** column name */
  id = 'id',
}

/** update columns of table "consumption" */
export enum consumption_update_column {
  /** column name */
  archived_at = 'archived_at',
  /** column name */
  building_id = 'building_id',
  /** column name */
  cbi_consumption_line_id = 'cbi_consumption_line_id',
  /** column name */
  co2_fee_gross = 'co2_fee_gross',
  /** column name */
  co2_fee_net = 'co2_fee_net',
  /** column name */
  consumption_type_id = 'consumption_type_id',
  /** column name */
  cost = 'cost',
  /** column name */
  cost_net = 'cost_net',
  /** column name */
  created_at = 'created_at',
  /** column name */
  created_by_user_id = 'created_by_user_id',
  /** column name */
  display_unit_value = 'display_unit_value',
  /** column name */
  energy_source_type_id = 'energy_source_type_id',
  /** column name */
  from = 'from',
  /** column name */
  id = 'id',
  /** column name */
  invoice_date_deprecated = 'invoice_date_deprecated',
  /** column name */
  invoice_file_id_deprecated = 'invoice_file_id_deprecated',
  /** column name */
  invoice_id = 'invoice_id',
  /** column name */
  market_location_id = 'market_location_id',
  /** column name */
  meter_id = 'meter_id',
  /** column name */
  meter_number = 'meter_number',
  /** column name */
  provider = 'provider',
  /** column name */
  source_type_id = 'source_type_id',
  /** column name */
  state = 'state',
  /** column name */
  sub_type_id = 'sub_type_id',
  /** column name */
  to = 'to',
  /** column name */
  updated_at = 'updated_at',
  /** column name */
  value = 'value',
}

/** unique or primary key constraints on table "consumption_waste_details" */
export enum consumption_waste_details_constraint {
  /** unique or primary key constraint on columns "consumption_id" */
  consumption_waste_details_consumption_id_key = 'consumption_waste_details_consumption_id_key',
  /** unique or primary key constraint on columns "id" */
  consumption_waste_details_pkey = 'consumption_waste_details_pkey',
}

/** unique or primary key constraints on table "consumption_waste_details_draft" */
export enum consumption_waste_details_draft_constraint {
  /** unique or primary key constraint on columns "consumption_draft_id" */
  consumption_waste_details_draft_consumption_draft_id_key = 'consumption_waste_details_draft_consumption_draft_id_key',
  /** unique or primary key constraint on columns "id" */
  consumption_waste_details_draft_pkey = 'consumption_waste_details_draft_pkey',
}

/** select columns of table "consumption_waste_details_draft" */
export enum consumption_waste_details_draft_select_column {
  /** column name */
  amount = 'amount',
  /** column name */
  consumption_draft_id = 'consumption_draft_id',
  /** column name */
  display_unit_frequency = 'display_unit_frequency',
  /** column name */
  display_unit_volume = 'display_unit_volume',
  /** column name */
  frequency = 'frequency',
  /** column name */
  id = 'id',
  /** column name */
  price = 'price',
  /** column name */
  volume = 'volume',
}

/** update columns of table "consumption_waste_details_draft" */
export enum consumption_waste_details_draft_update_column {
  /** column name */
  amount = 'amount',
  /** column name */
  consumption_draft_id = 'consumption_draft_id',
  /** column name */
  display_unit_frequency = 'display_unit_frequency',
  /** column name */
  display_unit_volume = 'display_unit_volume',
  /** column name */
  frequency = 'frequency',
  /** column name */
  id = 'id',
  /** column name */
  price = 'price',
  /** column name */
  volume = 'volume',
}

/** select columns of table "consumption_waste_details" */
export enum consumption_waste_details_select_column {
  /** column name */
  amount = 'amount',
  /** column name */
  consumption_id = 'consumption_id',
  /** column name */
  display_unit_frequency = 'display_unit_frequency',
  /** column name */
  display_unit_volume = 'display_unit_volume',
  /** column name */
  frequency = 'frequency',
  /** column name */
  id = 'id',
  /** column name */
  price = 'price',
  /** column name */
  volume = 'volume',
}

/** update columns of table "consumption_waste_details" */
export enum consumption_waste_details_update_column {
  /** column name */
  amount = 'amount',
  /** column name */
  consumption_id = 'consumption_id',
  /** column name */
  display_unit_frequency = 'display_unit_frequency',
  /** column name */
  display_unit_volume = 'display_unit_volume',
  /** column name */
  frequency = 'frequency',
  /** column name */
  id = 'id',
  /** column name */
  price = 'price',
  /** column name */
  volume = 'volume',
}

/** unique or primary key constraints on table "cost_version" */
export enum cost_version_constraint {
  /** unique or primary key constraint on columns "id" */
  cost_version_pkey = 'cost_version_pkey',
  /** unique or primary key constraint on columns "version" */
  cost_version_version_key = 'cost_version_version_key',
  /** unique or primary key constraint on columns  */
  only_one_rolled_out_cost_version = 'only_one_rolled_out_cost_version',
}

/** select columns of table "cost_version" */
export enum cost_version_select_column {
  /** column name */
  created_at = 'created_at',
  /** column name */
  id = 'id',
  /** column name */
  status = 'status',
  /** column name */
  updated_at = 'updated_at',
  /** column name */
  valid_from = 'valid_from',
  /** column name */
  valid_to = 'valid_to',
  /** column name */
  version = 'version',
}

/** unique or primary key constraints on table "cost_version_status" */
export enum cost_version_status_constraint {
  /** unique or primary key constraint on columns "id" */
  cost_version_status_pkey = 'cost_version_status_pkey',
}

export enum cost_version_status_enum {
  CREATED = 'CREATED',
  RELEASED = 'RELEASED',
  ROLLED_OUT = 'ROLLED_OUT',
}

/** select columns of table "cost_version_status" */
export enum cost_version_status_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "cost_version_status" */
export enum cost_version_status_update_column {
  /** column name */
  id = 'id',
}

/** update columns of table "cost_version" */
export enum cost_version_update_column {
  /** column name */
  created_at = 'created_at',
  /** column name */
  id = 'id',
  /** column name */
  status = 'status',
  /** column name */
  updated_at = 'updated_at',
  /** column name */
  valid_from = 'valid_from',
  /** column name */
  valid_to = 'valid_to',
  /** column name */
  version = 'version',
}

/** unique or primary key constraints on table "country" */
export enum country_constraint {
  /** unique or primary key constraint on columns "id" */
  country_pkey = 'country_pkey',
}

export enum country_enum {
  AT = 'AT',
  BE = 'BE',
  CZ = 'CZ',
  DE = 'DE',
  FR = 'FR',
  PL = 'PL',
  UK = 'UK',
}

/** select columns of table "country" */
export enum country_select_column {
  /** column name */
  id = 'id',
}

/** unique or primary key constraints on table "country_state" */
export enum country_state_constraint {
  /** unique or primary key constraint on columns "id" */
  country_state_pkey = 'country_state_pkey',
}

export enum country_state_enum {
  AT_BURGENLAND = 'AT_BURGENLAND',
  AT_CARINTHIA = 'AT_CARINTHIA',
  AT_LOWER_AUSTRIA = 'AT_LOWER_AUSTRIA',
  AT_SALZBURG = 'AT_SALZBURG',
  AT_STYRIA = 'AT_STYRIA',
  AT_TYROL = 'AT_TYROL',
  AT_UPPER_AUSTRIA = 'AT_UPPER_AUSTRIA',
  AT_VIENNA = 'AT_VIENNA',
  AT_VORARLBERG = 'AT_VORARLBERG',
  BE_BRUSSELS = 'BE_BRUSSELS',
  BE_FLANDERS = 'BE_FLANDERS',
  BE_WALLONIA = 'BE_WALLONIA',
  CZ_JIHOCESKY = 'CZ_JIHOCESKY',
  CZ_JIHOMORAVSKY = 'CZ_JIHOMORAVSKY',
  CZ_KARLOVARSKY = 'CZ_KARLOVARSKY',
  CZ_KRALOVEHRADECKY = 'CZ_KRALOVEHRADECKY',
  CZ_LIBERECKY = 'CZ_LIBERECKY',
  CZ_MORAVSKOSLEZSKY = 'CZ_MORAVSKOSLEZSKY',
  CZ_OLOMOUCKY = 'CZ_OLOMOUCKY',
  CZ_PARDUBICKY = 'CZ_PARDUBICKY',
  CZ_PLZENSKY = 'CZ_PLZENSKY',
  CZ_PRAHA = 'CZ_PRAHA',
  CZ_STREDOCESKY = 'CZ_STREDOCESKY',
  CZ_USTECKY = 'CZ_USTECKY',
  CZ_VYSOCINA = 'CZ_VYSOCINA',
  CZ_ZLINSKY = 'CZ_ZLINSKY',
  DE_BADEN_WUERTTEMBERG = 'DE_BADEN_WUERTTEMBERG',
  DE_BAYERN = 'DE_BAYERN',
  DE_BERLIN = 'DE_BERLIN',
  DE_BRANDENBURG = 'DE_BRANDENBURG',
  DE_BREMEN = 'DE_BREMEN',
  DE_HAMBURG = 'DE_HAMBURG',
  DE_HESSEN = 'DE_HESSEN',
  DE_MECKLENBURG_VORPOMMERN = 'DE_MECKLENBURG_VORPOMMERN',
  DE_NIEDERSACHSEN = 'DE_NIEDERSACHSEN',
  DE_NORDRHEIN_WESTFALEN = 'DE_NORDRHEIN_WESTFALEN',
  DE_RHEINLAND_PFALZ = 'DE_RHEINLAND_PFALZ',
  DE_SAARLAND = 'DE_SAARLAND',
  DE_SACHSEN = 'DE_SACHSEN',
  DE_SACHSEN_ANHALT = 'DE_SACHSEN_ANHALT',
  DE_SCHLESWIG_HOLSTEIN = 'DE_SCHLESWIG_HOLSTEIN',
  DE_THUERINGEN = 'DE_THUERINGEN',
  FR_AUVERGNE_RHONE_ALPES = 'FR_AUVERGNE_RHONE_ALPES',
  FR_BOURGOGNE_FRANCHE_COMTE = 'FR_BOURGOGNE_FRANCHE_COMTE',
  FR_BRETAGNE = 'FR_BRETAGNE',
  FR_CENTRE_VAL_DE_LOIRE = 'FR_CENTRE_VAL_DE_LOIRE',
  FR_CORSE = 'FR_CORSE',
  FR_GRAND_EST = 'FR_GRAND_EST',
  FR_GUADELOUPE = 'FR_GUADELOUPE',
  FR_GUYANE = 'FR_GUYANE',
  FR_HAUTS_DE_FRANCE = 'FR_HAUTS_DE_FRANCE',
  FR_ILE_DE_FRANCE = 'FR_ILE_DE_FRANCE',
  FR_LA_REUNION = 'FR_LA_REUNION',
  FR_MARTINIQUE = 'FR_MARTINIQUE',
  FR_MAYOTTE = 'FR_MAYOTTE',
  FR_NORMANDIE = 'FR_NORMANDIE',
  FR_NOUVELLE_AQUITAINE = 'FR_NOUVELLE_AQUITAINE',
  FR_OCCITANIE = 'FR_OCCITANIE',
  FR_PAYS_DE_LA_LOIRE = 'FR_PAYS_DE_LA_LOIRE',
  FR_PROVENCE_ALPES_COTE_D_AZUR = 'FR_PROVENCE_ALPES_COTE_D_AZUR',
  PL_GREATER_POLAND = 'PL_GREATER_POLAND',
  PL_HOLY_CROSS = 'PL_HOLY_CROSS',
  PL_KUYAVIAN_POMERANIAN = 'PL_KUYAVIAN_POMERANIAN',
  PL_LESSER_POLAND = 'PL_LESSER_POLAND',
  PL_LODZ = 'PL_LODZ',
  PL_LOWER_SILESIAN = 'PL_LOWER_SILESIAN',
  PL_LUBLIN = 'PL_LUBLIN',
  PL_LUBUSZ = 'PL_LUBUSZ',
  PL_MASOVIAN = 'PL_MASOVIAN',
  PL_OPOLE = 'PL_OPOLE',
  PL_PODLASKIE = 'PL_PODLASKIE',
  PL_POMERANIAN = 'PL_POMERANIAN',
  PL_SILESIAN = 'PL_SILESIAN',
  PL_SUBCARPATHIAN = 'PL_SUBCARPATHIAN',
  PL_WARMIAN_MASURIAN = 'PL_WARMIAN_MASURIAN',
  PL_WEST_POMERANIAN = 'PL_WEST_POMERANIAN',
  UK_EAST_MIDLANDS = 'UK_EAST_MIDLANDS',
  UK_EAST_OF_ENGLAND = 'UK_EAST_OF_ENGLAND',
  UK_LONDON = 'UK_LONDON',
  UK_NORTHERN_IRELAND = 'UK_NORTHERN_IRELAND',
  UK_NORTH_EAST = 'UK_NORTH_EAST',
  UK_NORTH_WEST = 'UK_NORTH_WEST',
  UK_SCOTLAND = 'UK_SCOTLAND',
  UK_SOUTH_EAST = 'UK_SOUTH_EAST',
  UK_SOUTH_WEST = 'UK_SOUTH_WEST',
  UK_WALES = 'UK_WALES',
  UK_WEST_MIDLANDS = 'UK_WEST_MIDLANDS',
  UK_YORKSHIRE_AND_THE_HUMBER = 'UK_YORKSHIRE_AND_THE_HUMBER',
  UNKNOWN = 'UNKNOWN',
}

/** select columns of table "country_state" */
export enum country_state_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "country_state" */
export enum country_state_update_column {
  /** column name */
  id = 'id',
}

/** update columns of table "country" */
export enum country_update_column {
  /** column name */
  id = 'id',
}

/** unique or primary key constraints on table "crrem_config" */
export enum crrem_config_constraint {
  /** unique or primary key constraint on columns "id", "org_id" */
  crrem_config_org_id_id_key = 'crrem_config_org_id_id_key',
  /** unique or primary key constraint on columns "id" */
  crrem_config_pkey = 'crrem_config_pkey',
}

/** select columns of table "crrem_config" */
export enum crrem_config_select_column {
  /** column name */
  created_at = 'created_at',
  /** column name */
  district_heating_chp_fossil_coal_factor = 'district_heating_chp_fossil_coal_factor',
  /** column name */
  district_heating_chp_fossil_gas_factor = 'district_heating_chp_fossil_gas_factor',
  /** column name */
  district_heating_chp_renewable_factor = 'district_heating_chp_renewable_factor',
  /** column name */
  district_heating_plants_fossil_coal_factor = 'district_heating_plants_fossil_coal_factor',
  /** column name */
  district_heating_plants_fossil_gas_factor = 'district_heating_plants_fossil_gas_factor',
  /** column name */
  district_heating_plants_renewable_factor = 'district_heating_plants_renewable_factor',
  /** column name */
  electricity_2020_factor = 'electricity_2020_factor',
  /** column name */
  electricity_annual_rate_of_change = 'electricity_annual_rate_of_change',
  /** column name */
  electricity_factor_table = 'electricity_factor_table',
  /** column name */
  green_electricity_factor = 'green_electricity_factor',
  /** column name */
  id = 'id',
  /** column name */
  org_id = 'org_id',
  /** column name */
  title = 'title',
  /** column name */
  updated_at = 'updated_at',
}

/** update columns of table "crrem_config" */
export enum crrem_config_update_column {
  /** column name */
  created_at = 'created_at',
  /** column name */
  district_heating_chp_fossil_coal_factor = 'district_heating_chp_fossil_coal_factor',
  /** column name */
  district_heating_chp_fossil_gas_factor = 'district_heating_chp_fossil_gas_factor',
  /** column name */
  district_heating_chp_renewable_factor = 'district_heating_chp_renewable_factor',
  /** column name */
  district_heating_plants_fossil_coal_factor = 'district_heating_plants_fossil_coal_factor',
  /** column name */
  district_heating_plants_fossil_gas_factor = 'district_heating_plants_fossil_gas_factor',
  /** column name */
  district_heating_plants_renewable_factor = 'district_heating_plants_renewable_factor',
  /** column name */
  electricity_2020_factor = 'electricity_2020_factor',
  /** column name */
  electricity_annual_rate_of_change = 'electricity_annual_rate_of_change',
  /** column name */
  electricity_factor_table = 'electricity_factor_table',
  /** column name */
  green_electricity_factor = 'green_electricity_factor',
  /** column name */
  id = 'id',
  /** column name */
  org_id = 'org_id',
  /** column name */
  title = 'title',
  /** column name */
  updated_at = 'updated_at',
}

/** unique or primary key constraints on table "crrem_consumption" */
export enum crrem_consumption_constraint {
  /** unique or primary key constraint on columns "building_id", "year", "reporting_year" */
  crrem_consumption_pkey = 'crrem_consumption_pkey',
}

/** select columns of table "crrem_consumption" */
export enum crrem_consumption_select_column {
  /** column name */
  building_id = 'building_id',
  /** column name */
  co2_intensity_total = 'co2_intensity_total',
  /** column name */
  energy_consumption_total = 'energy_consumption_total',
  /** column name */
  excess_emission_cost_total = 'excess_emission_cost_total',
  /** column name */
  excess_emissions_total = 'excess_emissions_total',
  /** column name */
  landlord_scope_1_co2_emissions_total = 'landlord_scope_1_co2_emissions_total',
  /** column name */
  landlord_scope_2_co2_emissions_total = 'landlord_scope_2_co2_emissions_total',
  /** column name */
  landlord_scope_3_co2_emissions_total = 'landlord_scope_3_co2_emissions_total',
  /** column name */
  reporting_year = 'reporting_year',
  /** column name */
  scope1_emissions_total = 'scope1_emissions_total',
  /** column name */
  scope2_emissions_total = 'scope2_emissions_total',
  /** column name */
  tenant_scope_1_co2_emissions_total = 'tenant_scope_1_co2_emissions_total',
  /** column name */
  tenant_scope_2_co2_emissions_total = 'tenant_scope_2_co2_emissions_total',
  /** column name */
  tenant_scope_3_co2_emissions_total = 'tenant_scope_3_co2_emissions_total',
  /** column name */
  year = 'year',
}

/** update columns of table "crrem_consumption" */
export enum crrem_consumption_update_column {
  /** column name */
  building_id = 'building_id',
  /** column name */
  co2_intensity_total = 'co2_intensity_total',
  /** column name */
  energy_consumption_total = 'energy_consumption_total',
  /** column name */
  excess_emission_cost_total = 'excess_emission_cost_total',
  /** column name */
  excess_emissions_total = 'excess_emissions_total',
  /** column name */
  landlord_scope_1_co2_emissions_total = 'landlord_scope_1_co2_emissions_total',
  /** column name */
  landlord_scope_2_co2_emissions_total = 'landlord_scope_2_co2_emissions_total',
  /** column name */
  landlord_scope_3_co2_emissions_total = 'landlord_scope_3_co2_emissions_total',
  /** column name */
  reporting_year = 'reporting_year',
  /** column name */
  scope1_emissions_total = 'scope1_emissions_total',
  /** column name */
  scope2_emissions_total = 'scope2_emissions_total',
  /** column name */
  tenant_scope_1_co2_emissions_total = 'tenant_scope_1_co2_emissions_total',
  /** column name */
  tenant_scope_2_co2_emissions_total = 'tenant_scope_2_co2_emissions_total',
  /** column name */
  tenant_scope_3_co2_emissions_total = 'tenant_scope_3_co2_emissions_total',
  /** column name */
  year = 'year',
}

/** Selected CRREM sub-chart in the Analysis view. */
export enum crrem_path_graph_type_enum {
  CO2_GRAPH = 'CO2_GRAPH',
  ENERGY_GRAPH = 'ENERGY_GRAPH',
  EXCESS_EMISSION_COST = 'EXCESS_EMISSION_COST',
  STRANDING_YEAR = 'STRANDING_YEAR',
}

/** unique or primary key constraints on table "crrem_version_type" */
export enum crrem_version_type_constraint {
  /** unique or primary key constraint on columns "id" */
  crrem_version_type_pkey = 'crrem_version_type_pkey',
}

export enum crrem_version_type_enum {
  V2_05 = 'V2_05',
  V2_07 = 'V2_07',
}

/** select columns of table "crrem_version_type" */
export enum crrem_version_type_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "crrem_version_type" */
export enum crrem_version_type_update_column {
  /** column name */
  id = 'id',
}

/** ordering argument of a cursor */
export enum cursor_ordering {
  /** ascending ordering of the cursor */
  ASC = 'ASC',
  /** descending ordering of the cursor */
  DESC = 'DESC',
}

/** unique or primary key constraints on table "custom_action_type" */
export enum custom_action_type_constraint {
  /** unique or primary key constraint on columns "id" */
  custom_action_type_pkey = 'custom_action_type_pkey',
}

export enum custom_action_type_enum {
  ENERGY_SOURCE = 'ENERGY_SOURCE',
  SYSTEM = 'SYSTEM',
}

/** select columns of table "custom_action_type" */
export enum custom_action_type_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "custom_action_type" */
export enum custom_action_type_update_column {
  /** column name */
  id = 'id',
}

/** unique or primary key constraints on table "custom_field" */
export enum custom_field_constraint {
  /** unique or primary key constraint on columns "entity_type", "key", "org_id" */
  custom_field_org_id_key_entity_type_key = 'custom_field_org_id_key_entity_type_key',
  /** unique or primary key constraint on columns "id" */
  custom_field_pkey = 'custom_field_pkey',
}

/** unique or primary key constraints on table "custom_field_entity" */
export enum custom_field_entity_constraint {
  /** unique or primary key constraint on columns "id" */
  custom_field_entity_pkey = 'custom_field_entity_pkey',
}

export enum custom_field_entity_enum {
  BUILDING = 'BUILDING',
  ECONOMIC_UNIT = 'ECONOMIC_UNIT',
}

/** select columns of table "custom_field_entity" */
export enum custom_field_entity_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "custom_field_entity" */
export enum custom_field_entity_update_column {
  /** column name */
  id = 'id',
}

/** select columns of table "custom_field" */
export enum custom_field_select_column {
  /** column name */
  created_at = 'created_at',
  /** column name */
  entity_type = 'entity_type',
  /** column name */
  id = 'id',
  /** column name */
  key = 'key',
  /** column name */
  name = 'name',
  /** column name */
  org_id = 'org_id',
  /** column name */
  show_in_analysis = 'show_in_analysis',
  /** column name */
  suffix = 'suffix',
  /** column name */
  type_id = 'type_id',
  /** column name */
  updated_at = 'updated_at',
}

/** unique or primary key constraints on table "custom_field_type" */
export enum custom_field_type_constraint {
  /** unique or primary key constraint on columns "id" */
  custom_field_type_pkey = 'custom_field_type_pkey',
}

export enum custom_field_type_enum {
  BOOLEAN = 'BOOLEAN',
  NUMBER = 'NUMBER',
  STRING = 'STRING',
}

/** select columns of table "custom_field_type" */
export enum custom_field_type_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "custom_field_type" */
export enum custom_field_type_update_column {
  /** column name */
  id = 'id',
}

/** update columns of table "custom_field" */
export enum custom_field_update_column {
  /** column name */
  created_at = 'created_at',
  /** column name */
  entity_type = 'entity_type',
  /** column name */
  id = 'id',
  /** column name */
  key = 'key',
  /** column name */
  name = 'name',
  /** column name */
  org_id = 'org_id',
  /** column name */
  show_in_analysis = 'show_in_analysis',
  /** column name */
  suffix = 'suffix',
  /** column name */
  type_id = 'type_id',
  /** column name */
  updated_at = 'updated_at',
}

/** unique or primary key constraints on table "custom_target" */
export enum custom_target_constraint {
  /** unique or primary key constraint on columns "id" */
  custom_target_pkey = 'custom_target_pkey',
}

/** select columns of table "custom_target" */
export enum custom_target_select_column {
  /** column name */
  active = 'active',
  /** column name */
  id = 'id',
  /** column name */
  name = 'name',
  /** column name */
  org_id = 'org_id',
}

/** update columns of table "custom_target" */
export enum custom_target_update_column {
  /** column name */
  active = 'active',
  /** column name */
  id = 'id',
  /** column name */
  name = 'name',
  /** column name */
  org_id = 'org_id',
}

/** unique or primary key constraints on table "data_source_type" */
export enum data_source_type_constraint {
  /** unique or primary key constraint on columns "id" */
  data_source_type_pkey = 'data_source_type_pkey',
}

export enum data_source_type_enum {
  APPROXIMATED = 'APPROXIMATED',
  GEOSPATIAL = 'GEOSPATIAL',
  GOOGLE_MESH = 'GOOGLE_MESH',
  GOOGLE_SOLAR_API = 'GOOGLE_SOLAR_API',
  GOOGLE_STREET_VIEW = 'GOOGLE_STREET_VIEW',
  MANUAL = 'MANUAL',
  TABULA = 'TABULA',
}

/** select columns of table "data_source_type" */
export enum data_source_type_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "data_source_type" */
export enum data_source_type_update_column {
  /** column name */
  id = 'id',
}

/** unique or primary key constraints on table "deleted_buildings" */
export enum deleted_buildings_constraint {
  /** unique or primary key constraint on columns "id" */
  deleted_buildings_pkey = 'deleted_buildings_pkey',
}

/** select columns of table "deleted_buildings" */
export enum deleted_buildings_select_column {
  /** column name */
  created_at = 'created_at',
  /** column name */
  data = 'data',
  /** column name */
  id = 'id',
  /** column name */
  org_id = 'org_id',
}

/** update columns of table "deleted_buildings" */
export enum deleted_buildings_update_column {
  /** column name */
  created_at = 'created_at',
  /** column name */
  data = 'data',
  /** column name */
  id = 'id',
  /** column name */
  org_id = 'org_id',
}

/** unique or primary key constraints on table "draft_status" */
export enum draft_status_constraint {
  /** unique or primary key constraint on columns "id" */
  draft_status_pkey = 'draft_status_pkey',
}

export enum draft_status_enum {
  COMPLETE = 'COMPLETE',
  INCOMPLETE = 'INCOMPLETE',
  OCR_FAILED = 'OCR_FAILED',
  OCR_IN_PROGRESS = 'OCR_IN_PROGRESS',
  OCR_REVIEW = 'OCR_REVIEW',
  UPLOADING = 'UPLOADING',
}

/** select columns of table "draft_status" */
export enum draft_status_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "draft_status" */
export enum draft_status_update_column {
  /** column name */
  id = 'id',
}

/** unique or primary key constraints on table "economic_unit" */
export enum economic_unit_constraint {
  /** unique or primary key constraint on columns "id" */
  economic_unit_pkey = 'economic_unit_pkey',
}

/** select columns of table "economic_unit" */
export enum economic_unit_select_column {
  /** column name */
  created_at = 'created_at',
  /** column name */
  custom_data = 'custom_data',
  /** column name */
  customer_economic_unit_id = 'customer_economic_unit_id',
  /** column name */
  description = 'description',
  /** column name */
  id = 'id',
  /** column name */
  name = 'name',
  /** column name */
  portfolio_id = 'portfolio_id',
  /** column name */
  responsible_user_id = 'responsible_user_id',
  /** column name */
  updated_at = 'updated_at',
}

/** update columns of table "economic_unit" */
export enum economic_unit_update_column {
  /** column name */
  created_at = 'created_at',
  /** column name */
  custom_data = 'custom_data',
  /** column name */
  customer_economic_unit_id = 'customer_economic_unit_id',
  /** column name */
  description = 'description',
  /** column name */
  id = 'id',
  /** column name */
  name = 'name',
  /** column name */
  portfolio_id = 'portfolio_id',
  /** column name */
  responsible_user_id = 'responsible_user_id',
  /** column name */
  updated_at = 'updated_at',
}

/** unique or primary key constraints on table "efficiency_class" */
export enum efficiency_class_constraint {
  /** unique or primary key constraint on columns "id" */
  efficiency_class_pkey = 'efficiency_class_pkey',
}

export enum efficiency_class_enum {
  A = 'A',
  AA = 'AA',
  AAA = 'AAA',
  B = 'B',
  C = 'C',
  D = 'D',
  E = 'E',
  F = 'F',
  G = 'G',
  H = 'H',
  NOT_APPLICABLE = 'NOT_APPLICABLE',
  UNKNOWN = 'UNKNOWN',
}

/** select columns of table "efficiency_class" */
export enum efficiency_class_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "efficiency_class" */
export enum efficiency_class_update_column {
  /** column name */
  id = 'id',
}

/** unique or primary key constraints on table "emission_certificate" */
export enum emission_certificate_constraint {
  /** unique or primary key constraint on columns "id" */
  emission_certificate_pkey = 'emission_certificate_pkey',
}

/** unique or primary key constraints on table "emission_certificate_document_type" */
export enum emission_certificate_document_type_constraint {
  /** unique or primary key constraint on columns "id" */
  emission_certificate_document_type_pkey = 'emission_certificate_document_type_pkey',
}

export enum emission_certificate_document_type_enum {
  CERTIFICATE_OF_ORIGIN = 'CERTIFICATE_OF_ORIGIN',
  EMISSION_CERTIFICATE = 'EMISSION_CERTIFICATE',
  OTHER = 'OTHER',
}

/** select columns of table "emission_certificate_document_type" */
export enum emission_certificate_document_type_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "emission_certificate_document_type" */
export enum emission_certificate_document_type_update_column {
  /** column name */
  id = 'id',
}

/** unique or primary key constraints on table "emission_certificate_draft" */
export enum emission_certificate_draft_constraint {
  /** unique or primary key constraint on columns "id" */
  emission_certificate_draft_pkey = 'emission_certificate_draft_pkey',
}

/** select columns of table "emission_certificate_draft" */
export enum emission_certificate_draft_select_column {
  /** column name */
  created_at = 'created_at',
  /** column name */
  emission_certificate_document_type_id = 'emission_certificate_document_type_id',
  /** column name */
  emission_certificate_energy_source_type_id = 'emission_certificate_energy_source_type_id',
  /** column name */
  emission_factor = 'emission_factor',
  /** column name */
  emission_factor_active_value_type = 'emission_factor_active_value_type',
  /** column name */
  file_id = 'file_id',
  /** column name */
  id = 'id',
  /** column name */
  issuer = 'issuer',
  /** column name */
  org_id = 'org_id',
  /** column name */
  primary_energy_factor = 'primary_energy_factor',
  /** column name */
  primary_energy_factor_active_value_type = 'primary_energy_factor_active_value_type',
  /** column name */
  updated_at = 'updated_at',
  /** column name */
  valid_from = 'valid_from',
  /** column name */
  valid_until = 'valid_until',
}

/** select "emission_certificate_draft_aggregate_bool_exp_avg_arguments_columns" columns of table "emission_certificate_draft" */
export enum emission_certificate_draft_select_column_emission_certificate_draft_aggregate_bool_exp_avg_arguments_columns {
  /** column name */
  emission_factor = 'emission_factor',
  /** column name */
  primary_energy_factor = 'primary_energy_factor',
}

/** select "emission_certificate_draft_aggregate_bool_exp_corr_arguments_columns" columns of table "emission_certificate_draft" */
export enum emission_certificate_draft_select_column_emission_certificate_draft_aggregate_bool_exp_corr_arguments_columns {
  /** column name */
  emission_factor = 'emission_factor',
  /** column name */
  primary_energy_factor = 'primary_energy_factor',
}

/** select "emission_certificate_draft_aggregate_bool_exp_covar_samp_arguments_columns" columns of table "emission_certificate_draft" */
export enum emission_certificate_draft_select_column_emission_certificate_draft_aggregate_bool_exp_covar_samp_arguments_columns {
  /** column name */
  emission_factor = 'emission_factor',
  /** column name */
  primary_energy_factor = 'primary_energy_factor',
}

/** select "emission_certificate_draft_aggregate_bool_exp_max_arguments_columns" columns of table "emission_certificate_draft" */
export enum emission_certificate_draft_select_column_emission_certificate_draft_aggregate_bool_exp_max_arguments_columns {
  /** column name */
  emission_factor = 'emission_factor',
  /** column name */
  primary_energy_factor = 'primary_energy_factor',
}

/** select "emission_certificate_draft_aggregate_bool_exp_min_arguments_columns" columns of table "emission_certificate_draft" */
export enum emission_certificate_draft_select_column_emission_certificate_draft_aggregate_bool_exp_min_arguments_columns {
  /** column name */
  emission_factor = 'emission_factor',
  /** column name */
  primary_energy_factor = 'primary_energy_factor',
}

/** select "emission_certificate_draft_aggregate_bool_exp_stddev_samp_arguments_columns" columns of table "emission_certificate_draft" */
export enum emission_certificate_draft_select_column_emission_certificate_draft_aggregate_bool_exp_stddev_samp_arguments_columns {
  /** column name */
  emission_factor = 'emission_factor',
  /** column name */
  primary_energy_factor = 'primary_energy_factor',
}

/** select "emission_certificate_draft_aggregate_bool_exp_sum_arguments_columns" columns of table "emission_certificate_draft" */
export enum emission_certificate_draft_select_column_emission_certificate_draft_aggregate_bool_exp_sum_arguments_columns {
  /** column name */
  emission_factor = 'emission_factor',
  /** column name */
  primary_energy_factor = 'primary_energy_factor',
}

/** select "emission_certificate_draft_aggregate_bool_exp_var_samp_arguments_columns" columns of table "emission_certificate_draft" */
export enum emission_certificate_draft_select_column_emission_certificate_draft_aggregate_bool_exp_var_samp_arguments_columns {
  /** column name */
  emission_factor = 'emission_factor',
  /** column name */
  primary_energy_factor = 'primary_energy_factor',
}

/** update columns of table "emission_certificate_draft" */
export enum emission_certificate_draft_update_column {
  /** column name */
  created_at = 'created_at',
  /** column name */
  emission_certificate_document_type_id = 'emission_certificate_document_type_id',
  /** column name */
  emission_certificate_energy_source_type_id = 'emission_certificate_energy_source_type_id',
  /** column name */
  emission_factor = 'emission_factor',
  /** column name */
  emission_factor_active_value_type = 'emission_factor_active_value_type',
  /** column name */
  file_id = 'file_id',
  /** column name */
  id = 'id',
  /** column name */
  issuer = 'issuer',
  /** column name */
  org_id = 'org_id',
  /** column name */
  primary_energy_factor = 'primary_energy_factor',
  /** column name */
  primary_energy_factor_active_value_type = 'primary_energy_factor_active_value_type',
  /** column name */
  updated_at = 'updated_at',
  /** column name */
  valid_from = 'valid_from',
  /** column name */
  valid_until = 'valid_until',
}

/** unique or primary key constraints on table "emission_certificate_draft_value_path" */
export enum emission_certificate_draft_value_path_constraint {
  /** unique or primary key constraint on columns "id" */
  emission_certificate_draft_value_path_pkey = 'emission_certificate_draft_value_path_pkey',
}

/** select columns of table "emission_certificate_draft_value_path" */
export enum emission_certificate_draft_value_path_select_column {
  /** column name */
  emission_factor = 'emission_factor',
  /** column name */
  id = 'id',
  /** column name */
  primary_energy_factor = 'primary_energy_factor',
}

/** update columns of table "emission_certificate_draft_value_path" */
export enum emission_certificate_draft_value_path_update_column {
  /** column name */
  emission_factor = 'emission_factor',
  /** column name */
  id = 'id',
  /** column name */
  primary_energy_factor = 'primary_energy_factor',
}

/** unique or primary key constraints on table "emission_certificate_energy_source_type" */
export enum emission_certificate_energy_source_type_constraint {
  /** unique or primary key constraint on columns "id" */
  emission_certificate_energy_source_type_pkey = 'emission_certificate_energy_source_type_pkey',
}

export enum emission_certificate_energy_source_type_enum {
  DISTRICT_HEATING = 'DISTRICT_HEATING',
  ELECTRICITY_GREEN = 'ELECTRICITY_GREEN',
  ELECTRICITY_MIX = 'ELECTRICITY_MIX',
}

/** select columns of table "emission_certificate_energy_source_type" */
export enum emission_certificate_energy_source_type_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "emission_certificate_energy_source_type" */
export enum emission_certificate_energy_source_type_update_column {
  /** column name */
  id = 'id',
}

/** select columns of table "emission_certificate" */
export enum emission_certificate_select_column {
  /** column name */
  created_at = 'created_at',
  /** column name */
  emission_certificate_document_type_id = 'emission_certificate_document_type_id',
  /** column name */
  emission_certificate_energy_source_type_id = 'emission_certificate_energy_source_type_id',
  /** column name */
  emission_factor = 'emission_factor',
  /** column name */
  emission_factor_active_value_type = 'emission_factor_active_value_type',
  /** column name */
  file_id = 'file_id',
  /** column name */
  id = 'id',
  /** column name */
  issuer = 'issuer',
  /** column name */
  org_id = 'org_id',
  /** column name */
  primary_energy_factor = 'primary_energy_factor',
  /** column name */
  primary_energy_factor_active_value_type = 'primary_energy_factor_active_value_type',
  /** column name */
  updated_at = 'updated_at',
  /** column name */
  valid_from = 'valid_from',
  /** column name */
  valid_until = 'valid_until',
}

/** select "emission_certificate_aggregate_bool_exp_avg_arguments_columns" columns of table "emission_certificate" */
export enum emission_certificate_select_column_emission_certificate_aggregate_bool_exp_avg_arguments_columns {
  /** column name */
  emission_factor = 'emission_factor',
  /** column name */
  primary_energy_factor = 'primary_energy_factor',
}

/** select "emission_certificate_aggregate_bool_exp_corr_arguments_columns" columns of table "emission_certificate" */
export enum emission_certificate_select_column_emission_certificate_aggregate_bool_exp_corr_arguments_columns {
  /** column name */
  emission_factor = 'emission_factor',
  /** column name */
  primary_energy_factor = 'primary_energy_factor',
}

/** select "emission_certificate_aggregate_bool_exp_covar_samp_arguments_columns" columns of table "emission_certificate" */
export enum emission_certificate_select_column_emission_certificate_aggregate_bool_exp_covar_samp_arguments_columns {
  /** column name */
  emission_factor = 'emission_factor',
  /** column name */
  primary_energy_factor = 'primary_energy_factor',
}

/** select "emission_certificate_aggregate_bool_exp_max_arguments_columns" columns of table "emission_certificate" */
export enum emission_certificate_select_column_emission_certificate_aggregate_bool_exp_max_arguments_columns {
  /** column name */
  emission_factor = 'emission_factor',
  /** column name */
  primary_energy_factor = 'primary_energy_factor',
}

/** select "emission_certificate_aggregate_bool_exp_min_arguments_columns" columns of table "emission_certificate" */
export enum emission_certificate_select_column_emission_certificate_aggregate_bool_exp_min_arguments_columns {
  /** column name */
  emission_factor = 'emission_factor',
  /** column name */
  primary_energy_factor = 'primary_energy_factor',
}

/** select "emission_certificate_aggregate_bool_exp_stddev_samp_arguments_columns" columns of table "emission_certificate" */
export enum emission_certificate_select_column_emission_certificate_aggregate_bool_exp_stddev_samp_arguments_columns {
  /** column name */
  emission_factor = 'emission_factor',
  /** column name */
  primary_energy_factor = 'primary_energy_factor',
}

/** select "emission_certificate_aggregate_bool_exp_sum_arguments_columns" columns of table "emission_certificate" */
export enum emission_certificate_select_column_emission_certificate_aggregate_bool_exp_sum_arguments_columns {
  /** column name */
  emission_factor = 'emission_factor',
  /** column name */
  primary_energy_factor = 'primary_energy_factor',
}

/** select "emission_certificate_aggregate_bool_exp_var_samp_arguments_columns" columns of table "emission_certificate" */
export enum emission_certificate_select_column_emission_certificate_aggregate_bool_exp_var_samp_arguments_columns {
  /** column name */
  emission_factor = 'emission_factor',
  /** column name */
  primary_energy_factor = 'primary_energy_factor',
}

/** update columns of table "emission_certificate" */
export enum emission_certificate_update_column {
  /** column name */
  created_at = 'created_at',
  /** column name */
  emission_certificate_document_type_id = 'emission_certificate_document_type_id',
  /** column name */
  emission_certificate_energy_source_type_id = 'emission_certificate_energy_source_type_id',
  /** column name */
  emission_factor = 'emission_factor',
  /** column name */
  emission_factor_active_value_type = 'emission_factor_active_value_type',
  /** column name */
  file_id = 'file_id',
  /** column name */
  id = 'id',
  /** column name */
  issuer = 'issuer',
  /** column name */
  org_id = 'org_id',
  /** column name */
  primary_energy_factor = 'primary_energy_factor',
  /** column name */
  primary_energy_factor_active_value_type = 'primary_energy_factor_active_value_type',
  /** column name */
  updated_at = 'updated_at',
  /** column name */
  valid_from = 'valid_from',
  /** column name */
  valid_until = 'valid_until',
}

/** unique or primary key constraints on table "emission_certificate_value_path" */
export enum emission_certificate_value_path_constraint {
  /** unique or primary key constraint on columns "id" */
  emission_certificate_value_path_pkey = 'emission_certificate_value_path_pkey',
}

/** select columns of table "emission_certificate_value_path" */
export enum emission_certificate_value_path_select_column {
  /** column name */
  emission_factor = 'emission_factor',
  /** column name */
  id = 'id',
  /** column name */
  primary_energy_factor = 'primary_energy_factor',
}

/** update columns of table "emission_certificate_value_path" */
export enum emission_certificate_value_path_update_column {
  /** column name */
  emission_factor = 'emission_factor',
  /** column name */
  id = 'id',
  /** column name */
  primary_energy_factor = 'primary_energy_factor',
}

/** The source of the emission factor, e.g., GEMIS, UBA, etc. */
export enum emission_factor_source_enum {
  ARBEITSHILFE_85 = 'ARBEITSHILFE_85',
  GEMIS = 'GEMIS',
  NATIONAL_ENERGY_LAW = 'NATIONAL_ENERGY_LAW',
  UBA = 'UBA',
}

/** unique or primary key constraints on table "emission_factor_type" */
export enum emission_factor_type_constraint {
  /** unique or primary key constraint on columns "id" */
  emission_factor_type_pkey = 'emission_factor_type_pkey',
}

export enum emission_factor_type_enum {
  CRREM = 'CRREM',
  CRREM_GEG = 'CRREM_GEG',
  NATIONAL_ENERGY_LAW = 'NATIONAL_ENERGY_LAW',
}

/** select columns of table "emission_factor_type" */
export enum emission_factor_type_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "emission_factor_type" */
export enum emission_factor_type_update_column {
  /** column name */
  id = 'id',
}

/** unique or primary key constraints on table "energy_certificate" */
export enum energy_certificate_constraint {
  /** unique or primary key constraint on columns "id" */
  energy_certificate_residential_demand_pkey = 'energy_certificate_residential_demand_pkey',
}

/** unique or primary key constraints on table "energy_certificate_consumer" */
export enum energy_certificate_consumer_constraint {
  /** unique or primary key constraint on columns "id" */
  energy_certificate_consumer_pkey = 'energy_certificate_consumer_pkey',
}

/** select columns of table "energy_certificate_consumer" */
export enum energy_certificate_consumer_select_column {
  /** column name */
  energy_certificate_draft_id = 'energy_certificate_draft_id',
  /** column name */
  energy_certificate_id = 'energy_certificate_id',
  /** column name */
  energy_final = 'energy_final',
  /** column name */
  energy_source_type_id = 'energy_source_type_id',
  /** column name */
  energy_system_type_id = 'energy_system_type_id',
  /** column name */
  id = 'id',
}

/** update columns of table "energy_certificate_consumer" */
export enum energy_certificate_consumer_update_column {
  /** column name */
  energy_certificate_draft_id = 'energy_certificate_draft_id',
  /** column name */
  energy_certificate_id = 'energy_certificate_id',
  /** column name */
  energy_final = 'energy_final',
  /** column name */
  energy_source_type_id = 'energy_source_type_id',
  /** column name */
  energy_system_type_id = 'energy_system_type_id',
  /** column name */
  id = 'id',
}

/** unique or primary key constraints on table "energy_certificate_draft" */
export enum energy_certificate_draft_constraint {
  /** unique or primary key constraint on columns "id" */
  energy_certificate_draft_pkey = 'energy_certificate_draft_pkey',
}

/** select columns of table "energy_certificate_draft" */
export enum energy_certificate_draft_select_column {
  /** column name */
  area_gross = 'area_gross',
  /** column name */
  area_usable = 'area_usable',
  /** column name */
  building_citydb_gml_ids = 'building_citydb_gml_ids',
  /** column name */
  building_draft_id = 'building_draft_id',
  /** column name */
  building_state_id = 'building_state_id',
  /** column name */
  city = 'city',
  /** column name */
  class_of_use_id = 'class_of_use_id',
  /** column name */
  co2_emissions = 'co2_emissions',
  /** column name */
  country_id = 'country_id',
  /** column name */
  country_state_id = 'country_state_id',
  /** column name */
  created_at = 'created_at',
  /** column name */
  customer_external_identifier = 'customer_external_identifier',
  /** column name */
  draft_status_id = 'draft_status_id',
  /** column name */
  economic_unit_id = 'economic_unit_id',
  /** column name */
  efficiency_class_id = 'efficiency_class_id',
  /** column name */
  energy_certificate_file_id = 'energy_certificate_file_id',
  /** column name */
  energy_certificate_type_id = 'energy_certificate_type_id',
  /** column name */
  energy_final_consumption = 'energy_final_consumption',
  /** column name */
  energy_final_demand = 'energy_final_demand',
  /** column name */
  energy_primary_consumption = 'energy_primary_consumption',
  /** column name */
  energy_primary_demand = 'energy_primary_demand',
  /** column name */
  energy_rating_score = 'energy_rating_score',
  /** column name */
  enev_year = 'enev_year',
  /** column name */
  expiry_date = 'expiry_date',
  /** column name */
  floors = 'floors',
  /** column name */
  geg_reference_value = 'geg_reference_value',
  /** column name */
  heating_days = 'heating_days',
  /** column name */
  heating_demand_ref_sk = 'heating_demand_ref_sk',
  /** column name */
  heritage_district = 'heritage_district',
  /** column name */
  id = 'id',
  /** column name */
  latitude = 'latitude',
  /** column name */
  leasehold = 'leasehold',
  /** column name */
  longitude = 'longitude',
  /** column name */
  mapbox_building_ids = 'mapbox_building_ids',
  /** column name */
  milieu_protection = 'milieu_protection',
  /** column name */
  monument_protection = 'monument_protection',
  /** column name */
  neighbor_citydb_gml_ids = 'neighbor_citydb_gml_ids',
  /** column name */
  postal_code = 'postal_code',
  /** column name */
  reference_value_class = 'reference_value_class',
  /** column name */
  reference_value_electricity = 'reference_value_electricity',
  /** column name */
  reference_value_heating_hot_water = 'reference_value_heating_hot_water',
  /** column name */
  responsible_user_id = 'responsible_user_id',
  /** column name */
  street = 'street',
  /** column name */
  target_internal_temperature = 'target_internal_temperature',
  /** column name */
  type_of_use_id = 'type_of_use_id',
  /** column name */
  units = 'units',
  /** column name */
  updated_at = 'updated_at',
  /** column name */
  year_constructed = 'year_constructed',
  /** column name */
  year_renovated = 'year_renovated',
}

/** select "energy_certificate_draft_aggregate_bool_exp_bool_and_arguments_columns" columns of table "energy_certificate_draft" */
export enum energy_certificate_draft_select_column_energy_certificate_draft_aggregate_bool_exp_bool_and_arguments_columns {
  /** column name */
  heritage_district = 'heritage_district',
  /** column name */
  leasehold = 'leasehold',
  /** column name */
  milieu_protection = 'milieu_protection',
  /** column name */
  monument_protection = 'monument_protection',
}

/** select "energy_certificate_draft_aggregate_bool_exp_bool_or_arguments_columns" columns of table "energy_certificate_draft" */
export enum energy_certificate_draft_select_column_energy_certificate_draft_aggregate_bool_exp_bool_or_arguments_columns {
  /** column name */
  heritage_district = 'heritage_district',
  /** column name */
  leasehold = 'leasehold',
  /** column name */
  milieu_protection = 'milieu_protection',
  /** column name */
  monument_protection = 'monument_protection',
}

/** update columns of table "energy_certificate_draft" */
export enum energy_certificate_draft_update_column {
  /** column name */
  area_gross = 'area_gross',
  /** column name */
  area_usable = 'area_usable',
  /** column name */
  building_citydb_gml_ids = 'building_citydb_gml_ids',
  /** column name */
  building_draft_id = 'building_draft_id',
  /** column name */
  building_state_id = 'building_state_id',
  /** column name */
  city = 'city',
  /** column name */
  class_of_use_id = 'class_of_use_id',
  /** column name */
  co2_emissions = 'co2_emissions',
  /** column name */
  country_id = 'country_id',
  /** column name */
  country_state_id = 'country_state_id',
  /** column name */
  created_at = 'created_at',
  /** column name */
  customer_external_identifier = 'customer_external_identifier',
  /** column name */
  draft_status_id = 'draft_status_id',
  /** column name */
  economic_unit_id = 'economic_unit_id',
  /** column name */
  efficiency_class_id = 'efficiency_class_id',
  /** column name */
  energy_certificate_file_id = 'energy_certificate_file_id',
  /** column name */
  energy_certificate_type_id = 'energy_certificate_type_id',
  /** column name */
  energy_final_consumption = 'energy_final_consumption',
  /** column name */
  energy_final_demand = 'energy_final_demand',
  /** column name */
  energy_primary_consumption = 'energy_primary_consumption',
  /** column name */
  energy_primary_demand = 'energy_primary_demand',
  /** column name */
  energy_rating_score = 'energy_rating_score',
  /** column name */
  enev_year = 'enev_year',
  /** column name */
  expiry_date = 'expiry_date',
  /** column name */
  floors = 'floors',
  /** column name */
  geg_reference_value = 'geg_reference_value',
  /** column name */
  heating_days = 'heating_days',
  /** column name */
  heating_demand_ref_sk = 'heating_demand_ref_sk',
  /** column name */
  heritage_district = 'heritage_district',
  /** column name */
  id = 'id',
  /** column name */
  latitude = 'latitude',
  /** column name */
  leasehold = 'leasehold',
  /** column name */
  longitude = 'longitude',
  /** column name */
  mapbox_building_ids = 'mapbox_building_ids',
  /** column name */
  milieu_protection = 'milieu_protection',
  /** column name */
  monument_protection = 'monument_protection',
  /** column name */
  neighbor_citydb_gml_ids = 'neighbor_citydb_gml_ids',
  /** column name */
  postal_code = 'postal_code',
  /** column name */
  reference_value_class = 'reference_value_class',
  /** column name */
  reference_value_electricity = 'reference_value_electricity',
  /** column name */
  reference_value_heating_hot_water = 'reference_value_heating_hot_water',
  /** column name */
  responsible_user_id = 'responsible_user_id',
  /** column name */
  street = 'street',
  /** column name */
  target_internal_temperature = 'target_internal_temperature',
  /** column name */
  type_of_use_id = 'type_of_use_id',
  /** column name */
  units = 'units',
  /** column name */
  updated_at = 'updated_at',
  /** column name */
  year_constructed = 'year_constructed',
  /** column name */
  year_renovated = 'year_renovated',
}

/** select columns of table "energy_certificate" */
export enum energy_certificate_select_column {
  /** column name */
  area_gross = 'area_gross',
  /** column name */
  area_usable = 'area_usable',
  /** column name */
  building_draft_id = 'building_draft_id',
  /** column name */
  building_id = 'building_id',
  /** column name */
  city = 'city',
  /** column name */
  co2_emissions = 'co2_emissions',
  /** column name */
  country_id = 'country_id',
  /** column name */
  country_state_id = 'country_state_id',
  /** column name */
  created_at = 'created_at',
  /** column name */
  efficiency_class = 'efficiency_class',
  /** column name */
  energy_certificate_type = 'energy_certificate_type',
  /** column name */
  energy_final_consumption = 'energy_final_consumption',
  /** column name */
  energy_final_demand = 'energy_final_demand',
  /** column name */
  energy_primary_consumption = 'energy_primary_consumption',
  /** column name */
  energy_primary_demand = 'energy_primary_demand',
  /** column name */
  energy_rating_score = 'energy_rating_score',
  /** column name */
  enev_year = 'enev_year',
  /** column name */
  expiry_date = 'expiry_date',
  /** column name */
  file_id = 'file_id',
  /** column name */
  geg_reference_value = 'geg_reference_value',
  /** column name */
  heating_days = 'heating_days',
  /** column name */
  heating_demand_ref_sk = 'heating_demand_ref_sk',
  /** column name */
  id = 'id',
  /** column name */
  org_id = 'org_id',
  /** column name */
  postal_code = 'postal_code',
  /** column name */
  reference_value_class = 'reference_value_class',
  /** column name */
  reference_value_electricity = 'reference_value_electricity',
  /** column name */
  reference_value_heating_hot_water = 'reference_value_heating_hot_water',
  /** column name */
  state = 'state',
  /** column name */
  street = 'street',
  /** column name */
  target_internal_temperature = 'target_internal_temperature',
  /** column name */
  type_of_use_id = 'type_of_use_id',
  /** column name */
  units_commercial = 'units_commercial',
  /** column name */
  units_residential = 'units_residential',
  /** column name */
  updated_at = 'updated_at',
  /** column name */
  year_constructed = 'year_constructed',
  /** column name */
  year_renovated = 'year_renovated',
}

/** unique or primary key constraints on table "energy_certificate_type" */
export enum energy_certificate_type_constraint {
  /** unique or primary key constraint on columns "id" */
  energy_certificate_type_pkey = 'energy_certificate_type_pkey',
}

export enum energy_certificate_type_enum {
  CONSUMPTION = 'CONSUMPTION',
  DEMAND = 'DEMAND',
}

/** select columns of table "energy_certificate_type" */
export enum energy_certificate_type_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "energy_certificate_type" */
export enum energy_certificate_type_update_column {
  /** column name */
  id = 'id',
}

/** update columns of table "energy_certificate" */
export enum energy_certificate_update_column {
  /** column name */
  area_gross = 'area_gross',
  /** column name */
  area_usable = 'area_usable',
  /** column name */
  building_draft_id = 'building_draft_id',
  /** column name */
  building_id = 'building_id',
  /** column name */
  city = 'city',
  /** column name */
  co2_emissions = 'co2_emissions',
  /** column name */
  country_id = 'country_id',
  /** column name */
  country_state_id = 'country_state_id',
  /** column name */
  created_at = 'created_at',
  /** column name */
  efficiency_class = 'efficiency_class',
  /** column name */
  energy_certificate_type = 'energy_certificate_type',
  /** column name */
  energy_final_consumption = 'energy_final_consumption',
  /** column name */
  energy_final_demand = 'energy_final_demand',
  /** column name */
  energy_primary_consumption = 'energy_primary_consumption',
  /** column name */
  energy_primary_demand = 'energy_primary_demand',
  /** column name */
  energy_rating_score = 'energy_rating_score',
  /** column name */
  enev_year = 'enev_year',
  /** column name */
  expiry_date = 'expiry_date',
  /** column name */
  file_id = 'file_id',
  /** column name */
  geg_reference_value = 'geg_reference_value',
  /** column name */
  heating_days = 'heating_days',
  /** column name */
  heating_demand_ref_sk = 'heating_demand_ref_sk',
  /** column name */
  id = 'id',
  /** column name */
  org_id = 'org_id',
  /** column name */
  postal_code = 'postal_code',
  /** column name */
  reference_value_class = 'reference_value_class',
  /** column name */
  reference_value_electricity = 'reference_value_electricity',
  /** column name */
  reference_value_heating_hot_water = 'reference_value_heating_hot_water',
  /** column name */
  state = 'state',
  /** column name */
  street = 'street',
  /** column name */
  target_internal_temperature = 'target_internal_temperature',
  /** column name */
  type_of_use_id = 'type_of_use_id',
  /** column name */
  units_commercial = 'units_commercial',
  /** column name */
  units_residential = 'units_residential',
  /** column name */
  updated_at = 'updated_at',
  /** column name */
  year_constructed = 'year_constructed',
  /** column name */
  year_renovated = 'year_renovated',
}

/** unique or primary key constraints on table "energy_consumer" */
export enum energy_consumer_constraint {
  /** unique or primary key constraint on columns "id" */
  energy_consumer_pkey = 'energy_consumer_pkey',
}

/** select columns of table "energy_consumer" */
export enum energy_consumer_select_column {
  /** column name */
  building_model_id = 'building_model_id',
  /** column name */
  construction_year = 'construction_year',
  /** column name */
  created_at = 'created_at',
  /** column name */
  efficiency = 'efficiency',
  /** column name */
  energy_consumer_technology_type_id = 'energy_consumer_technology_type_id',
  /** column name */
  energy_consumer_type_id = 'energy_consumer_type_id',
  /** column name */
  id = 'id',
  /** column name */
  name = 'name',
  /** column name */
  updated_at = 'updated_at',
}

/** unique or primary key constraints on table "energy_consumer_technology_type" */
export enum energy_consumer_technology_type_constraint {
  /** unique or primary key constraint on columns "id" */
  energy_consumer_technology_type_pkey = 'energy_consumer_technology_type_pkey',
}

export enum energy_consumer_technology_type_enum {
  CENTRAL_AIR_CONDITIONING = 'CENTRAL_AIR_CONDITIONING',
  CENTRAL_ELECTRIC_STORAGE = 'CENTRAL_ELECTRIC_STORAGE',
  CENTRAL_EXHAUST = 'CENTRAL_EXHAUST',
  CENTRAL_SUPPLY_EXHAUST_WITHOUT_HRU = 'CENTRAL_SUPPLY_EXHAUST_WITHOUT_HRU',
  CENTRAL_SUPPLY_EXHAUST_WITH_HRU = 'CENTRAL_SUPPLY_EXHAUST_WITH_HRU',
  COAL_FURNACE = 'COAL_FURNACE',
  CONDENSING_BOILER = 'CONDENSING_BOILER',
  DECENTRAL_SINGLE_ROOM_VENTILATION_UNITS_WITHOUT_HRU = 'DECENTRAL_SINGLE_ROOM_VENTILATION_UNITS_WITHOUT_HRU',
  DECENTRAL_SINGLE_ROOM_VENTILATION_UNITS_WITH_HRU = 'DECENTRAL_SINGLE_ROOM_VENTILATION_UNITS_WITH_HRU',
  DIRECT_ELECTRICITY_HEATING = 'DIRECT_ELECTRICITY_HEATING',
  DISTRICT_HEATING_WITHOUT_KWK = 'DISTRICT_HEATING_WITHOUT_KWK',
  DISTRICT_HEATING_WITH_KWK = 'DISTRICT_HEATING_WITH_KWK',
  ELECTRICITY_MISCELLANEOUS = 'ELECTRICITY_MISCELLANEOUS',
  ELECTRIC_FLOW_HEATER = 'ELECTRIC_FLOW_HEATER',
  ELECTRIC_HEAT_PUMP_AIR = 'ELECTRIC_HEAT_PUMP_AIR',
  ELECTRIC_HEAT_PUMP_GEO = 'ELECTRIC_HEAT_PUMP_GEO',
  ELECTRIC_IMMERSION_HEATER = 'ELECTRIC_IMMERSION_HEATER',
  FLUORESCENT_LAMPS = 'FLUORESCENT_LAMPS',
  GAS_CONDENSING_BOILER = 'GAS_CONDENSING_BOILER',
  GAS_FLOOR_HEATING = 'GAS_FLOOR_HEATING',
  GAS_FLOW_HEATER = 'GAS_FLOW_HEATER',
  GAS_NON_CONDENSING_BOILER = 'GAS_NON_CONDENSING_BOILER',
  GAS_ROOM_HEATER = 'GAS_ROOM_HEATER',
  HALOGEN_LAMPS = 'HALOGEN_LAMPS',
  INCANDESCENT_LAMPS = 'INCANDESCENT_LAMPS',
  LED = 'LED',
  LOW_TEMPERATURE_BOILER = 'LOW_TEMPERATURE_BOILER',
  OIL_FURNACE = 'OIL_FURNACE',
  REVERSIBLE_AIR_WATER_HEAT_PUMP = 'REVERSIBLE_AIR_WATER_HEAT_PUMP',
  REVERSIBLE_BRINE_WATER_HEAT_PUMP = 'REVERSIBLE_BRINE_WATER_HEAT_PUMP',
  ROOM_AIR_CONDITIONING = 'ROOM_AIR_CONDITIONING',
  SMALL_ELECTRIC_STORAGE = 'SMALL_ELECTRIC_STORAGE',
  SOLAR_PLANT = 'SOLAR_PLANT',
  STANDARD_BOILER = 'STANDARD_BOILER',
  WOOD_BOILER = 'WOOD_BOILER',
  WOOD_FURNACE = 'WOOD_FURNACE',
}

/** select columns of table "energy_consumer_technology_type" */
export enum energy_consumer_technology_type_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "energy_consumer_technology_type" */
export enum energy_consumer_technology_type_update_column {
  /** column name */
  id = 'id',
}

/** unique or primary key constraints on table "energy_consumer_type" */
export enum energy_consumer_type_constraint {
  /** unique or primary key constraint on columns "id" */
  energy_source_coverage_pkey = 'energy_source_coverage_pkey',
}

export enum energy_consumer_type_enum {
  COOLING = 'COOLING',
  ELECTRICITY = 'ELECTRICITY',
  GENERAL = 'GENERAL',
  HEATING = 'HEATING',
  HOT_WATER = 'HOT_WATER',
  LIGHTING = 'LIGHTING',
  VENTILATION = 'VENTILATION',
}

/** select columns of table "energy_consumer_type" */
export enum energy_consumer_type_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "energy_consumer_type" */
export enum energy_consumer_type_update_column {
  /** column name */
  id = 'id',
}

/** update columns of table "energy_consumer" */
export enum energy_consumer_update_column {
  /** column name */
  building_model_id = 'building_model_id',
  /** column name */
  construction_year = 'construction_year',
  /** column name */
  created_at = 'created_at',
  /** column name */
  efficiency = 'efficiency',
  /** column name */
  energy_consumer_technology_type_id = 'energy_consumer_technology_type_id',
  /** column name */
  energy_consumer_type_id = 'energy_consumer_type_id',
  /** column name */
  id = 'id',
  /** column name */
  name = 'name',
  /** column name */
  updated_at = 'updated_at',
}

export enum energy_consumption_type_enum {
  ENERGY = 'ENERGY',
  WASTE = 'WASTE',
  WATER = 'WATER',
}

/** unique or primary key constraints on table "energy_distribution" */
export enum energy_distribution_constraint {
  /** unique or primary key constraint on columns "id" */
  energy_distribution_pkey = 'energy_distribution_pkey',
}

/** select columns of table "energy_distribution" */
export enum energy_distribution_select_column {
  /** column name */
  balancing_with_components = 'balancing_with_components',
  /** column name */
  building_model_id = 'building_model_id',
  /** column name */
  construction_year = 'construction_year',
  /** column name */
  energy_distribution_type_id = 'energy_distribution_type_id',
  /** column name */
  heat_distribution_loss = 'heat_distribution_loss',
  /** column name */
  heat_transfer_loss = 'heat_transfer_loss',
  /** column name */
  id = 'id',
  /** column name */
  last_balancing_set_to_never = 'last_balancing_set_to_never',
  /** column name */
  last_hydraulic_balancing = 'last_hydraulic_balancing',
  /** column name */
  recoverable_heat_loss = 'recoverable_heat_loss',
}

/** select "energy_distribution_aggregate_bool_exp_bool_and_arguments_columns" columns of table "energy_distribution" */
export enum energy_distribution_select_column_energy_distribution_aggregate_bool_exp_bool_and_arguments_columns {
  /** column name */
  balancing_with_components = 'balancing_with_components',
  /** column name */
  last_balancing_set_to_never = 'last_balancing_set_to_never',
}

/** select "energy_distribution_aggregate_bool_exp_bool_or_arguments_columns" columns of table "energy_distribution" */
export enum energy_distribution_select_column_energy_distribution_aggregate_bool_exp_bool_or_arguments_columns {
  /** column name */
  balancing_with_components = 'balancing_with_components',
  /** column name */
  last_balancing_set_to_never = 'last_balancing_set_to_never',
}

/** unique or primary key constraints on table "energy_distribution_type" */
export enum energy_distribution_type_constraint {
  /** unique or primary key constraint on columns "id" */
  energy_distribution_type_pkey = 'energy_distribution_type_pkey',
}

export enum energy_distribution_type_enum {
  AIR_DUCTS_INTERNAL = 'AIR_DUCTS_INTERNAL',
  CENTRAL_CIRCULATION_INSIDE = 'CENTRAL_CIRCULATION_INSIDE',
  CENTRAL_CIRCULATION_OUTSIDE = 'CENTRAL_CIRCULATION_OUTSIDE',
  CENTRAL_INSIDE = 'CENTRAL_INSIDE',
  CENTRAL_NO_CIRCULATION_INSIDE = 'CENTRAL_NO_CIRCULATION_INSIDE',
  CENTRAL_NO_CIRCULATION_OUTSIDE = 'CENTRAL_NO_CIRCULATION_OUTSIDE',
  CENTRAL_OUTSIDE = 'CENTRAL_OUTSIDE',
  DECENTRAL = 'DECENTRAL',
  FAN = 'FAN',
  THERMAL_COMPONENT_ACTIVATION = 'THERMAL_COMPONENT_ACTIVATION',
}

/** select columns of table "energy_distribution_type" */
export enum energy_distribution_type_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "energy_distribution_type" */
export enum energy_distribution_type_update_column {
  /** column name */
  id = 'id',
}

/** update columns of table "energy_distribution" */
export enum energy_distribution_update_column {
  /** column name */
  balancing_with_components = 'balancing_with_components',
  /** column name */
  building_model_id = 'building_model_id',
  /** column name */
  construction_year = 'construction_year',
  /** column name */
  energy_distribution_type_id = 'energy_distribution_type_id',
  /** column name */
  heat_distribution_loss = 'heat_distribution_loss',
  /** column name */
  heat_transfer_loss = 'heat_transfer_loss',
  /** column name */
  id = 'id',
  /** column name */
  last_balancing_set_to_never = 'last_balancing_set_to_never',
  /** column name */
  last_hydraulic_balancing = 'last_hydraulic_balancing',
  /** column name */
  recoverable_heat_loss = 'recoverable_heat_loss',
}

/** unique or primary key constraints on table "energy_path" */
export enum energy_path_constraint {
  /** unique or primary key constraint on columns "id" */
  energy_path_pkey = 'energy_path_pkey',
}

/** select columns of table "energy_path" */
export enum energy_path_select_column {
  /** column name */
  building_model_id = 'building_model_id',
  /** column name */
  co2_costs = 'co2_costs',
  /** column name */
  co2_costs_total = 'co2_costs_total',
  /** column name */
  crrem_co2_emissions = 'crrem_co2_emissions',
  /** column name */
  crrem_co2_emissions_total = 'crrem_co2_emissions_total',
  /** column name */
  crrem_energy_consumption = 'crrem_energy_consumption',
  /** column name */
  crrem_energy_consumption_total = 'crrem_energy_consumption_total',
  /** column name */
  crrem_excess_emission_cost_total = 'crrem_excess_emission_cost_total',
  /** column name */
  crrem_excess_emissions_total = 'crrem_excess_emissions_total',
  /** column name */
  crrem_primary_energy = 'crrem_primary_energy',
  /** column name */
  crrem_primary_energy_total = 'crrem_primary_energy_total',
  /** column name */
  crrem_scope_1_co2_emissions = 'crrem_scope_1_co2_emissions',
  /** column name */
  crrem_scope_1_co2_emissions_total = 'crrem_scope_1_co2_emissions_total',
  /** column name */
  crrem_scope_2_co2_emissions = 'crrem_scope_2_co2_emissions',
  /** column name */
  crrem_scope_2_co2_emissions_total = 'crrem_scope_2_co2_emissions_total',
  /** column name */
  crrem_scope_3_co2_emissions = 'crrem_scope_3_co2_emissions',
  /** column name */
  crrem_scope_3_co2_emissions_total = 'crrem_scope_3_co2_emissions_total',
  /** column name */
  crrem_stranded = 'crrem_stranded',
  /** column name */
  efficiency_class_id = 'efficiency_class_id',
  /** column name */
  energy_consumption = 'energy_consumption',
  /** column name */
  energy_consumption_total = 'energy_consumption_total',
  /** column name */
  energy_costs = 'energy_costs',
  /** column name */
  energy_costs_total = 'energy_costs_total',
  /** column name */
  eu_taxonomy_compliance_id = 'eu_taxonomy_compliance_id',
  /** column name */
  id = 'id',
  /** column name */
  landlord_scope_1_co2_emissions = 'landlord_scope_1_co2_emissions',
  /** column name */
  landlord_scope_1_co2_emissions_total = 'landlord_scope_1_co2_emissions_total',
  /** column name */
  landlord_scope_2_co2_emissions = 'landlord_scope_2_co2_emissions',
  /** column name */
  landlord_scope_2_co2_emissions_total = 'landlord_scope_2_co2_emissions_total',
  /** column name */
  landlord_scope_3_co2_emissions = 'landlord_scope_3_co2_emissions',
  /** column name */
  landlord_scope_3_co2_emissions_total = 'landlord_scope_3_co2_emissions_total',
  /** column name */
  national_energy_law_arbeitshilfe_85_co2_emissions = 'national_energy_law_arbeitshilfe_85_co2_emissions',
  /** column name */
  national_energy_law_arbeitshilfe_85_co2_emissions_total = 'national_energy_law_arbeitshilfe_85_co2_emissions_total',
  /** column name */
  national_energy_law_co2_emissions = 'national_energy_law_co2_emissions',
  /** column name */
  national_energy_law_co2_emissions_total = 'national_energy_law_co2_emissions_total',
  /** column name */
  national_energy_law_consumption = 'national_energy_law_consumption',
  /** column name */
  national_energy_law_consumption_total = 'national_energy_law_consumption_total',
  /** column name */
  national_energy_law_gemis_co2_emissions = 'national_energy_law_gemis_co2_emissions',
  /** column name */
  national_energy_law_gemis_co2_emissions_total = 'national_energy_law_gemis_co2_emissions_total',
  /** column name */
  national_energy_law_primary_energy = 'national_energy_law_primary_energy',
  /** column name */
  national_energy_law_primary_energy_total = 'national_energy_law_primary_energy_total',
  /** column name */
  national_energy_law_uba_co2_emissions = 'national_energy_law_uba_co2_emissions',
  /** column name */
  national_energy_law_uba_co2_emissions_total = 'national_energy_law_uba_co2_emissions_total',
  /** column name */
  tax_bracket_id = 'tax_bracket_id',
  /** column name */
  tenant_scope_1_co2_emissions = 'tenant_scope_1_co2_emissions',
  /** column name */
  tenant_scope_1_co2_emissions_total = 'tenant_scope_1_co2_emissions_total',
  /** column name */
  tenant_scope_2_co2_emissions = 'tenant_scope_2_co2_emissions',
  /** column name */
  tenant_scope_2_co2_emissions_total = 'tenant_scope_2_co2_emissions_total',
  /** column name */
  tenant_scope_3_co2_emissions = 'tenant_scope_3_co2_emissions',
  /** column name */
  tenant_scope_3_co2_emissions_total = 'tenant_scope_3_co2_emissions_total',
  /** column name */
  year = 'year',
}

/** select "energy_path_aggregate_bool_exp_bool_and_arguments_columns" columns of table "energy_path" */
export enum energy_path_select_column_energy_path_aggregate_bool_exp_bool_and_arguments_columns {
  /** column name */
  crrem_stranded = 'crrem_stranded',
}

/** select "energy_path_aggregate_bool_exp_bool_or_arguments_columns" columns of table "energy_path" */
export enum energy_path_select_column_energy_path_aggregate_bool_exp_bool_or_arguments_columns {
  /** column name */
  crrem_stranded = 'crrem_stranded',
}

/** update columns of table "energy_path" */
export enum energy_path_update_column {
  /** column name */
  building_model_id = 'building_model_id',
  /** column name */
  co2_costs = 'co2_costs',
  /** column name */
  co2_costs_total = 'co2_costs_total',
  /** column name */
  crrem_co2_emissions = 'crrem_co2_emissions',
  /** column name */
  crrem_co2_emissions_total = 'crrem_co2_emissions_total',
  /** column name */
  crrem_energy_consumption = 'crrem_energy_consumption',
  /** column name */
  crrem_energy_consumption_total = 'crrem_energy_consumption_total',
  /** column name */
  crrem_excess_emission_cost_total = 'crrem_excess_emission_cost_total',
  /** column name */
  crrem_excess_emissions_total = 'crrem_excess_emissions_total',
  /** column name */
  crrem_primary_energy = 'crrem_primary_energy',
  /** column name */
  crrem_primary_energy_total = 'crrem_primary_energy_total',
  /** column name */
  crrem_scope_1_co2_emissions = 'crrem_scope_1_co2_emissions',
  /** column name */
  crrem_scope_1_co2_emissions_total = 'crrem_scope_1_co2_emissions_total',
  /** column name */
  crrem_scope_2_co2_emissions = 'crrem_scope_2_co2_emissions',
  /** column name */
  crrem_scope_2_co2_emissions_total = 'crrem_scope_2_co2_emissions_total',
  /** column name */
  crrem_scope_3_co2_emissions = 'crrem_scope_3_co2_emissions',
  /** column name */
  crrem_scope_3_co2_emissions_total = 'crrem_scope_3_co2_emissions_total',
  /** column name */
  crrem_stranded = 'crrem_stranded',
  /** column name */
  efficiency_class_id = 'efficiency_class_id',
  /** column name */
  energy_consumption = 'energy_consumption',
  /** column name */
  energy_consumption_total = 'energy_consumption_total',
  /** column name */
  energy_costs = 'energy_costs',
  /** column name */
  energy_costs_total = 'energy_costs_total',
  /** column name */
  eu_taxonomy_compliance_id = 'eu_taxonomy_compliance_id',
  /** column name */
  id = 'id',
  /** column name */
  landlord_scope_1_co2_emissions = 'landlord_scope_1_co2_emissions',
  /** column name */
  landlord_scope_1_co2_emissions_total = 'landlord_scope_1_co2_emissions_total',
  /** column name */
  landlord_scope_2_co2_emissions = 'landlord_scope_2_co2_emissions',
  /** column name */
  landlord_scope_2_co2_emissions_total = 'landlord_scope_2_co2_emissions_total',
  /** column name */
  landlord_scope_3_co2_emissions = 'landlord_scope_3_co2_emissions',
  /** column name */
  landlord_scope_3_co2_emissions_total = 'landlord_scope_3_co2_emissions_total',
  /** column name */
  national_energy_law_arbeitshilfe_85_co2_emissions = 'national_energy_law_arbeitshilfe_85_co2_emissions',
  /** column name */
  national_energy_law_arbeitshilfe_85_co2_emissions_total = 'national_energy_law_arbeitshilfe_85_co2_emissions_total',
  /** column name */
  national_energy_law_co2_emissions = 'national_energy_law_co2_emissions',
  /** column name */
  national_energy_law_co2_emissions_total = 'national_energy_law_co2_emissions_total',
  /** column name */
  national_energy_law_consumption = 'national_energy_law_consumption',
  /** column name */
  national_energy_law_consumption_total = 'national_energy_law_consumption_total',
  /** column name */
  national_energy_law_gemis_co2_emissions = 'national_energy_law_gemis_co2_emissions',
  /** column name */
  national_energy_law_gemis_co2_emissions_total = 'national_energy_law_gemis_co2_emissions_total',
  /** column name */
  national_energy_law_primary_energy = 'national_energy_law_primary_energy',
  /** column name */
  national_energy_law_primary_energy_total = 'national_energy_law_primary_energy_total',
  /** column name */
  national_energy_law_uba_co2_emissions = 'national_energy_law_uba_co2_emissions',
  /** column name */
  national_energy_law_uba_co2_emissions_total = 'national_energy_law_uba_co2_emissions_total',
  /** column name */
  tax_bracket_id = 'tax_bracket_id',
  /** column name */
  tenant_scope_1_co2_emissions = 'tenant_scope_1_co2_emissions',
  /** column name */
  tenant_scope_1_co2_emissions_total = 'tenant_scope_1_co2_emissions_total',
  /** column name */
  tenant_scope_2_co2_emissions = 'tenant_scope_2_co2_emissions',
  /** column name */
  tenant_scope_2_co2_emissions_total = 'tenant_scope_2_co2_emissions_total',
  /** column name */
  tenant_scope_3_co2_emissions = 'tenant_scope_3_co2_emissions',
  /** column name */
  tenant_scope_3_co2_emissions_total = 'tenant_scope_3_co2_emissions_total',
  /** column name */
  year = 'year',
}

/** unique or primary key constraints on table "energy_producer" */
export enum energy_producer_constraint {
  /** unique or primary key constraint on columns "id" */
  energy_producer_pkey = 'energy_producer_pkey',
}

/** select columns of table "energy_producer" */
export enum energy_producer_select_column {
  /** column name */
  building_model_id = 'building_model_id',
  /** column name */
  construction_year = 'construction_year',
  /** column name */
  efficiency = 'efficiency',
  /** column name */
  energy_source_type_id = 'energy_source_type_id',
  /** column name */
  id = 'id',
  /** column name */
  module_angle = 'module_angle',
  /** column name */
  module_peak_power = 'module_peak_power',
  /** column name */
  name = 'name',
  /** column name */
  nominal_power = 'nominal_power',
  /** column name */
  roof_area_ratio = 'roof_area_ratio',
  /** column name */
  solar_active_area = 'solar_active_area',
}

/** update columns of table "energy_producer" */
export enum energy_producer_update_column {
  /** column name */
  building_model_id = 'building_model_id',
  /** column name */
  construction_year = 'construction_year',
  /** column name */
  efficiency = 'efficiency',
  /** column name */
  energy_source_type_id = 'energy_source_type_id',
  /** column name */
  id = 'id',
  /** column name */
  module_angle = 'module_angle',
  /** column name */
  module_peak_power = 'module_peak_power',
  /** column name */
  name = 'name',
  /** column name */
  nominal_power = 'nominal_power',
  /** column name */
  roof_area_ratio = 'roof_area_ratio',
  /** column name */
  solar_active_area = 'solar_active_area',
}

/** unique or primary key constraints on table "energy_source_type" */
export enum energy_source_type_constraint {
  /** unique or primary key constraint on columns "id" */
  energy_source_type_pkey = 'energy_source_type_pkey',
}

export enum energy_source_type_enum {
  BIO_GAS = 'BIO_GAS',
  COAL = 'COAL',
  DISTRICT_HEATING_CHP_FOSSIL_COAL = 'DISTRICT_HEATING_CHP_FOSSIL_COAL',
  DISTRICT_HEATING_CHP_FOSSIL_GAS = 'DISTRICT_HEATING_CHP_FOSSIL_GAS',
  DISTRICT_HEATING_CHP_RENEWABLE = 'DISTRICT_HEATING_CHP_RENEWABLE',
  DISTRICT_HEATING_PLANTS_FOSSIL_COAL = 'DISTRICT_HEATING_PLANTS_FOSSIL_COAL',
  DISTRICT_HEATING_PLANTS_FOSSIL_GAS = 'DISTRICT_HEATING_PLANTS_FOSSIL_GAS',
  DISTRICT_HEATING_PLANTS_RENEWABLE = 'DISTRICT_HEATING_PLANTS_RENEWABLE',
  ELECTRICITY_GREEN = 'ELECTRICITY_GREEN',
  ELECTRICITY_MIX = 'ELECTRICITY_MIX',
  FUEL_OIL = 'FUEL_OIL',
  LIGNITE = 'LIGNITE',
  LPG = 'LPG',
  NATURAL_GAS = 'NATURAL_GAS',
  SOLAR = 'SOLAR',
  UNSPECIFIED = 'UNSPECIFIED',
  WOOD = 'WOOD',
  WOODEN_PELLETS = 'WOODEN_PELLETS',
}

/** select columns of table "energy_source_type" */
export enum energy_source_type_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "energy_source_type" */
export enum energy_source_type_update_column {
  /** column name */
  id = 'id',
}

/** unique or primary key constraints on table "energy_storage" */
export enum energy_storage_constraint {
  /** unique or primary key constraint on columns "id" */
  energy_storage_pkey = 'energy_storage_pkey',
}

/** select columns of table "energy_storage" */
export enum energy_storage_select_column {
  /** column name */
  annual_heat_loss = 'annual_heat_loss',
  /** column name */
  building_model_id = 'building_model_id',
  /** column name */
  construction_year = 'construction_year',
  /** column name */
  energy_storage_type_id = 'energy_storage_type_id',
  /** column name */
  id = 'id',
  /** column name */
  recoverable_heat_loss = 'recoverable_heat_loss',
}

/** unique or primary key constraints on table "energy_storage_type" */
export enum energy_storage_type_constraint {
  /** unique or primary key constraint on columns "id" */
  energy_storage_type_pkey = 'energy_storage_type_pkey',
}

export enum energy_storage_type_enum {
  BUFFER_STORAGE = 'BUFFER_STORAGE',
  BUFFER_STORAGE_ELECTRIC = 'BUFFER_STORAGE_ELECTRIC',
  CENTRAL_INSIDE = 'CENTRAL_INSIDE',
  CENTRAL_OUTSIDE = 'CENTRAL_OUTSIDE',
}

/** select columns of table "energy_storage_type" */
export enum energy_storage_type_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "energy_storage_type" */
export enum energy_storage_type_update_column {
  /** column name */
  id = 'id',
}

/** update columns of table "energy_storage" */
export enum energy_storage_update_column {
  /** column name */
  annual_heat_loss = 'annual_heat_loss',
  /** column name */
  building_model_id = 'building_model_id',
  /** column name */
  construction_year = 'construction_year',
  /** column name */
  energy_storage_type_id = 'energy_storage_type_id',
  /** column name */
  id = 'id',
  /** column name */
  recoverable_heat_loss = 'recoverable_heat_loss',
}

/** unique or primary key constraints on table "energy_supplier_value_type" */
export enum energy_supplier_value_type_constraint {
  /** unique or primary key constraint on columns "id" */
  energy_supplier_value_type_pkey = 'energy_supplier_value_type_pkey',
}

export enum energy_supplier_value_type_enum {
  PROGRESSIVE = 'PROGRESSIVE',
  STATIC = 'STATIC',
}

/** select columns of table "energy_supplier_value_type" */
export enum energy_supplier_value_type_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "energy_supplier_value_type" */
export enum energy_supplier_value_type_update_column {
  /** column name */
  id = 'id',
}

/** unique or primary key constraints on table "energy_system" */
export enum energy_system_constraint {
  /** unique or primary key constraint on columns "id" */
  energy_system_pkey = 'energy_system_pkey',
  /** unique or primary key constraint on columns "building_model_id", "secondary_id" */
  energy_system_secondary_id_building_model_id_key = 'energy_system_secondary_id_building_model_id_key',
}

/** unique or primary key constraints on table "energy_system_consumer_route" */
export enum energy_system_consumer_route_constraint {
  /** unique or primary key constraint on columns "id" */
  energy_system_consumer_route_pkey = 'energy_system_consumer_route_pkey',
  /** unique or primary key constraint on columns "energy_system_id", "secondary_id" */
  energy_system_consumer_route_secondary_id_energy_system_id_key = 'energy_system_consumer_route_secondary_id_energy_system_id_key',
}

/** select columns of table "energy_system_consumer_route" */
export enum energy_system_consumer_route_select_column {
  /** column name */
  area_id = 'area_id',
  /** column name */
  emission_certificate_id = 'emission_certificate_id',
  /** column name */
  energy_consumer_id = 'energy_consumer_id',
  /** column name */
  energy_distribution_id = 'energy_distribution_id',
  /** column name */
  energy_effective = 'energy_effective',
  /** column name */
  energy_final = 'energy_final',
  /** column name */
  energy_primary = 'energy_primary',
  /** column name */
  energy_source_type_id = 'energy_source_type_id',
  /** column name */
  energy_storage_id = 'energy_storage_id',
  /** column name */
  energy_system_id = 'energy_system_id',
  /** column name */
  final_energy_reduction = 'final_energy_reduction',
  /** column name */
  id = 'id',
  /** column name */
  primary_energy_factor_emission_certificate_id = 'primary_energy_factor_emission_certificate_id',
  /** column name */
  route_contribution = 'route_contribution',
  /** column name */
  secondary_id = 'secondary_id',
  /** column name */
  use_renewable_energy = 'use_renewable_energy',
}

/** select "energy_system_consumer_route_aggregate_bool_exp_bool_and_arguments_columns" columns of table "energy_system_consumer_route" */
export enum energy_system_consumer_route_select_column_energy_system_consumer_route_aggregate_bool_exp_bool_and_arguments_columns {
  /** column name */
  use_renewable_energy = 'use_renewable_energy',
}

/** select "energy_system_consumer_route_aggregate_bool_exp_bool_or_arguments_columns" columns of table "energy_system_consumer_route" */
export enum energy_system_consumer_route_select_column_energy_system_consumer_route_aggregate_bool_exp_bool_or_arguments_columns {
  /** column name */
  use_renewable_energy = 'use_renewable_energy',
}

/** update columns of table "energy_system_consumer_route" */
export enum energy_system_consumer_route_update_column {
  /** column name */
  area_id = 'area_id',
  /** column name */
  emission_certificate_id = 'emission_certificate_id',
  /** column name */
  energy_consumer_id = 'energy_consumer_id',
  /** column name */
  energy_distribution_id = 'energy_distribution_id',
  /** column name */
  energy_effective = 'energy_effective',
  /** column name */
  energy_final = 'energy_final',
  /** column name */
  energy_primary = 'energy_primary',
  /** column name */
  energy_source_type_id = 'energy_source_type_id',
  /** column name */
  energy_storage_id = 'energy_storage_id',
  /** column name */
  energy_system_id = 'energy_system_id',
  /** column name */
  final_energy_reduction = 'final_energy_reduction',
  /** column name */
  id = 'id',
  /** column name */
  primary_energy_factor_emission_certificate_id = 'primary_energy_factor_emission_certificate_id',
  /** column name */
  route_contribution = 'route_contribution',
  /** column name */
  secondary_id = 'secondary_id',
  /** column name */
  use_renewable_energy = 'use_renewable_energy',
}

/** unique or primary key constraints on table "energy_system_producer_route" */
export enum energy_system_producer_route_constraint {
  /** unique or primary key constraint on columns "energy_system_id", "secondary_id" */
  energy_system_producer_route_energy_system_id_secondary_id_key = 'energy_system_producer_route_energy_system_id_secondary_id_key',
  /** unique or primary key constraint on columns "id" */
  energy_system_producer_route_pkey = 'energy_system_producer_route_pkey',
}

/** select columns of table "energy_system_producer_route" */
export enum energy_system_producer_route_select_column {
  /** column name */
  area_id = 'area_id',
  /** column name */
  energy_effective = 'energy_effective',
  /** column name */
  energy_produced = 'energy_produced',
  /** column name */
  energy_producer_id = 'energy_producer_id',
  /** column name */
  energy_source_type_id = 'energy_source_type_id',
  /** column name */
  energy_system_id = 'energy_system_id',
  /** column name */
  id = 'id',
  /** column name */
  secondary_id = 'secondary_id',
}

/** update columns of table "energy_system_producer_route" */
export enum energy_system_producer_route_update_column {
  /** column name */
  area_id = 'area_id',
  /** column name */
  energy_effective = 'energy_effective',
  /** column name */
  energy_produced = 'energy_produced',
  /** column name */
  energy_producer_id = 'energy_producer_id',
  /** column name */
  energy_source_type_id = 'energy_source_type_id',
  /** column name */
  energy_system_id = 'energy_system_id',
  /** column name */
  id = 'id',
  /** column name */
  secondary_id = 'secondary_id',
}

/** select columns of table "energy_system" */
export enum energy_system_select_column {
  /** column name */
  building_model_id = 'building_model_id',
  /** column name */
  data_source_type_id = 'data_source_type_id',
  /** column name */
  description = 'description',
  /** column name */
  energy_system_type_id = 'energy_system_type_id',
  /** column name */
  id = 'id',
  /** column name */
  secondary_id = 'secondary_id',
}

/** unique or primary key constraints on table "energy_system_type" */
export enum energy_system_type_constraint {
  /** unique or primary key constraint on columns "id" */
  energy_system_type_pkey = 'energy_system_type_pkey',
}

export enum energy_system_type_enum {
  COOLING = 'COOLING',
  ELECTRICITY = 'ELECTRICITY',
  GENERAL = 'GENERAL',
  HEATING = 'HEATING',
  HOT_WATER = 'HOT_WATER',
  LIGHTING = 'LIGHTING',
  SOLAR = 'SOLAR',
  VENTILATION = 'VENTILATION',
}

/** select columns of table "energy_system_type" */
export enum energy_system_type_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "energy_system_type" */
export enum energy_system_type_update_column {
  /** column name */
  id = 'id',
}

/** update columns of table "energy_system" */
export enum energy_system_update_column {
  /** column name */
  building_model_id = 'building_model_id',
  /** column name */
  data_source_type_id = 'data_source_type_id',
  /** column name */
  description = 'description',
  /** column name */
  energy_system_type_id = 'energy_system_type_id',
  /** column name */
  id = 'id',
  /** column name */
  secondary_id = 'secondary_id',
}

/** unique or primary key constraints on table "entity_state_type" */
export enum entity_state_type_constraint {
  /** unique or primary key constraint on columns "id" */
  entity_state_type_pkey = 'entity_state_type_pkey',
}

export enum entity_state_type_enum {
  /** the entitiy is accesible to the user */
  ACTIVE = 'ACTIVE',
  /** The entitiy is ignored by analysis and can not be updated by the user. An inactive entity can be re-activated. */
  INACTIVE = 'INACTIVE',
}

/** select columns of table "entity_state_type" */
export enum entity_state_type_select_column {
  /** column name */
  description = 'description',
  /** column name */
  id = 'id',
}

/** update columns of table "entity_state_type" */
export enum entity_state_type_update_column {
  /** column name */
  description = 'description',
  /** column name */
  id = 'id',
}

/** unique or primary key constraints on table "envelope" */
export enum envelope_constraint {
  /** unique or primary key constraint on columns "id" */
  envelope_pkey = 'envelope_pkey',
  /** unique or primary key constraint on columns "building_model_id", "secondary_id" */
  envelope_secondary_id_building_model_id_key = 'envelope_secondary_id_building_model_id_key',
}

/** select columns of table "envelope" */
export enum envelope_select_column {
  /** column name */
  area = 'area',
  /** column name */
  base_construction_lambda = 'base_construction_lambda',
  /** column name */
  base_construction_material_name = 'base_construction_material_name',
  /** column name */
  base_construction_thickness = 'base_construction_thickness',
  /** column name */
  building_model_id = 'building_model_id',
  /** column name */
  data_source_type_id = 'data_source_type_id',
  /** column name */
  description = 'description',
  /** column name */
  envelope_type_id = 'envelope_type_id',
  /** column name */
  id = 'id',
  /** column name */
  inclination = 'inclination',
  /** column name */
  insulation_lambda = 'insulation_lambda',
  /** column name */
  insulation_material_category_id = 'insulation_material_category_id',
  /** column name */
  insulation_material_name = 'insulation_material_name',
  /** column name */
  insulation_method_id = 'insulation_method_id',
  /** column name */
  insulation_thickness = 'insulation_thickness',
  /** column name */
  orientation = 'orientation',
  /** column name */
  secondary_id = 'secondary_id',
  /** column name */
  u_value = 'u_value',
}

/** unique or primary key constraints on table "envelope_type" */
export enum envelope_type_constraint {
  /** unique or primary key constraint on columns "id" */
  envelope_type_pkey = 'envelope_type_pkey',
}

export enum envelope_type_enum {
  BASEMENT_CEILING = 'BASEMENT_CEILING',
  DOOR = 'DOOR',
  FLAT_ROOF = 'FLAT_ROOF',
  FLOOR = 'FLOOR',
  PITCHED_ROOF = 'PITCHED_ROOF',
  TOP_FLOOR_CEILING = 'TOP_FLOOR_CEILING',
  WALL = 'WALL',
  WINDOW = 'WINDOW',
}

/** select columns of table "envelope_type" */
export enum envelope_type_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "envelope_type" */
export enum envelope_type_update_column {
  /** column name */
  id = 'id',
}

/** update columns of table "envelope" */
export enum envelope_update_column {
  /** column name */
  area = 'area',
  /** column name */
  base_construction_lambda = 'base_construction_lambda',
  /** column name */
  base_construction_material_name = 'base_construction_material_name',
  /** column name */
  base_construction_thickness = 'base_construction_thickness',
  /** column name */
  building_model_id = 'building_model_id',
  /** column name */
  data_source_type_id = 'data_source_type_id',
  /** column name */
  description = 'description',
  /** column name */
  envelope_type_id = 'envelope_type_id',
  /** column name */
  id = 'id',
  /** column name */
  inclination = 'inclination',
  /** column name */
  insulation_lambda = 'insulation_lambda',
  /** column name */
  insulation_material_category_id = 'insulation_material_category_id',
  /** column name */
  insulation_material_name = 'insulation_material_name',
  /** column name */
  insulation_method_id = 'insulation_method_id',
  /** column name */
  insulation_thickness = 'insulation_thickness',
  /** column name */
  orientation = 'orientation',
  /** column name */
  secondary_id = 'secondary_id',
  /** column name */
  u_value = 'u_value',
}

/** unique or primary key constraints on table "eu_taxonomy_compliance" */
export enum eu_taxonomy_compliance_constraint {
  /** unique or primary key constraint on columns "id" */
  eu_taxonomy_compliance_pkey = 'eu_taxonomy_compliance_pkey',
}

export enum eu_taxonomy_compliance_enum {
  COMPLIANT = 'COMPLIANT',
  MEETS_DNSH_CRITERIA = 'MEETS_DNSH_CRITERIA',
  NON_COMPLIANT = 'NON_COMPLIANT',
  UNKNOWN = 'UNKNOWN',
}

/** select columns of table "eu_taxonomy_compliance" */
export enum eu_taxonomy_compliance_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "eu_taxonomy_compliance" */
export enum eu_taxonomy_compliance_update_column {
  /** column name */
  id = 'id',
}

/** unique or primary key constraints on table "eu_taxonomy_datasource" */
export enum eu_taxonomy_datasource_constraint {
  /** unique or primary key constraint on columns "id" */
  eu_taxonomy_datasource_pkey = 'eu_taxonomy_datasource_pkey',
}

export enum eu_taxonomy_datasource_enum {
  DEFAULT_INDEX = 'DEFAULT_INDEX',
  DREES_UND_SOMMER = 'DREES_UND_SOMMER',
}

/** select columns of table "eu_taxonomy_datasource" */
export enum eu_taxonomy_datasource_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "eu_taxonomy_datasource" */
export enum eu_taxonomy_datasource_update_column {
  /** column name */
  id = 'id',
}

/** unique or primary key constraints on table "event_status" */
export enum event_status_constraint {
  /** unique or primary key constraint on columns "id" */
  event_status_pkey = 'event_status_pkey',
}

export enum event_status_enum {
  ERROR = 'ERROR',
  SUCCESS = 'SUCCESS',
}

/** select columns of table "event_status" */
export enum event_status_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "event_status" */
export enum event_status_update_column {
  /** column name */
  id = 'id',
}

/** unique or primary key constraints on table "export_template" */
export enum export_template_constraint {
  /** unique or primary key constraint on columns "id" */
  export_template_pkey = 'export_template_pkey',
}

/** select columns of table "export_template" */
export enum export_template_select_column {
  /** column name */
  created_at = 'created_at',
  /** column name */
  export_type_id = 'export_type_id',
  /** column name */
  id = 'id',
  /** column name */
  name = 'name',
  /** column name */
  org_id = 'org_id',
  /** column name */
  s3_key = 's3_key',
}

/** unique or primary key constraints on table "export_template_type" */
export enum export_template_type_constraint {
  /** unique or primary key constraint on columns "id" */
  export_template_type_pkey = 'export_template_type_pkey',
}

export enum export_template_type_enum {
  CUSTOM_EXPORT = 'CUSTOM_EXPORT',
  STANDARD_EXPORT = 'STANDARD_EXPORT',
}

/** select columns of table "export_template_type" */
export enum export_template_type_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "export_template_type" */
export enum export_template_type_update_column {
  /** column name */
  id = 'id',
}

/** update columns of table "export_template" */
export enum export_template_update_column {
  /** column name */
  created_at = 'created_at',
  /** column name */
  export_type_id = 'export_type_id',
  /** column name */
  id = 'id',
  /** column name */
  name = 'name',
  /** column name */
  org_id = 'org_id',
  /** column name */
  s3_key = 's3_key',
}

/** unique or primary key constraints on table "file_chunk" */
export enum file_chunk_constraint {
  /** unique or primary key constraint on columns "id" */
  file_chunk_pkey = 'file_chunk_pkey',
}

/** unique or primary key constraints on table "file_chunk_file_link" */
export enum file_chunk_file_link_constraint {
  /** unique or primary key constraint on columns "chunk_id" */
  file_chunk_file_link_pkey = 'file_chunk_file_link_pkey',
}

/** select columns of table "file_chunk_file_link" */
export enum file_chunk_file_link_select_column {
  /** column name */
  chunk_id = 'chunk_id',
  /** column name */
  created_at = 'created_at',
  /** column name */
  file_id = 'file_id',
}

/** update columns of table "file_chunk_file_link" */
export enum file_chunk_file_link_update_column {
  /** column name */
  chunk_id = 'chunk_id',
  /** column name */
  created_at = 'created_at',
  /** column name */
  file_id = 'file_id',
}

/** select columns of table "file_chunk" */
export enum file_chunk_select_column {
  /** column name */
  element_id = 'element_id',
  /** column name */
  embeddings = 'embeddings',
  /** column name */
  filetype = 'filetype',
  /** column name */
  id = 'id',
  /** column name */
  image_mime_type = 'image_mime_type',
  /** column name */
  is_extracted = 'is_extracted',
  /** column name */
  languages = 'languages',
  /** column name */
  layout_height = 'layout_height',
  /** column name */
  layout_width = 'layout_width',
  /** column name */
  orig_elements = 'orig_elements',
  /** column name */
  page_number = 'page_number',
  /** column name */
  points = 'points',
  /** column name */
  record_id = 'record_id',
  /** column name */
  record_locator = 'record_locator',
  /** column name */
  system = 'system',
  /** column name */
  text = 'text',
  /** column name */
  type = 'type',
  /** column name */
  url = 'url',
}

/** update columns of table "file_chunk" */
export enum file_chunk_update_column {
  /** column name */
  element_id = 'element_id',
  /** column name */
  embeddings = 'embeddings',
  /** column name */
  filetype = 'filetype',
  /** column name */
  id = 'id',
  /** column name */
  image_mime_type = 'image_mime_type',
  /** column name */
  is_extracted = 'is_extracted',
  /** column name */
  languages = 'languages',
  /** column name */
  layout_height = 'layout_height',
  /** column name */
  layout_width = 'layout_width',
  /** column name */
  orig_elements = 'orig_elements',
  /** column name */
  page_number = 'page_number',
  /** column name */
  points = 'points',
  /** column name */
  record_id = 'record_id',
  /** column name */
  record_locator = 'record_locator',
  /** column name */
  system = 'system',
  /** column name */
  text = 'text',
  /** column name */
  type = 'type',
  /** column name */
  url = 'url',
}

/** unique or primary key constraints on table "file" */
export enum file_constraint {
  /** unique or primary key constraint on columns "id" */
  file_pkey = 'file_pkey',
  /** unique or primary key constraint on columns "s3_key" */
  file_s3_key_key = 'file_s3_key_key',
}

/** unique or primary key constraints on table "file_folder_membership" */
export enum file_folder_membership_constraint {
  /** unique or primary key constraint on columns "folder_id", "file_id" */
  file_folder_membership_file_folder_unique = 'file_folder_membership_file_folder_unique',
  /** unique or primary key constraint on columns "file_id" */
  file_folder_membership_file_id_unique = 'file_folder_membership_file_id_unique',
  /** unique or primary key constraint on columns "id" */
  file_folder_membership_pkey = 'file_folder_membership_pkey',
}

/** select columns of table "file_folder_membership" */
export enum file_folder_membership_select_column {
  /** column name */
  created_at = 'created_at',
  /** column name */
  file_id = 'file_id',
  /** column name */
  folder_id = 'folder_id',
  /** column name */
  id = 'id',
}

/** update columns of table "file_folder_membership" */
export enum file_folder_membership_update_column {
  /** column name */
  created_at = 'created_at',
  /** column name */
  file_id = 'file_id',
  /** column name */
  folder_id = 'folder_id',
  /** column name */
  id = 'id',
}

/** select columns of table "file" */
export enum file_select_column {
  /** column name */
  created_at = 'created_at',
  /** column name */
  description = 'description',
  /** column name */
  file_storage_id = 'file_storage_id',
  /** column name */
  file_type_id = 'file_type_id',
  /** column name */
  id = 'id',
  /** column name */
  name = 'name',
  /** column name */
  s3_key = 's3_key',
  /** column name */
  uploading = 'uploading',
  /** column name */
  user_id = 'user_id',
}

/** unique or primary key constraints on table "file_storage" */
export enum file_storage_constraint {
  /** unique or primary key constraint on columns "id" */
  file_storage_pkey = 'file_storage_pkey',
}

export enum file_storage_enum {
  /** predium-batch-export */
  PREDIUM_BATCH_EXPORT = 'PREDIUM_BATCH_EXPORT',
  /** predium-cbi-inbox */
  PREDIUM_CBI_INBOX = 'PREDIUM_CBI_INBOX',
  /** predium-climate-risk-v2-api-response */
  PREDIUM_CLIMATE_RISK_V2_API_RESPONSE = 'PREDIUM_CLIMATE_RISK_V2_API_RESPONSE',
  /** predium-climate-risk-v2-api-response-bucket */
  PREDIUM_CLIMATE_RISK_V2_API_RESPONSE_BUCKET = 'PREDIUM_CLIMATE_RISK_V2_API_RESPONSE_BUCKET',
  /** predium-files */
  PREDIUM_FILES = 'PREDIUM_FILES',
  /** predium-staging-batch-export */
  PREDIUM_STAGING_BATCH_EXPORT = 'PREDIUM_STAGING_BATCH_EXPORT',
  /** predium-staging-cbi-inbox */
  PREDIUM_STAGING_CBI_INBOX = 'PREDIUM_STAGING_CBI_INBOX',
  /** predium-staging-climate-risk-v2-api-response-bucket */
  PREDIUM_STAGING_CLIMATE_RISK_V2_API_RESPONSE_BUCKET = 'PREDIUM_STAGING_CLIMATE_RISK_V2_API_RESPONSE_BUCKET',
  /** predium-staging-files */
  PREDIUM_STAGING_FILES = 'PREDIUM_STAGING_FILES',
  /** predium-test-cbi-inbox */
  PREDIUM_TEST_CBI_INBOX = 'PREDIUM_TEST_CBI_INBOX',
  /** predium-test-files */
  PREDIUM_TEST_FILES = 'PREDIUM_TEST_FILES',
  UNKNOWN = 'UNKNOWN',
}

/** select columns of table "file_storage" */
export enum file_storage_select_column {
  /** column name */
  id = 'id',
  /** column name */
  location = 'location',
}

/** update columns of table "file_storage" */
export enum file_storage_update_column {
  /** column name */
  id = 'id',
  /** column name */
  location = 'location',
}

/** unique or primary key constraints on table "file_type" */
export enum file_type_constraint {
  /** unique or primary key constraint on columns "id" */
  file_type_pkey = 'file_type_pkey',
}

export enum file_type_enum {
  ACTION_PLAN_PDF_EXPORT = 'ACTION_PLAN_PDF_EXPORT',
  ACTION_PLAN_PDF_EXPORT_LEGACY = 'ACTION_PLAN_PDF_EXPORT_LEGACY',
  BATCH_ESG_ANALYSIS_ZIP_EXPORT = 'BATCH_ESG_ANALYSIS_ZIP_EXPORT',
  BUILDING_CERTIFICATE = 'BUILDING_CERTIFICATE',
  BUILDING_EXCEL_EXPORT = 'BUILDING_EXCEL_EXPORT',
  BUILDING_EXCEL_EXPORT_LEGACY = 'BUILDING_EXCEL_EXPORT_LEGACY',
  CBI_EMAIL_ATTACHMENT_UNMATCHED = 'CBI_EMAIL_ATTACHMENT_UNMATCHED',
  CBI_INVOICE = 'CBI_INVOICE',
  CBI_ZIA_EXCEL_EXPORT = 'CBI_ZIA_EXCEL_EXPORT',
  CLIMATE_RISK_API_RESPONSE = 'CLIMATE_RISK_API_RESPONSE',
  CONSUMPTION_INVOICE = 'CONSUMPTION_INVOICE',
  CONSUMPTION_INVOICE_DEPRECATED = 'CONSUMPTION_INVOICE_DEPRECATED',
  DRIVE_CONSUMPTION_INVOICE = 'DRIVE_CONSUMPTION_INVOICE',
  DRIVE_CONTEXT_DOCUMENT = 'DRIVE_CONTEXT_DOCUMENT',
  DRIVE_EMISSION_CERTIFICATE = 'DRIVE_EMISSION_CERTIFICATE',
  DRIVE_ENERGY_CERTIFICATE = 'DRIVE_ENERGY_CERTIFICATE',
  DRIVE_USER_DOCUMENT = 'DRIVE_USER_DOCUMENT',
  EMAIL_ATTACHMENT_IMAGE = 'EMAIL_ATTACHMENT_IMAGE',
  EMISSION_CERTIFICATE = 'EMISSION_CERTIFICATE',
  ENERGY_CERTIFICATE = 'ENERGY_CERTIFICATE',
  ONE_PAGER_PDF_EXPORT = 'ONE_PAGER_PDF_EXPORT',
  ONE_PAGER_PDF_EXPORT_LEGACY = 'ONE_PAGER_PDF_EXPORT_LEGACY',
  ORG_LOGO = 'ORG_LOGO',
  REPORT_EVIDENCE = 'REPORT_EVIDENCE',
  REPORT_PDF_EXPORT = 'REPORT_PDF_EXPORT',
  SCENARIO_EXCEL_EXPORT = 'SCENARIO_EXCEL_EXPORT',
  SCENARIO_EXCEL_EXPORT_LEGACY = 'SCENARIO_EXCEL_EXPORT_LEGACY',
  UNKNOWN = 'UNKNOWN',
}

/** select columns of table "file_type" */
export enum file_type_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "file_type" */
export enum file_type_update_column {
  /** column name */
  id = 'id',
}

/** update columns of table "file" */
export enum file_update_column {
  /** column name */
  created_at = 'created_at',
  /** column name */
  description = 'description',
  /** column name */
  file_storage_id = 'file_storage_id',
  /** column name */
  file_type_id = 'file_type_id',
  /** column name */
  id = 'id',
  /** column name */
  name = 'name',
  /** column name */
  s3_key = 's3_key',
  /** column name */
  uploading = 'uploading',
  /** column name */
  user_id = 'user_id',
}

/** unique or primary key constraints on table "folder" */
export enum folder_constraint {
  /** unique or primary key constraint on columns "building_id" */
  folder_building_id_unique = 'folder_building_id_unique',
  /** unique or primary key constraint on columns "economic_unit_id" */
  folder_economic_unit_id_unique = 'folder_economic_unit_id_unique',
  /** unique or primary key constraint on columns "org_id" */
  folder_org_root_unique = 'folder_org_root_unique',
  /** unique or primary key constraint on columns "folder_type_id", "parent_id" */
  folder_parent_type_system_unique = 'folder_parent_type_system_unique',
  /** unique or primary key constraint on columns "id" */
  folder_pkey = 'folder_pkey',
  /** unique or primary key constraint on columns "portfolio_id" */
  folder_portfolio_id_unique = 'folder_portfolio_id_unique',
}

/** select columns of table "folder" */
export enum folder_select_column {
  /** column name */
  building_id = 'building_id',
  /** column name */
  created_at = 'created_at',
  /** column name */
  created_by = 'created_by',
  /** column name */
  economic_unit_id = 'economic_unit_id',
  /** column name */
  folder_type_id = 'folder_type_id',
  /** column name */
  id = 'id',
  /** column name */
  name = 'name',
  /** column name */
  org_id = 'org_id',
  /** column name */
  parent_id = 'parent_id',
  /** column name */
  portfolio_id = 'portfolio_id',
  /** column name */
  updated_at = 'updated_at',
}

/** unique or primary key constraints on table "folder_type" */
export enum folder_type_constraint {
  /** unique or primary key constraint on columns "id" */
  folder_type_pkey = 'folder_type_pkey',
}

export enum folder_type_enum {
  BUILDING = 'BUILDING',
  CONSUMPTION_INVOICES = 'CONSUMPTION_INVOICES',
  ECONOMIC_UNIT = 'ECONOMIC_UNIT',
  EMISSION_CERTIFICATES = 'EMISSION_CERTIFICATES',
  ENERGY_CERTIFICATES = 'ENERGY_CERTIFICATES',
  PORTFOLIO = 'PORTFOLIO',
  PORTFOLIOS = 'PORTFOLIOS',
  ROOT = 'ROOT',
  USER_CREATED = 'USER_CREATED',
}

/** select columns of table "folder_type" */
export enum folder_type_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "folder_type" */
export enum folder_type_update_column {
  /** column name */
  id = 'id',
}

/** update columns of table "folder" */
export enum folder_update_column {
  /** column name */
  building_id = 'building_id',
  /** column name */
  created_at = 'created_at',
  /** column name */
  created_by = 'created_by',
  /** column name */
  economic_unit_id = 'economic_unit_id',
  /** column name */
  folder_type_id = 'folder_type_id',
  /** column name */
  id = 'id',
  /** column name */
  name = 'name',
  /** column name */
  org_id = 'org_id',
  /** column name */
  parent_id = 'parent_id',
  /** column name */
  portfolio_id = 'portfolio_id',
  /** column name */
  updated_at = 'updated_at',
}

/** select columns of table "inactive_buildings" */
export enum inactive_buildings_select_column {
  /** column name */
  active_building_model_id = 'active_building_model_id',
  /** column name */
  address_id = 'address_id',
  /** column name */
  building_state_id = 'building_state_id',
  /** column name */
  created_at = 'created_at',
  /** column name */
  customer_external_identifier = 'customer_external_identifier',
  /** column name */
  economic_unit_id = 'economic_unit_id',
  /** column name */
  heritage_district = 'heritage_district',
  /** column name */
  id = 'id',
  /** column name */
  leasehold = 'leasehold',
  /** column name */
  milieu_protection = 'milieu_protection',
  /** column name */
  monument_protection = 'monument_protection',
  /** column name */
  responsible_user_id = 'responsible_user_id',
  /** column name */
  state = 'state',
  /** column name */
  updated_at = 'updated_at',
  /** column name */
  year_constructed = 'year_constructed',
}

/** select "inactive_buildings_aggregate_bool_exp_bool_and_arguments_columns" columns of table "inactive_buildings" */
export enum inactive_buildings_select_column_inactive_buildings_aggregate_bool_exp_bool_and_arguments_columns {
  /** column name */
  heritage_district = 'heritage_district',
  /** column name */
  leasehold = 'leasehold',
  /** column name */
  milieu_protection = 'milieu_protection',
  /** column name */
  monument_protection = 'monument_protection',
}

/** select "inactive_buildings_aggregate_bool_exp_bool_or_arguments_columns" columns of table "inactive_buildings" */
export enum inactive_buildings_select_column_inactive_buildings_aggregate_bool_exp_bool_or_arguments_columns {
  /** column name */
  heritage_district = 'heritage_district',
  /** column name */
  leasehold = 'leasehold',
  /** column name */
  milieu_protection = 'milieu_protection',
  /** column name */
  monument_protection = 'monument_protection',
}

/** unique or primary key constraints on table "insulation_material_category" */
export enum insulation_material_category_constraint {
  /** unique or primary key constraint on columns "id" */
  insulation_material_category_pkey = 'insulation_material_category_pkey',
}

export enum insulation_material_category_enum {
  MINERAL = 'MINERAL',
  MIXED = 'MIXED',
  RENEWABLE = 'RENEWABLE',
  SYNTHETIC = 'SYNTHETIC',
}

/** select columns of table "insulation_material_category" */
export enum insulation_material_category_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "insulation_material_category" */
export enum insulation_material_category_update_column {
  /** column name */
  id = 'id',
}

/** unique or primary key constraints on table "insulation_method" */
export enum insulation_method_constraint {
  /** unique or primary key constraint on columns "id" */
  insulation_method_pkey = 'insulation_method_pkey',
}

export enum insulation_method_enum {
  ABOVE_RAFTER = 'ABOVE_RAFTER',
  BELOW_RAFTER = 'BELOW_RAFTER',
  BETWEEN_RAFTER = 'BETWEEN_RAFTER',
  BLOW_IN_INSULATION = 'BLOW_IN_INSULATION',
  COLD_ROOF_BETWEEN_RAFTER = 'COLD_ROOF_BETWEEN_RAFTER',
  CORE_INSULATION_BOARDS = 'CORE_INSULATION_BOARDS',
  FACING_SHELL = 'FACING_SHELL',
  FROM_BELOW = 'FROM_BELOW',
  INSULATION_BOARDS = 'INSULATION_BOARDS',
  INSULATION_BOARDS_WALKABLE = 'INSULATION_BOARDS_WALKABLE',
  INSULATION_BOARDS_WITHOUT_CLADDING = 'INSULATION_BOARDS_WITHOUT_CLADDING',
  INSULATION_BOARDS_WITH_CLADDING = 'INSULATION_BOARDS_WITH_CLADDING',
  INSULATION_GRADIENT = 'INSULATION_GRADIENT',
  INSULATION_MATS = 'INSULATION_MATS',
  INTERIOR_PLASTER_SYSTEM = 'INTERIOR_PLASTER_SYSTEM',
  INVERTED_ROOF = 'INVERTED_ROOF',
  ON_RAFTER = 'ON_RAFTER',
  VHF = 'VHF',
  WARM_ROOF = 'WARM_ROOF',
  WDVS = 'WDVS',
}

/** select columns of table "insulation_method" */
export enum insulation_method_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "insulation_method" */
export enum insulation_method_update_column {
  /** column name */
  id = 'id',
}

/** unique or primary key constraints on table "invoice_payer_type" */
export enum invoice_payer_type_constraint {
  /** unique or primary key constraint on columns "id" */
  invoice_payer_type_pkey = 'invoice_payer_type_pkey',
}

export enum invoice_payer_type_enum {
  OWNER = 'OWNER',
  TENANT = 'TENANT',
}

/** select columns of table "invoice_payer_type" */
export enum invoice_payer_type_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "invoice_payer_type" */
export enum invoice_payer_type_update_column {
  /** column name */
  id = 'id',
}

/** unique or primary key constraints on table "language" */
export enum language_constraint {
  /** unique or primary key constraint on columns "id" */
  language_pkey = 'language_pkey',
}

export enum language_enum {
  /** the main language and fallback of the application */
  deDE = 'deDE',
  enGB = 'enGB',
}

/** select columns of table "language" */
export enum language_select_column {
  /** column name */
  description = 'description',
  /** column name */
  id = 'id',
}

/** update columns of table "language" */
export enum language_update_column {
  /** column name */
  description = 'description',
  /** column name */
  id = 'id',
}

/** unique or primary key constraints on table "main_energy_source_type" */
export enum main_energy_source_type_constraint {
  /** unique or primary key constraint on columns "id" */
  main_energy_source_type_pkey = 'main_energy_source_type_pkey',
}

export enum main_energy_source_type_enum {
  DISTRICT_HEATING = 'DISTRICT_HEATING',
  ELECTRICITY = 'ELECTRICITY',
  GAS = 'GAS',
  OIL = 'OIL',
  OTHER = 'OTHER',
}

/** select columns of table "main_energy_source_type" */
export enum main_energy_source_type_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "main_energy_source_type" */
export enum main_energy_source_type_update_column {
  /** column name */
  id = 'id',
}

/** unique or primary key constraints on table "module_config" */
export enum module_config_constraint {
  /** unique or primary key constraint on columns "org_contract_id", "module" */
  module_config_org_contract_id_module_key = 'module_config_org_contract_id_module_key',
  /** unique or primary key constraint on columns "id" */
  module_config_pkey = 'module_config_pkey',
}

/** select columns of table "module_config" */
export enum module_config_select_column {
  /** column name */
  acquisition_window_months = 'acquisition_window_months',
  /** column name */
  activated_at = 'activated_at',
  /** column name */
  area_type_id = 'area_type_id',
  /** column name */
  cadence = 'cadence',
  /** column name */
  carry_over_enabled = 'carry_over_enabled',
  /** column name */
  created_at = 'created_at',
  /** column name */
  deactivated_at = 'deactivated_at',
  /** column name */
  id = 'id',
  /** column name */
  limit_value = 'limit_value',
  /** column name */
  metric = 'metric',
  /** column name */
  module = 'module',
  /** column name */
  org_contract_id = 'org_contract_id',
  /** column name */
  updated_at = 'updated_at',
}

/** select "module_config_aggregate_bool_exp_bool_and_arguments_columns" columns of table "module_config" */
export enum module_config_select_column_module_config_aggregate_bool_exp_bool_and_arguments_columns {
  /** column name */
  carry_over_enabled = 'carry_over_enabled',
}

/** select "module_config_aggregate_bool_exp_bool_or_arguments_columns" columns of table "module_config" */
export enum module_config_select_column_module_config_aggregate_bool_exp_bool_or_arguments_columns {
  /** column name */
  carry_over_enabled = 'carry_over_enabled',
}

/** update columns of table "module_config" */
export enum module_config_update_column {
  /** column name */
  acquisition_window_months = 'acquisition_window_months',
  /** column name */
  activated_at = 'activated_at',
  /** column name */
  area_type_id = 'area_type_id',
  /** column name */
  cadence = 'cadence',
  /** column name */
  carry_over_enabled = 'carry_over_enabled',
  /** column name */
  created_at = 'created_at',
  /** column name */
  deactivated_at = 'deactivated_at',
  /** column name */
  id = 'id',
  /** column name */
  limit_value = 'limit_value',
  /** column name */
  metric = 'metric',
  /** column name */
  module = 'module',
  /** column name */
  org_contract_id = 'org_contract_id',
  /** column name */
  updated_at = 'updated_at',
}

/** unique or primary key constraints on table "notification" */
export enum notification_constraint {
  /** unique or primary key constraint on columns "id" */
  notification_pkey = 'notification_pkey',
}

/** select columns of table "notification" */
export enum notification_select_column {
  /** column name */
  created_at = 'created_at',
  /** column name */
  id = 'id',
  /** column name */
  read = 'read',
  /** column name */
  template_id = 'template_id',
  /** column name */
  updated_at = 'updated_at',
  /** column name */
  user_id = 'user_id',
}

/** select "notification_aggregate_bool_exp_bool_and_arguments_columns" columns of table "notification" */
export enum notification_select_column_notification_aggregate_bool_exp_bool_and_arguments_columns {
  /** column name */
  read = 'read',
}

/** select "notification_aggregate_bool_exp_bool_or_arguments_columns" columns of table "notification" */
export enum notification_select_column_notification_aggregate_bool_exp_bool_or_arguments_columns {
  /** column name */
  read = 'read',
}

/** unique or primary key constraints on table "notification_template" */
export enum notification_template_constraint {
  /** unique or primary key constraint on columns "id" */
  notification_template_pkey = 'notification_template_pkey',
}

/** select columns of table "notification_template" */
export enum notification_template_select_column {
  /** column name */
  created_at = 'created_at',
  /** column name */
  description = 'description',
  /** column name */
  href = 'href',
  /** column name */
  id = 'id',
  /** column name */
  org_id = 'org_id',
  /** column name */
  title = 'title',
  /** column name */
  type = 'type',
  /** column name */
  updated_at = 'updated_at',
}

/** update columns of table "notification_template" */
export enum notification_template_update_column {
  /** column name */
  created_at = 'created_at',
  /** column name */
  description = 'description',
  /** column name */
  href = 'href',
  /** column name */
  id = 'id',
  /** column name */
  org_id = 'org_id',
  /** column name */
  title = 'title',
  /** column name */
  type = 'type',
  /** column name */
  updated_at = 'updated_at',
}

/** unique or primary key constraints on table "notification_type" */
export enum notification_type_constraint {
  /** unique or primary key constraint on columns "id" */
  notification_type_pkey = 'notification_type_pkey',
}

export enum notification_type_enum {
  /** notification type for all users */
  GLOBAL_NOTIFICATION = 'GLOBAL_NOTIFICATION',
  /** notification type specific to a whole organisation */
  ORG_NOTIFICATION = 'ORG_NOTIFICATION',
  /** notification type specific to a single user */
  USER_NOTIFICATION = 'USER_NOTIFICATION',
}

/** select columns of table "notification_type" */
export enum notification_type_select_column {
  /** column name */
  description = 'description',
  /** column name */
  id = 'id',
}

/** update columns of table "notification_type" */
export enum notification_type_update_column {
  /** column name */
  description = 'description',
  /** column name */
  id = 'id',
}

/** update columns of table "notification" */
export enum notification_update_column {
  /** column name */
  created_at = 'created_at',
  /** column name */
  id = 'id',
  /** column name */
  read = 'read',
  /** column name */
  template_id = 'template_id',
  /** column name */
  updated_at = 'updated_at',
  /** column name */
  user_id = 'user_id',
}

/** column ordering options */
export enum order_by {
  /** in ascending order, nulls last */
  asc = 'asc',
  /** in ascending order, nulls first */
  asc_nulls_first = 'asc_nulls_first',
  /** in ascending order, nulls last */
  asc_nulls_last = 'asc_nulls_last',
  /** in descending order, nulls first */
  desc = 'desc',
  /** in descending order, nulls first */
  desc_nulls_first = 'desc_nulls_first',
  /** in descending order, nulls last */
  desc_nulls_last = 'desc_nulls_last',
}

/** unique or primary key constraints on table "org" */
export enum org_constraint {
  /** unique or primary key constraint on columns "logo_file_id" */
  org_logo_file_id_key = 'org_logo_file_id_key',
  /** unique or primary key constraint on columns "id" */
  org_pkey = 'org_pkey',
}

/** unique or primary key constraints on table "org_contract" */
export enum org_contract_constraint {
  /** unique or primary key constraint on columns "id" */
  org_contract_pkey = 'org_contract_pkey',
}

/** select columns of table "org_contract" */
export enum org_contract_select_column {
  /** column name */
  created_at = 'created_at',
  /** column name */
  ends_at = 'ends_at',
  /** column name */
  id = 'id',
  /** column name */
  org_id = 'org_id',
  /** column name */
  starts_at = 'starts_at',
  /** column name */
  updated_at = 'updated_at',
}

/** update columns of table "org_contract" */
export enum org_contract_update_column {
  /** column name */
  created_at = 'created_at',
  /** column name */
  ends_at = 'ends_at',
  /** column name */
  id = 'id',
  /** column name */
  org_id = 'org_id',
  /** column name */
  starts_at = 'starts_at',
  /** column name */
  updated_at = 'updated_at',
}

/** unique or primary key constraints on table "org_preference" */
export enum org_preference_constraint {
  /** unique or primary key constraint on columns "crrem_config_id", "org_id" */
  org_preference_org_id_crrem_config_id_key = 'org_preference_org_id_crrem_config_id_key',
  /** unique or primary key constraint on columns "org_id" */
  org_preference_org_id_key = 'org_preference_org_id_key',
  /** unique or primary key constraint on columns "id" */
  org_preference_pkey = 'org_preference_pkey',
}

/** select columns of table "org_preference" */
export enum org_preference_select_column {
  /** column name */
  automatic_action_cost_update_enabled = 'automatic_action_cost_update_enabled',
  /** column name */
  co2_price_anchor_prices = 'co2_price_anchor_prices',
  /** column name */
  co2_price_anchor_years = 'co2_price_anchor_years',
  /** column name */
  co2_price_curve_kind_id = 'co2_price_curve_kind_id',
  /** column name */
  co2_price_growth_annual_percent = 'co2_price_growth_annual_percent',
  /** column name */
  co2_price_growth_start_value = 'co2_price_growth_start_value',
  /** column name */
  crrem_config_id = 'crrem_config_id',
  /** column name */
  crrem_version = 'crrem_version',
  /** column name */
  economic_unit_enabled = 'economic_unit_enabled',
  /** column name */
  economic_unit_in_file_drive_enabled = 'economic_unit_in_file_drive_enabled',
  /** column name */
  emission_factor_type_id = 'emission_factor_type_id',
  /** column name */
  eu_taxonomy_datasource_id = 'eu_taxonomy_datasource_id',
  /** column name */
  id = 'id',
  /** column name */
  org_id = 'org_id',
  /** column name */
  sso_auto_create_user = 'sso_auto_create_user',
  /** column name */
  sso_connection_name = 'sso_connection_name',
  /** column name */
  user_logout_idle_timeout = 'user_logout_idle_timeout',
}

/** update columns of table "org_preference" */
export enum org_preference_update_column {
  /** column name */
  automatic_action_cost_update_enabled = 'automatic_action_cost_update_enabled',
  /** column name */
  co2_price_anchor_prices = 'co2_price_anchor_prices',
  /** column name */
  co2_price_anchor_years = 'co2_price_anchor_years',
  /** column name */
  co2_price_curve_kind_id = 'co2_price_curve_kind_id',
  /** column name */
  co2_price_growth_annual_percent = 'co2_price_growth_annual_percent',
  /** column name */
  co2_price_growth_start_value = 'co2_price_growth_start_value',
  /** column name */
  crrem_config_id = 'crrem_config_id',
  /** column name */
  crrem_version = 'crrem_version',
  /** column name */
  economic_unit_enabled = 'economic_unit_enabled',
  /** column name */
  economic_unit_in_file_drive_enabled = 'economic_unit_in_file_drive_enabled',
  /** column name */
  emission_factor_type_id = 'emission_factor_type_id',
  /** column name */
  eu_taxonomy_datasource_id = 'eu_taxonomy_datasource_id',
  /** column name */
  id = 'id',
  /** column name */
  org_id = 'org_id',
  /** column name */
  sso_auto_create_user = 'sso_auto_create_user',
  /** column name */
  sso_connection_name = 'sso_connection_name',
  /** column name */
  user_logout_idle_timeout = 'user_logout_idle_timeout',
}

/** unique or primary key constraints on table "org_sector_type" */
export enum org_sector_type_constraint {
  /** unique or primary key constraint on columns "id" */
  org_sector_type_pkey = 'org_sector_type_pkey',
}

export enum org_sector_type_enum {
  BANKING = 'BANKING',
  STANDARD = 'STANDARD',
}

/** select columns of table "org_sector_type" */
export enum org_sector_type_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "org_sector_type" */
export enum org_sector_type_update_column {
  /** column name */
  id = 'id',
}

/** select columns of table "org" */
export enum org_select_column {
  /** column name */
  created_at = 'created_at',
  /** column name */
  id = 'id',
  /** column name */
  logo_file_id = 'logo_file_id',
  /** column name */
  name = 'name',
  /** column name */
  org_sector_type_id = 'org_sector_type_id',
  /** column name */
  org_type_id = 'org_type_id',
  /** column name */
  status_type_id = 'status_type_id',
  /** column name */
  updated_at = 'updated_at',
}

/** unique or primary key constraints on table "org_status_type" */
export enum org_status_type_constraint {
  /** unique or primary key constraint on columns "id" */
  org_status_type_pkey = 'org_status_type_pkey',
}

export enum org_status_type_enum {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}

/** select columns of table "org_status_type" */
export enum org_status_type_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "org_status_type" */
export enum org_status_type_update_column {
  /** column name */
  id = 'id',
}

/** unique or primary key constraints on table "org_type" */
export enum org_type_constraint {
  /** unique or primary key constraint on columns "id" */
  org_type_pkey = 'org_type_pkey',
}

export enum org_type_enum {
  CUSTOMER = 'CUSTOMER',
  DEMO = 'DEMO',
  INTERNAL = 'INTERNAL',
  TEMPLATE = 'TEMPLATE',
}

/** select columns of table "org_type" */
export enum org_type_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "org_type" */
export enum org_type_update_column {
  /** column name */
  id = 'id',
}

/** update columns of table "org" */
export enum org_update_column {
  /** column name */
  created_at = 'created_at',
  /** column name */
  id = 'id',
  /** column name */
  logo_file_id = 'logo_file_id',
  /** column name */
  name = 'name',
  /** column name */
  org_sector_type_id = 'org_sector_type_id',
  /** column name */
  org_type_id = 'org_type_id',
  /** column name */
  status_type_id = 'status_type_id',
  /** column name */
  updated_at = 'updated_at',
}

/** unique or primary key constraints on table "portfolio_analysis_billed_event" */
export enum portfolio_analysis_billed_event_constraint {
  /** unique or primary key constraint on columns "id" */
  portfolio_analysis_billed_event_pkey = 'portfolio_analysis_billed_event_pkey',
  /** unique or primary key constraint on columns "billed_building_id" */
  uq_portfolio_analysis_billed_event_building_added = 'uq_portfolio_analysis_billed_event_building_added',
  /** unique or primary key constraint on columns "source_event_id" */
  uq_portfolio_analysis_billed_event_building_converted = 'uq_portfolio_analysis_billed_event_building_converted',
  /** unique or primary key constraint on columns "billed_building_id" */
  uq_portfolio_analysis_billed_event_building_removed = 'uq_portfolio_analysis_billed_event_building_removed',
  /** unique or primary key constraint on columns "source_event_id" */
  uq_portfolio_analysis_billed_event_inventory_transition = 'uq_portfolio_analysis_billed_event_inventory_transition',
}

/** select columns of table "portfolio_analysis_billed_event" */
export enum portfolio_analysis_billed_event_select_column {
  /** column name */
  area_type_id = 'area_type_id',
  /** column name */
  billed_building_id = 'billed_building_id',
  /** column name */
  billing_period_id = 'billing_period_id',
  /** column name */
  building_id = 'building_id',
  /** column name */
  created_at = 'created_at',
  /** column name */
  delta = 'delta',
  /** column name */
  event_type = 'event_type',
  /** column name */
  id = 'id',
  /** column name */
  metric = 'metric',
  /** column name */
  module_config_id = 'module_config_id',
  /** column name */
  org_id = 'org_id',
  /** column name */
  source_event_id = 'source_event_id',
  /** column name */
  status = 'status',
}

/** update columns of table "portfolio_analysis_billed_event" */
export enum portfolio_analysis_billed_event_update_column {
  /** column name */
  area_type_id = 'area_type_id',
  /** column name */
  billed_building_id = 'billed_building_id',
  /** column name */
  billing_period_id = 'billing_period_id',
  /** column name */
  building_id = 'building_id',
  /** column name */
  created_at = 'created_at',
  /** column name */
  delta = 'delta',
  /** column name */
  event_type = 'event_type',
  /** column name */
  id = 'id',
  /** column name */
  metric = 'metric',
  /** column name */
  module_config_id = 'module_config_id',
  /** column name */
  org_id = 'org_id',
  /** column name */
  source_event_id = 'source_event_id',
  /** column name */
  status = 'status',
}

/** unique or primary key constraints on table "portfolio_analysis_event_type" */
export enum portfolio_analysis_event_type_constraint {
  /** unique or primary key constraint on columns "id" */
  portfolio_analysis_event_type_pkey = 'portfolio_analysis_event_type_pkey',
}

export enum portfolio_analysis_event_type_enum {
  AREA_ADJUSTED = 'AREA_ADJUSTED',
  BUILDING_ADDED_TO_INVENTORY = 'BUILDING_ADDED_TO_INVENTORY',
  BUILDING_CONVERTED_TO_ACQUISITION = 'BUILDING_CONVERTED_TO_ACQUISITION',
  BUILDING_CONVERTED_TO_INVENTORY = 'BUILDING_CONVERTED_TO_INVENTORY',
  BUILDING_DEACTIVATED = 'BUILDING_DEACTIVATED',
  BUILDING_REACTIVATED = 'BUILDING_REACTIVATED',
  BUILDING_REMOVED_FROM_INVENTORY = 'BUILDING_REMOVED_FROM_INVENTORY',
  RESIDENTIAL_UNITS_ADJUSTED = 'RESIDENTIAL_UNITS_ADJUSTED',
}

/** select columns of table "portfolio_analysis_event_type" */
export enum portfolio_analysis_event_type_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "portfolio_analysis_event_type" */
export enum portfolio_analysis_event_type_update_column {
  /** column name */
  id = 'id',
}

/** unique or primary key constraints on table "portfolio" */
export enum portfolio_constraint {
  /** unique or primary key constraint on columns "id" */
  portfolio_pkey = 'portfolio_pkey',
}

/** select columns of table "portfolio" */
export enum portfolio_select_column {
  /** column name */
  created_at = 'created_at',
  /** column name */
  crrem_config_id = 'crrem_config_id',
  /** column name */
  emission_factor_type_id = 'emission_factor_type_id',
  /** column name */
  id = 'id',
  /** column name */
  name = 'name',
  /** column name */
  org_id = 'org_id',
  /** column name */
  updated_at = 'updated_at',
}

/** update columns of table "portfolio" */
export enum portfolio_update_column {
  /** column name */
  created_at = 'created_at',
  /** column name */
  crrem_config_id = 'crrem_config_id',
  /** column name */
  emission_factor_type_id = 'emission_factor_type_id',
  /** column name */
  id = 'id',
  /** column name */
  name = 'name',
  /** column name */
  org_id = 'org_id',
  /** column name */
  updated_at = 'updated_at',
}

/** Status of the queue */
export enum queue_status_enum {
  PAUSE = 'PAUSE',
  RESUME = 'RESUME',
}

/** Sorting options for recommended actions */
export enum recommended_action_sorting_enum {
  CO2 = 'CO2',
  COST_CO2 = 'COST_CO2',
  COST_FINAL_ENERGY = 'COST_FINAL_ENERGY',
  COST_PRIMARY_ENERGY = 'COST_PRIMARY_ENERGY',
  FINAL_ENERGY = 'FINAL_ENERGY',
  PRIMARY_ENERGY = 'PRIMARY_ENERGY',
}

/** Renovation cost category options */
export enum renovation_category_enum {
  CUSTOM = 'CUSTOM',
  ENERGY_SOURCE = 'ENERGY_SOURCE',
  ENVELOPE = 'ENVELOPE',
  TECHNOLOGY = 'TECHNOLOGY',
}

/** unique or primary key constraints on table "renovation" */
export enum renovation_constraint {
  /** unique or primary key constraint on columns "id" */
  renovation_pkey = 'renovation_pkey',
}

/** select columns of table "renovation_energy_consumer" */
export enum renovation_energy_consumer_select_column {
  /** column name */
  action_id = 'action_id',
  /** column name */
  id = 'id',
  /** column name */
  new_entity_id = 'new_entity_id',
  /** column name */
  old_entity_id = 'old_entity_id',
  /** column name */
  order = 'order',
  /** column name */
  renovation_type_id = 'renovation_type_id',
}

/** select columns of table "renovation_energy_producer" */
export enum renovation_energy_producer_select_column {
  /** column name */
  action_id = 'action_id',
  /** column name */
  id = 'id',
  /** column name */
  new_entity_id = 'new_entity_id',
  /** column name */
  old_entity_id = 'old_entity_id',
  /** column name */
  order = 'order',
  /** column name */
  renovation_type_id = 'renovation_type_id',
}

/** select columns of table "renovation_energy_system_route" */
export enum renovation_energy_system_route_select_column {
  /** column name */
  action_id = 'action_id',
  /** column name */
  id = 'id',
  /** column name */
  new_entity_id = 'new_entity_id',
  /** column name */
  old_entity_id = 'old_entity_id',
  /** column name */
  order = 'order',
  /** column name */
  renovation_type_id = 'renovation_type_id',
}

/** select columns of table "renovation_envelope" */
export enum renovation_envelope_select_column {
  /** column name */
  action_id = 'action_id',
  /** column name */
  id = 'id',
  /** column name */
  new_entity_id = 'new_entity_id',
  /** column name */
  old_entity_id = 'old_entity_id',
  /** column name */
  order = 'order',
  /** column name */
  renovation_type_id = 'renovation_type_id',
}

/** select columns of table "renovation" */
export enum renovation_select_column {
  /** column name */
  action_id = 'action_id',
  /** column name */
  cost_total = 'cost_total',
  /** column name */
  cost_total_custom = 'cost_total_custom',
  /** column name */
  cost_total_predium = 'cost_total_predium',
  /** column name */
  id = 'id',
  /** column name */
  new_entity_id = 'new_entity_id',
  /** column name */
  old_entity_id = 'old_entity_id',
  /** column name */
  order = 'order',
  /** column name */
  renovation_type_id = 'renovation_type_id',
  /** column name */
  use_custom_cost = 'use_custom_cost',
}

/** select "renovation_aggregate_bool_exp_bool_and_arguments_columns" columns of table "renovation" */
export enum renovation_select_column_renovation_aggregate_bool_exp_bool_and_arguments_columns {
  /** column name */
  use_custom_cost = 'use_custom_cost',
}

/** select "renovation_aggregate_bool_exp_bool_or_arguments_columns" columns of table "renovation" */
export enum renovation_select_column_renovation_aggregate_bool_exp_bool_or_arguments_columns {
  /** column name */
  use_custom_cost = 'use_custom_cost',
}

/** unique or primary key constraints on table "renovation_type" */
export enum renovation_type_constraint {
  /** unique or primary key constraint on columns "id" */
  renovation_type_pkey = 'renovation_type_pkey',
}

export enum renovation_type_enum {
  CREATE_VENTILATION = 'CREATE_VENTILATION',
  CUSTOM = 'CUSTOM',
  ENERGY_CONSUMER = 'ENERGY_CONSUMER',
  ENERGY_SYSTEM_ROUTE = 'ENERGY_SYSTEM_ROUTE',
  ENVELOPE = 'ENVELOPE',
  EXCLUSION_NOTE = 'EXCLUSION_NOTE',
  EXISTING_VENTILATION = 'EXISTING_VENTILATION',
  HYDRAULIC_BALANCING = 'HYDRAULIC_BALANCING',
  MAINTENANCE = 'MAINTENANCE',
  SOLAR_PLANT = 'SOLAR_PLANT',
}

/** select columns of table "renovation_type" */
export enum renovation_type_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "renovation_type" */
export enum renovation_type_update_column {
  /** column name */
  id = 'id',
}

/** update columns of table "renovation" */
export enum renovation_update_column {
  /** column name */
  action_id = 'action_id',
  /** column name */
  cost_total_custom = 'cost_total_custom',
  /** column name */
  cost_total_predium = 'cost_total_predium',
  /** column name */
  id = 'id',
  /** column name */
  new_entity_id = 'new_entity_id',
  /** column name */
  old_entity_id = 'old_entity_id',
  /** column name */
  order = 'order',
  /** column name */
  renovation_type_id = 'renovation_type_id',
  /** column name */
  use_custom_cost = 'use_custom_cost',
}

/** unique or primary key constraints on table "report_answer_autofill_option" */
export enum report_answer_autofill_option_constraint {
  /** unique or primary key constraint on columns "report_template_question_option_id", "report_answer_id" */
  report_answer_autofill_option_pkey = 'report_answer_autofill_option_pkey',
}

/** select columns of table "report_answer_autofill_option" */
export enum report_answer_autofill_option_select_column {
  /** column name */
  report_answer_id = 'report_answer_id',
  /** column name */
  report_template_question_option_id = 'report_template_question_option_id',
}

/** update columns of table "report_answer_autofill_option" */
export enum report_answer_autofill_option_update_column {
  /** column name */
  report_answer_id = 'report_answer_id',
  /** column name */
  report_template_question_option_id = 'report_template_question_option_id',
}

/** unique or primary key constraints on table "report_answer_chosen_option" */
export enum report_answer_chosen_option_constraint {
  /** unique or primary key constraint on columns "report_template_question_option_id", "report_answer_id" */
  report_answer_chosen_option_pkey = 'report_answer_chosen_option_pkey',
}

/** select columns of table "report_answer_chosen_option" */
export enum report_answer_chosen_option_select_column {
  /** column name */
  created_at = 'created_at',
  /** column name */
  report_answer_id = 'report_answer_id',
  /** column name */
  report_template_question_option_id = 'report_template_question_option_id',
  /** column name */
  updated_at = 'updated_at',
}

/** update columns of table "report_answer_chosen_option" */
export enum report_answer_chosen_option_update_column {
  /** column name */
  created_at = 'created_at',
  /** column name */
  report_answer_id = 'report_answer_id',
  /** column name */
  report_template_question_option_id = 'report_template_question_option_id',
  /** column name */
  updated_at = 'updated_at',
}

/** unique or primary key constraints on table "report_answer" */
export enum report_answer_constraint {
  /** unique or primary key constraint on columns "id" */
  report_answer_pkey = 'report_answer_pkey',
}

/** select columns of table "report_answer" */
export enum report_answer_select_column {
  /** column name */
  autofill = 'autofill',
  /** column name */
  building_id = 'building_id',
  /** column name */
  created_at = 'created_at',
  /** column name */
  id = 'id',
  /** column name */
  report_id = 'report_id',
  /** column name */
  report_question_id = 'report_question_id',
  /** column name */
  state = 'state',
  /** column name */
  updated_at = 'updated_at',
  /** column name */
  value = 'value',
}

/** unique or primary key constraints on table "report_answer_state" */
export enum report_answer_state_constraint {
  /** unique or primary key constraint on columns "id" */
  report_answer_state_pkey = 'report_answer_state_pkey',
}

export enum report_answer_state_enum {
  ANSWERED = 'ANSWERED',
  REVIEWED = 'REVIEWED',
  UNANSWERED = 'UNANSWERED',
}

/** select columns of table "report_answer_state" */
export enum report_answer_state_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "report_answer_state" */
export enum report_answer_state_update_column {
  /** column name */
  id = 'id',
}

/** update columns of table "report_answer" */
export enum report_answer_update_column {
  /** column name */
  autofill = 'autofill',
  /** column name */
  building_id = 'building_id',
  /** column name */
  created_at = 'created_at',
  /** column name */
  id = 'id',
  /** column name */
  report_id = 'report_id',
  /** column name */
  report_question_id = 'report_question_id',
  /** column name */
  state = 'state',
  /** column name */
  updated_at = 'updated_at',
  /** column name */
  value = 'value',
}

/** unique or primary key constraints on table "report_building" */
export enum report_building_constraint {
  /** unique or primary key constraint on columns "report_id", "building_id" */
  report_building_pkey = 'report_building_pkey',
}

/** select columns of table "report_building_details" */
export enum report_building_details_select_column {
  /** column name */
  building_id = 'building_id',
  /** column name */
  org_id = 'org_id',
  /** column name */
  portfolio_id = 'portfolio_id',
  /** column name */
  portfolio_name = 'portfolio_name',
  /** column name */
  report_id = 'report_id',
  /** column name */
  street = 'street',
}

/** select columns of table "report_building" */
export enum report_building_select_column {
  /** column name */
  building_id = 'building_id',
  /** column name */
  report_id = 'report_id',
}

/** update columns of table "report_building" */
export enum report_building_update_column {
  /** column name */
  building_id = 'building_id',
  /** column name */
  report_id = 'report_id',
}

/** unique or primary key constraints on table "report" */
export enum report_constraint {
  /** unique or primary key constraint on columns "id" */
  report_instance_pkey = 'report_instance_pkey',
}

/** unique or primary key constraints on table "report_evidence" */
export enum report_evidence_constraint {
  /** unique or primary key constraint on columns "id" */
  report_evidence_pkey = 'report_evidence_pkey',
}

/** select columns of table "report_evidence" */
export enum report_evidence_select_column {
  /** column name */
  created_at = 'created_at',
  /** column name */
  file_id = 'file_id',
  /** column name */
  id = 'id',
  /** column name */
  report_id = 'report_id',
  /** column name */
  updated_at = 'updated_at',
  /** column name */
  url = 'url',
}

/** update columns of table "report_evidence" */
export enum report_evidence_update_column {
  /** column name */
  created_at = 'created_at',
  /** column name */
  file_id = 'file_id',
  /** column name */
  id = 'id',
  /** column name */
  report_id = 'report_id',
  /** column name */
  updated_at = 'updated_at',
  /** column name */
  url = 'url',
}

/** select columns of table "report_overview" */
export enum report_overview_select_column {
  /** column name */
  completed = 'completed',
  /** column name */
  created_at = 'created_at',
  /** column name */
  org_id = 'org_id',
  /** column name */
  portfolio_id = 'portfolio_id',
  /** column name */
  portfolio_name = 'portfolio_name',
  /** column name */
  progress = 'progress',
  /** column name */
  report_id = 'report_id',
  /** column name */
  report_template_excel_path_de = 'report_template_excel_path_de',
  /** column name */
  report_template_excel_path_en = 'report_template_excel_path_en',
  /** column name */
  report_template_id = 'report_template_id',
  /** column name */
  report_template_title = 'report_template_title',
  /** column name */
  responsible_user_first_name = 'responsible_user_first_name',
  /** column name */
  responsible_user_id = 'responsible_user_id',
  /** column name */
  responsible_user_last_name = 'responsible_user_last_name',
  /** column name */
  streets = 'streets',
}

/** unique or primary key constraints on table "report_question_evidence" */
export enum report_question_evidence_constraint {
  /** unique or primary key constraint on columns "id" */
  report_question_evidence_pkey = 'report_question_evidence_pkey',
}

/** select columns of table "report_question_evidence" */
export enum report_question_evidence_select_column {
  /** column name */
  created_at = 'created_at',
  /** column name */
  description = 'description',
  /** column name */
  id = 'id',
  /** column name */
  report_evidence_id = 'report_evidence_id',
  /** column name */
  report_template_question_id = 'report_template_question_id',
  /** column name */
  updated_at = 'updated_at',
}

/** update columns of table "report_question_evidence" */
export enum report_question_evidence_update_column {
  /** column name */
  created_at = 'created_at',
  /** column name */
  description = 'description',
  /** column name */
  id = 'id',
  /** column name */
  report_evidence_id = 'report_evidence_id',
  /** column name */
  report_template_question_id = 'report_template_question_id',
  /** column name */
  updated_at = 'updated_at',
}

/** unique or primary key constraints on table "report_score" */
export enum report_score_constraint {
  /** unique or primary key constraint on columns "id" */
  report_score_pkey = 'report_score_pkey',
  /** unique or primary key constraint on columns "report_id", "report_template_score_id" */
  report_score_report_template_score_id_report_id_key = 'report_score_report_template_score_id_report_id_key',
}

/** select columns of table "report_score" */
export enum report_score_select_column {
  /** column name */
  created_at = 'created_at',
  /** column name */
  id = 'id',
  /** column name */
  report_id = 'report_id',
  /** column name */
  report_template_score_id = 'report_template_score_id',
  /** column name */
  updated_at = 'updated_at',
  /** column name */
  value = 'value',
}

/** update columns of table "report_score" */
export enum report_score_update_column {
  /** column name */
  created_at = 'created_at',
  /** column name */
  id = 'id',
  /** column name */
  report_id = 'report_id',
  /** column name */
  report_template_score_id = 'report_template_score_id',
  /** column name */
  updated_at = 'updated_at',
  /** column name */
  value = 'value',
}

/** unique or primary key constraints on table "report_section_progress" */
export enum report_section_progress_constraint {
  /** unique or primary key constraint on columns "id" */
  report_section_progress_pkey = 'report_section_progress_pkey',
}

/** select columns of table "report_section_progress" */
export enum report_section_progress_select_column {
  /** column name */
  building_id = 'building_id',
  /** column name */
  id = 'id',
  /** column name */
  progress = 'progress',
  /** column name */
  report_id = 'report_id',
  /** column name */
  report_template_section_id = 'report_template_section_id',
}

/** update columns of table "report_section_progress" */
export enum report_section_progress_update_column {
  /** column name */
  building_id = 'building_id',
  /** column name */
  id = 'id',
  /** column name */
  progress = 'progress',
  /** column name */
  report_id = 'report_id',
  /** column name */
  report_template_section_id = 'report_template_section_id',
}

/** select columns of table "report" */
export enum report_select_column {
  /** column name */
  completed = 'completed',
  /** column name */
  created_at = 'created_at',
  /** column name */
  due_date = 'due_date',
  /** column name */
  end_date = 'end_date',
  /** column name */
  file_id = 'file_id',
  /** column name */
  id = 'id',
  /** column name */
  org_id = 'org_id',
  /** column name */
  progress = 'progress',
  /** column name */
  report_template_id = 'report_template_id',
  /** column name */
  responsible_user_id = 'responsible_user_id',
  /** column name */
  start_date = 'start_date',
  /** column name */
  updated_at = 'updated_at',
  /** column name */
  version = 'version',
}

/** select "report_aggregate_bool_exp_bool_and_arguments_columns" columns of table "report" */
export enum report_select_column_report_aggregate_bool_exp_bool_and_arguments_columns {
  /** column name */
  completed = 'completed',
}

/** select "report_aggregate_bool_exp_bool_or_arguments_columns" columns of table "report" */
export enum report_select_column_report_aggregate_bool_exp_bool_or_arguments_columns {
  /** column name */
  completed = 'completed',
}

/** unique or primary key constraints on table "report_template" */
export enum report_template_constraint {
  /** unique or primary key constraint on columns "id" */
  report_pkey = 'report_pkey',
}

/** unique or primary key constraints on table "report_template_question" */
export enum report_template_question_constraint {
  /** unique or primary key constraint on columns "id" */
  report_question_pkey = 'report_question_pkey',
}

/** unique or primary key constraints on table "report_template_question_option" */
export enum report_template_question_option_constraint {
  /** unique or primary key constraint on columns "id" */
  report_question_option_pkey = 'report_question_option_pkey',
  /** unique or primary key constraint on columns "order", "report_template_question_id" */
  report_question_option_question_id_order_key = 'report_question_option_question_id_order_key',
  /** unique or primary key constraint on columns "value", "report_template_question_id" */
  report_question_option_question_id_value_key = 'report_question_option_question_id_value_key',
}

/** select columns of table "report_template_question_option" */
export enum report_template_question_option_select_column {
  /** column name */
  id = 'id',
  /** column name */
  max_score = 'max_score',
  /** column name */
  order = 'order',
  /** column name */
  report_template_question_id = 'report_template_question_id',
  /** column name */
  value = 'value',
}

/** select "report_template_question_option_aggregate_bool_exp_avg_arguments_columns" columns of table "report_template_question_option" */
export enum report_template_question_option_select_column_report_template_question_option_aggregate_bool_exp_avg_arguments_columns {
  /** column name */
  max_score = 'max_score',
}

/** select "report_template_question_option_aggregate_bool_exp_corr_arguments_columns" columns of table "report_template_question_option" */
export enum report_template_question_option_select_column_report_template_question_option_aggregate_bool_exp_corr_arguments_columns {
  /** column name */
  max_score = 'max_score',
}

/** select "report_template_question_option_aggregate_bool_exp_covar_samp_arguments_columns" columns of table "report_template_question_option" */
export enum report_template_question_option_select_column_report_template_question_option_aggregate_bool_exp_covar_samp_arguments_columns {
  /** column name */
  max_score = 'max_score',
}

/** select "report_template_question_option_aggregate_bool_exp_max_arguments_columns" columns of table "report_template_question_option" */
export enum report_template_question_option_select_column_report_template_question_option_aggregate_bool_exp_max_arguments_columns {
  /** column name */
  max_score = 'max_score',
}

/** select "report_template_question_option_aggregate_bool_exp_min_arguments_columns" columns of table "report_template_question_option" */
export enum report_template_question_option_select_column_report_template_question_option_aggregate_bool_exp_min_arguments_columns {
  /** column name */
  max_score = 'max_score',
}

/** select "report_template_question_option_aggregate_bool_exp_stddev_samp_arguments_columns" columns of table "report_template_question_option" */
export enum report_template_question_option_select_column_report_template_question_option_aggregate_bool_exp_stddev_samp_arguments_columns {
  /** column name */
  max_score = 'max_score',
}

/** select "report_template_question_option_aggregate_bool_exp_sum_arguments_columns" columns of table "report_template_question_option" */
export enum report_template_question_option_select_column_report_template_question_option_aggregate_bool_exp_sum_arguments_columns {
  /** column name */
  max_score = 'max_score',
}

/** select "report_template_question_option_aggregate_bool_exp_var_samp_arguments_columns" columns of table "report_template_question_option" */
export enum report_template_question_option_select_column_report_template_question_option_aggregate_bool_exp_var_samp_arguments_columns {
  /** column name */
  max_score = 'max_score',
}

/** update columns of table "report_template_question_option" */
export enum report_template_question_option_update_column {
  /** column name */
  id = 'id',
  /** column name */
  max_score = 'max_score',
  /** column name */
  order = 'order',
  /** column name */
  report_template_question_id = 'report_template_question_id',
  /** column name */
  value = 'value',
}

/** select columns of table "report_template_question" */
export enum report_template_question_select_column {
  /** column name */
  autofill = 'autofill',
  /** column name */
  created_at = 'created_at',
  /** column name */
  description = 'description',
  /** column name */
  evidence_required = 'evidence_required',
  /** column name */
  id = 'id',
  /** column name */
  max_score = 'max_score',
  /** column name */
  metadata = 'metadata',
  /** column name */
  order = 'order',
  /** column name */
  parent_option_id = 'parent_option_id',
  /** column name */
  parent_section_id = 'parent_section_id',
  /** column name */
  question = 'question',
  /** column name */
  reference = 'reference',
  /** column name */
  type = 'type',
  /** column name */
  updated_at = 'updated_at',
}

/** select "report_template_question_aggregate_bool_exp_avg_arguments_columns" columns of table "report_template_question" */
export enum report_template_question_select_column_report_template_question_aggregate_bool_exp_avg_arguments_columns {
  /** column name */
  max_score = 'max_score',
}

/** select "report_template_question_aggregate_bool_exp_bool_and_arguments_columns" columns of table "report_template_question" */
export enum report_template_question_select_column_report_template_question_aggregate_bool_exp_bool_and_arguments_columns {
  /** column name */
  evidence_required = 'evidence_required',
}

/** select "report_template_question_aggregate_bool_exp_bool_or_arguments_columns" columns of table "report_template_question" */
export enum report_template_question_select_column_report_template_question_aggregate_bool_exp_bool_or_arguments_columns {
  /** column name */
  evidence_required = 'evidence_required',
}

/** select "report_template_question_aggregate_bool_exp_corr_arguments_columns" columns of table "report_template_question" */
export enum report_template_question_select_column_report_template_question_aggregate_bool_exp_corr_arguments_columns {
  /** column name */
  max_score = 'max_score',
}

/** select "report_template_question_aggregate_bool_exp_covar_samp_arguments_columns" columns of table "report_template_question" */
export enum report_template_question_select_column_report_template_question_aggregate_bool_exp_covar_samp_arguments_columns {
  /** column name */
  max_score = 'max_score',
}

/** select "report_template_question_aggregate_bool_exp_max_arguments_columns" columns of table "report_template_question" */
export enum report_template_question_select_column_report_template_question_aggregate_bool_exp_max_arguments_columns {
  /** column name */
  max_score = 'max_score',
}

/** select "report_template_question_aggregate_bool_exp_min_arguments_columns" columns of table "report_template_question" */
export enum report_template_question_select_column_report_template_question_aggregate_bool_exp_min_arguments_columns {
  /** column name */
  max_score = 'max_score',
}

/** select "report_template_question_aggregate_bool_exp_stddev_samp_arguments_columns" columns of table "report_template_question" */
export enum report_template_question_select_column_report_template_question_aggregate_bool_exp_stddev_samp_arguments_columns {
  /** column name */
  max_score = 'max_score',
}

/** select "report_template_question_aggregate_bool_exp_sum_arguments_columns" columns of table "report_template_question" */
export enum report_template_question_select_column_report_template_question_aggregate_bool_exp_sum_arguments_columns {
  /** column name */
  max_score = 'max_score',
}

/** select "report_template_question_aggregate_bool_exp_var_samp_arguments_columns" columns of table "report_template_question" */
export enum report_template_question_select_column_report_template_question_aggregate_bool_exp_var_samp_arguments_columns {
  /** column name */
  max_score = 'max_score',
}

/** unique or primary key constraints on table "report_template_question_type" */
export enum report_template_question_type_constraint {
  /** unique or primary key constraint on columns "id" */
  report_question_type_pkey = 'report_question_type_pkey',
}

export enum report_template_question_type_enum {
  DATE = 'DATE',
  FLOAT = 'FLOAT',
  INTEGER = 'INTEGER',
  MULTIPLE_CHOICE = 'MULTIPLE_CHOICE',
  PERCENTAGE = 'PERCENTAGE',
  SINGLE_CHOICE = 'SINGLE_CHOICE',
  TEXT = 'TEXT',
  YEAR = 'YEAR',
}

/** select columns of table "report_template_question_type" */
export enum report_template_question_type_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "report_template_question_type" */
export enum report_template_question_type_update_column {
  /** column name */
  id = 'id',
}

/** update columns of table "report_template_question" */
export enum report_template_question_update_column {
  /** column name */
  autofill = 'autofill',
  /** column name */
  created_at = 'created_at',
  /** column name */
  description = 'description',
  /** column name */
  evidence_required = 'evidence_required',
  /** column name */
  id = 'id',
  /** column name */
  max_score = 'max_score',
  /** column name */
  metadata = 'metadata',
  /** column name */
  order = 'order',
  /** column name */
  parent_option_id = 'parent_option_id',
  /** column name */
  parent_section_id = 'parent_section_id',
  /** column name */
  question = 'question',
  /** column name */
  reference = 'reference',
  /** column name */
  type = 'type',
  /** column name */
  updated_at = 'updated_at',
}

/** unique or primary key constraints on table "report_template_score" */
export enum report_template_score_constraint {
  /** unique or primary key constraint on columns "name", "report_template_id" */
  report_template_score_name_report_template_id_key = 'report_template_score_name_report_template_id_key',
  /** unique or primary key constraint on columns "id" */
  report_template_score_pkey = 'report_template_score_pkey',
  /** unique or primary key constraint on columns "reference", "report_template_id" */
  report_template_score_reference_report_template_id_key = 'report_template_score_reference_report_template_id_key',
  /** unique or primary key constraint on columns "report_template_id", "order" */
  report_template_score_report_template_id_order_key = 'report_template_score_report_template_id_order_key',
}

/** unique or primary key constraints on table "report_template_score_display_type" */
export enum report_template_score_display_type_constraint {
  /** unique or primary key constraint on columns "id" */
  report_template_score_display_type_pkey = 'report_template_score_display_type_pkey',
}

export enum report_template_score_display_type_enum {
  BOOLEAN = 'BOOLEAN',
  PERCENTAGE = 'PERCENTAGE',
  POINTS = 'POINTS',
}

/** select columns of table "report_template_score_display_type" */
export enum report_template_score_display_type_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "report_template_score_display_type" */
export enum report_template_score_display_type_update_column {
  /** column name */
  id = 'id',
}

/** select columns of table "report_template_score" */
export enum report_template_score_select_column {
  /** column name */
  created_at = 'created_at',
  /** column name */
  display = 'display',
  /** column name */
  display_type = 'display_type',
  /** column name */
  id = 'id',
  /** column name */
  max_score = 'max_score',
  /** column name */
  name = 'name',
  /** column name */
  order = 'order',
  /** column name */
  reference = 'reference',
  /** column name */
  report_template_id = 'report_template_id',
  /** column name */
  resolver = 'resolver',
  /** column name */
  updated_at = 'updated_at',
}

/** select "report_template_score_aggregate_bool_exp_bool_and_arguments_columns" columns of table "report_template_score" */
export enum report_template_score_select_column_report_template_score_aggregate_bool_exp_bool_and_arguments_columns {
  /** column name */
  display = 'display',
}

/** select "report_template_score_aggregate_bool_exp_bool_or_arguments_columns" columns of table "report_template_score" */
export enum report_template_score_select_column_report_template_score_aggregate_bool_exp_bool_or_arguments_columns {
  /** column name */
  display = 'display',
}

/** update columns of table "report_template_score" */
export enum report_template_score_update_column {
  /** column name */
  created_at = 'created_at',
  /** column name */
  display = 'display',
  /** column name */
  display_type = 'display_type',
  /** column name */
  id = 'id',
  /** column name */
  max_score = 'max_score',
  /** column name */
  name = 'name',
  /** column name */
  order = 'order',
  /** column name */
  reference = 'reference',
  /** column name */
  report_template_id = 'report_template_id',
  /** column name */
  resolver = 'resolver',
  /** column name */
  updated_at = 'updated_at',
}

/** unique or primary key constraints on table "report_template_section" */
export enum report_template_section_constraint {
  /** unique or primary key constraint on columns "id" */
  report_section_pkey = 'report_section_pkey',
}

/** select columns of table "report_template_section" */
export enum report_template_section_select_column {
  /** column name */
  created_at = 'created_at',
  /** column name */
  id = 'id',
  /** column name */
  name = 'name',
  /** column name */
  order = 'order',
  /** column name */
  parent_section_id = 'parent_section_id',
  /** column name */
  report_template_id = 'report_template_id',
  /** column name */
  target = 'target',
  /** column name */
  updated_at = 'updated_at',
}

/** unique or primary key constraints on table "report_template_section_target" */
export enum report_template_section_target_constraint {
  /** unique or primary key constraint on columns "id" */
  report_question_target_pkey = 'report_question_target_pkey',
}

export enum report_template_section_target_enum {
  BUILDING = 'BUILDING',
  ORG = 'ORG',
  PORTFOLIO = 'PORTFOLIO',
}

/** select columns of table "report_template_section_target" */
export enum report_template_section_target_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "report_template_section_target" */
export enum report_template_section_target_update_column {
  /** column name */
  id = 'id',
}

/** update columns of table "report_template_section" */
export enum report_template_section_update_column {
  /** column name */
  created_at = 'created_at',
  /** column name */
  id = 'id',
  /** column name */
  name = 'name',
  /** column name */
  order = 'order',
  /** column name */
  parent_section_id = 'parent_section_id',
  /** column name */
  report_template_id = 'report_template_id',
  /** column name */
  target = 'target',
  /** column name */
  updated_at = 'updated_at',
}

/** select columns of table "report_template" */
export enum report_template_select_column {
  /** column name */
  created_at = 'created_at',
  /** column name */
  description = 'description',
  /** column name */
  description_en = 'description_en',
  /** column name */
  excel_template_path_de = 'excel_template_path_de',
  /** column name */
  excel_template_path_en = 'excel_template_path_en',
  /** column name */
  id = 'id',
  /** column name */
  org_id = 'org_id',
  /** column name */
  single_building = 'single_building',
  /** column name */
  title = 'title',
  /** column name */
  updated_at = 'updated_at',
  /** column name */
  version = 'version',
}

/** select columns of table "report_template_to_all_questions" */
export enum report_template_to_all_questions_select_column {
  /** column name */
  question_id = 'question_id',
  /** column name */
  report_template_id = 'report_template_id',
}

/** update columns of table "report_template" */
export enum report_template_update_column {
  /** column name */
  created_at = 'created_at',
  /** column name */
  description = 'description',
  /** column name */
  description_en = 'description_en',
  /** column name */
  excel_template_path_de = 'excel_template_path_de',
  /** column name */
  excel_template_path_en = 'excel_template_path_en',
  /** column name */
  id = 'id',
  /** column name */
  org_id = 'org_id',
  /** column name */
  single_building = 'single_building',
  /** column name */
  title = 'title',
  /** column name */
  updated_at = 'updated_at',
  /** column name */
  version = 'version',
}

/** update columns of table "report" */
export enum report_update_column {
  /** column name */
  completed = 'completed',
  /** column name */
  created_at = 'created_at',
  /** column name */
  due_date = 'due_date',
  /** column name */
  end_date = 'end_date',
  /** column name */
  file_id = 'file_id',
  /** column name */
  id = 'id',
  /** column name */
  org_id = 'org_id',
  /** column name */
  progress = 'progress',
  /** column name */
  report_template_id = 'report_template_id',
  /** column name */
  responsible_user_id = 'responsible_user_id',
  /** column name */
  start_date = 'start_date',
  /** column name */
  updated_at = 'updated_at',
  /** column name */
  version = 'version',
}

/** unique or primary key constraints on table "role" */
export enum role_constraint {
  /** unique or primary key constraint on columns "id" */
  role_pkey = 'role_pkey',
}

export enum role_enum {
  API_READ = 'API_READ',
  API_WRITE = 'API_WRITE',
  BANKING_USER = 'BANKING_USER',
  DANGER_PUBLIC_ROLE = 'DANGER_PUBLIC_ROLE',
  ORG_ADMIN = 'ORG_ADMIN',
  RETOOL_ADMIN = 'RETOOL_ADMIN',
  RETOOL_USER = 'RETOOL_USER',
  UNAUTHORIZED = 'UNAUTHORIZED',
  USER = 'USER',
  admin = 'admin',
}

/** select columns of table "role" */
export enum role_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "role" */
export enum role_update_column {
  /** column name */
  id = 'id',
}

/** unique or primary key constraints on table "roof_type" */
export enum roof_type_constraint {
  /** unique or primary key constraint on columns "id" */
  roof_type_pkey = 'roof_type_pkey',
}

/** select columns of table "roof_type" */
export enum roof_type_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "roof_type" */
export enum roof_type_update_column {
  /** column name */
  id = 'id',
}

/** unique or primary key constraints on table "scenario" */
export enum scenario_constraint {
  /** unique or primary key constraint on columns "id" */
  scenario_pkey = 'scenario_pkey',
}

/** select columns of table "scenario" */
export enum scenario_select_column {
  /** column name */
  archived_at = 'archived_at',
  /** column name */
  cost_update_in_progress = 'cost_update_in_progress',
  /** column name */
  created_at = 'created_at',
  /** column name */
  description = 'description',
  /** column name */
  duplication_in_progress = 'duplication_in_progress',
  /** column name */
  id = 'id',
  /** column name */
  name = 'name',
  /** column name */
  org_id = 'org_id',
  /** column name */
  owner_id = 'owner_id',
  /** column name */
  portfolio_id = 'portfolio_id',
  /** column name */
  updated_at = 'updated_at',
}

/** update columns of table "scenario" */
export enum scenario_update_column {
  /** column name */
  archived_at = 'archived_at',
  /** column name */
  cost_update_in_progress = 'cost_update_in_progress',
  /** column name */
  created_at = 'created_at',
  /** column name */
  description = 'description',
  /** column name */
  duplication_in_progress = 'duplication_in_progress',
  /** column name */
  id = 'id',
  /** column name */
  name = 'name',
  /** column name */
  org_id = 'org_id',
  /** column name */
  owner_id = 'owner_id',
  /** column name */
  portfolio_id = 'portfolio_id',
  /** column name */
  updated_at = 'updated_at',
}

/** unique or primary key constraints on table "scenario_version" */
export enum scenario_version_constraint {
  /** unique or primary key constraint on columns "id", "scenario_id" */
  scenario_version_id_scenario_id_key = 'scenario_version_id_scenario_id_key',
  /** unique or primary key constraint on columns "id" */
  scenario_version_pkey = 'scenario_version_pkey',
  /** unique or primary key constraint on columns "cost_version_id", "scenario_id" */
  scenario_version_scenario_id_cost_version_id_key = 'scenario_version_scenario_id_cost_version_id_key',
}

/** select columns of table "scenario_version" */
export enum scenario_version_select_column {
  /** column name */
  cost_version_id = 'cost_version_id',
  /** column name */
  created_at = 'created_at',
  /** column name */
  id = 'id',
  /** column name */
  scenario_id = 'scenario_id',
  /** column name */
  status = 'status',
  /** column name */
  updated_at = 'updated_at',
}

/** unique or primary key constraints on table "scenario_version_status" */
export enum scenario_version_status_constraint {
  /** unique or primary key constraint on columns "id" */
  scenario_version_status_pkey = 'scenario_version_status_pkey',
}

export enum scenario_version_status_enum {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  INVALIDATED = 'INVALIDATED',
  PENDING = 'PENDING',
}

/** select columns of table "scenario_version_status" */
export enum scenario_version_status_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "scenario_version_status" */
export enum scenario_version_status_update_column {
  /** column name */
  id = 'id',
}

/** update columns of table "scenario_version" */
export enum scenario_version_update_column {
  /** column name */
  cost_version_id = 'cost_version_id',
  /** column name */
  created_at = 'created_at',
  /** column name */
  id = 'id',
  /** column name */
  scenario_id = 'scenario_id',
  /** column name */
  status = 'status',
  /** column name */
  updated_at = 'updated_at',
}

/** Generated enum from hasura. Look at the corresponding TS enum for description. */
export enum scope_perspective_enum {
  OC_LANDLORD = 'OC_LANDLORD',
  OC_TENANT = 'OC_TENANT',
  WHOLE_BUILDING = 'WHOLE_BUILDING',
}

/** unique or primary key constraints on table "shared_status_quo" */
export enum shared_status_quo_constraint {
  /** unique or primary key constraint on columns "hash" */
  shared_status_quo_hash_key = 'shared_status_quo_hash_key',
  /** unique or primary key constraint on columns "id" */
  shared_status_quo_pkey = 'shared_status_quo_pkey',
}

/** unique or primary key constraints on table "shared_status_quo_purpose" */
export enum shared_status_quo_purpose_constraint {
  /** unique or primary key constraint on columns "id" */
  shared_status_quo_purpose_pkey = 'shared_status_quo_purpose_pkey',
}

export enum shared_status_quo_purpose_enum {
  PDF_EXPORT = 'PDF_EXPORT',
  SHARE = 'SHARE',
}

/** select columns of table "shared_status_quo_purpose" */
export enum shared_status_quo_purpose_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "shared_status_quo_purpose" */
export enum shared_status_quo_purpose_update_column {
  /** column name */
  id = 'id',
}

/** select columns of table "shared_status_quo" */
export enum shared_status_quo_select_column {
  /** column name */
  access_token_hash = 'access_token_hash',
  /** column name */
  created_at = 'created_at',
  /** column name */
  data = 'data',
  /** column name */
  hash = 'hash',
  /** column name */
  id = 'id',
  /** column name */
  purpose = 'purpose',
}

/** update columns of table "shared_status_quo" */
export enum shared_status_quo_update_column {
  /** column name */
  access_token_hash = 'access_token_hash',
  /** column name */
  created_at = 'created_at',
  /** column name */
  data = 'data',
  /** column name */
  hash = 'hash',
  /** column name */
  id = 'id',
  /** column name */
  purpose = 'purpose',
}

export enum slf_compliance_factor_enum {
  CERTIFICATE = 'CERTIFICATE',
  EFFICIENCY_CLASS = 'EFFICIENCY_CLASS',
  EU_TAXONOMY = 'EU_TAXONOMY',
  REPORT = 'REPORT',
}

/** unique or primary key constraints on table "smart_meter_archive_reason" */
export enum smart_meter_archive_reason_constraint {
  /** unique or primary key constraint on columns "id" */
  smart_meter_archive_reason_pkey = 'smart_meter_archive_reason_pkey',
}

export enum smart_meter_archive_reason_enum {
  MANUALLY_ARCHIVED = 'MANUALLY_ARCHIVED',
  NOT_FOUND_IN_PROVIDER = 'NOT_FOUND_IN_PROVIDER',
}

/** select columns of table "smart_meter_archive_reason" */
export enum smart_meter_archive_reason_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "smart_meter_archive_reason" */
export enum smart_meter_archive_reason_update_column {
  /** column name */
  id = 'id',
}

/** unique or primary key constraints on table "smart_meter" */
export enum smart_meter_constraint {
  /** unique or primary key constraint on columns "id" */
  meter_pkey = 'meter_pkey',
}

/** unique or primary key constraints on table "smart_meter_health_status" */
export enum smart_meter_health_status_constraint {
  /** unique or primary key constraint on columns "id" */
  smart_meter_health_status_pkey = 'smart_meter_health_status_pkey',
}

export enum smart_meter_health_status_enum {
  AUTH_FAILED = 'AUTH_FAILED',
  HEALTHY = 'HEALTHY',
  PROVIDER_UNAVAILABLE = 'PROVIDER_UNAVAILABLE',
  RATE_LIMITED = 'RATE_LIMITED',
  UNKNOWN = 'UNKNOWN',
}

/** select columns of table "smart_meter_health_status" */
export enum smart_meter_health_status_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "smart_meter_health_status" */
export enum smart_meter_health_status_update_column {
  /** column name */
  id = 'id',
}

/** unique or primary key constraints on table "smart_meter_integration_building" */
export enum smart_meter_integration_building_constraint {
  /** unique or primary key constraint on columns "id" */
  meter_integration_building_pkey = 'meter_integration_building_pkey',
}

/** select columns of table "smart_meter_integration_building" */
export enum smart_meter_integration_building_select_column {
  /** column name */
  archive_reason_id = 'archive_reason_id',
  /** column name */
  archived_at = 'archived_at',
  /** column name */
  building_id = 'building_id',
  /** column name */
  consecutive_missed_syncs = 'consecutive_missed_syncs',
  /** column name */
  created_at = 'created_at',
  /** column name */
  external_id = 'external_id',
  /** column name */
  id = 'id',
  /** column name */
  last_seen_at = 'last_seen_at',
  /** column name */
  link_method_id = 'link_method_id',
  /** column name */
  link_state_id = 'link_state_id',
  /** column name */
  match_score = 'match_score',
  /** column name */
  properties = 'properties',
  /** column name */
  smart_meter_integration_id = 'smart_meter_integration_id',
  /** column name */
  suggested_building_id = 'suggested_building_id',
  /** column name */
  updated_at = 'updated_at',
}

/** select "smart_meter_integration_building_aggregate_bool_exp_avg_arguments_columns" columns of table "smart_meter_integration_building" */
export enum smart_meter_integration_building_select_column_smart_meter_integration_building_aggregate_bool_exp_avg_arguments_columns {
  /** column name */
  match_score = 'match_score',
}

/** select "smart_meter_integration_building_aggregate_bool_exp_corr_arguments_columns" columns of table "smart_meter_integration_building" */
export enum smart_meter_integration_building_select_column_smart_meter_integration_building_aggregate_bool_exp_corr_arguments_columns {
  /** column name */
  match_score = 'match_score',
}

/** select "smart_meter_integration_building_aggregate_bool_exp_covar_samp_arguments_columns" columns of table "smart_meter_integration_building" */
export enum smart_meter_integration_building_select_column_smart_meter_integration_building_aggregate_bool_exp_covar_samp_arguments_columns {
  /** column name */
  match_score = 'match_score',
}

/** select "smart_meter_integration_building_aggregate_bool_exp_max_arguments_columns" columns of table "smart_meter_integration_building" */
export enum smart_meter_integration_building_select_column_smart_meter_integration_building_aggregate_bool_exp_max_arguments_columns {
  /** column name */
  match_score = 'match_score',
}

/** select "smart_meter_integration_building_aggregate_bool_exp_min_arguments_columns" columns of table "smart_meter_integration_building" */
export enum smart_meter_integration_building_select_column_smart_meter_integration_building_aggregate_bool_exp_min_arguments_columns {
  /** column name */
  match_score = 'match_score',
}

/** select "smart_meter_integration_building_aggregate_bool_exp_stddev_samp_arguments_columns" columns of table "smart_meter_integration_building" */
export enum smart_meter_integration_building_select_column_smart_meter_integration_building_aggregate_bool_exp_stddev_samp_arguments_columns {
  /** column name */
  match_score = 'match_score',
}

/** select "smart_meter_integration_building_aggregate_bool_exp_sum_arguments_columns" columns of table "smart_meter_integration_building" */
export enum smart_meter_integration_building_select_column_smart_meter_integration_building_aggregate_bool_exp_sum_arguments_columns {
  /** column name */
  match_score = 'match_score',
}

/** select "smart_meter_integration_building_aggregate_bool_exp_var_samp_arguments_columns" columns of table "smart_meter_integration_building" */
export enum smart_meter_integration_building_select_column_smart_meter_integration_building_aggregate_bool_exp_var_samp_arguments_columns {
  /** column name */
  match_score = 'match_score',
}

/** update columns of table "smart_meter_integration_building" */
export enum smart_meter_integration_building_update_column {
  /** column name */
  archive_reason_id = 'archive_reason_id',
  /** column name */
  archived_at = 'archived_at',
  /** column name */
  building_id = 'building_id',
  /** column name */
  consecutive_missed_syncs = 'consecutive_missed_syncs',
  /** column name */
  created_at = 'created_at',
  /** column name */
  external_id = 'external_id',
  /** column name */
  id = 'id',
  /** column name */
  last_seen_at = 'last_seen_at',
  /** column name */
  link_method_id = 'link_method_id',
  /** column name */
  link_state_id = 'link_state_id',
  /** column name */
  match_score = 'match_score',
  /** column name */
  properties = 'properties',
  /** column name */
  smart_meter_integration_id = 'smart_meter_integration_id',
  /** column name */
  suggested_building_id = 'suggested_building_id',
  /** column name */
  updated_at = 'updated_at',
}

/** unique or primary key constraints on table "smart_meter_integration" */
export enum smart_meter_integration_constraint {
  /** unique or primary key constraint on columns "id" */
  meter_integration_pkey = 'meter_integration_pkey',
}

/** select columns of table "smart_meter_integration" */
export enum smart_meter_integration_select_column {
  /** column name */
  api_key = 'api_key',
  /** column name */
  api_metadata = 'api_metadata',
  /** column name */
  api_secret_encrypted = 'api_secret_encrypted',
  /** column name */
  created_at = 'created_at',
  /** column name */
  credential_version = 'credential_version',
  /** column name */
  disabled = 'disabled',
  /** column name */
  health_status_id = 'health_status_id',
  /** column name */
  id = 'id',
  /** column name */
  last_sync = 'last_sync',
  /** column name */
  meter_integration_type_id = 'meter_integration_type_id',
  /** column name */
  org_id = 'org_id',
  /** column name */
  sync_interval_id = 'sync_interval_id',
  /** column name */
  sync_state_id = 'sync_state_id',
  /** column name */
  tenant_id = 'tenant_id',
  /** column name */
  tenant_name = 'tenant_name',
  /** column name */
  updated_at = 'updated_at',
}

/** unique or primary key constraints on table "smart_meter_integration_type" */
export enum smart_meter_integration_type_constraint {
  /** unique or primary key constraint on columns "id" */
  meter_integration_type_pkey = 'meter_integration_type_pkey',
}

export enum smart_meter_integration_type_enum {
  BASCLOUD = 'BASCLOUD',
  QIVALO = 'QIVALO',
}

/** select columns of table "smart_meter_integration_type" */
export enum smart_meter_integration_type_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "smart_meter_integration_type" */
export enum smart_meter_integration_type_update_column {
  /** column name */
  id = 'id',
}

/** update columns of table "smart_meter_integration" */
export enum smart_meter_integration_update_column {
  /** column name */
  api_key = 'api_key',
  /** column name */
  api_metadata = 'api_metadata',
  /** column name */
  api_secret_encrypted = 'api_secret_encrypted',
  /** column name */
  created_at = 'created_at',
  /** column name */
  credential_version = 'credential_version',
  /** column name */
  disabled = 'disabled',
  /** column name */
  health_status_id = 'health_status_id',
  /** column name */
  id = 'id',
  /** column name */
  last_sync = 'last_sync',
  /** column name */
  meter_integration_type_id = 'meter_integration_type_id',
  /** column name */
  org_id = 'org_id',
  /** column name */
  sync_interval_id = 'sync_interval_id',
  /** column name */
  sync_state_id = 'sync_state_id',
  /** column name */
  tenant_id = 'tenant_id',
  /** column name */
  tenant_name = 'tenant_name',
  /** column name */
  updated_at = 'updated_at',
}

/** unique or primary key constraints on table "smart_meter_link_method" */
export enum smart_meter_link_method_constraint {
  /** unique or primary key constraint on columns "id" */
  smart_meter_link_method_pkey = 'smart_meter_link_method_pkey',
}

export enum smart_meter_link_method_enum {
  MANUAL = 'MANUAL',
}

/** select columns of table "smart_meter_link_method" */
export enum smart_meter_link_method_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "smart_meter_link_method" */
export enum smart_meter_link_method_update_column {
  /** column name */
  id = 'id',
}

/** unique or primary key constraints on table "smart_meter_link_state" */
export enum smart_meter_link_state_constraint {
  /** unique or primary key constraint on columns "id" */
  smart_meter_link_state_pkey = 'smart_meter_link_state_pkey',
}

export enum smart_meter_link_state_enum {
  LINKED = 'LINKED',
  UNLINKED = 'UNLINKED',
}

/** select columns of table "smart_meter_link_state" */
export enum smart_meter_link_state_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "smart_meter_link_state" */
export enum smart_meter_link_state_update_column {
  /** column name */
  id = 'id',
}

/** select columns of table "smart_meter" */
export enum smart_meter_select_column {
  /** column name */
  archive_reason_id = 'archive_reason_id',
  /** column name */
  archived_at = 'archived_at',
  /** column name */
  area_id = 'area_id',
  /** column name */
  consecutive_failure_count = 'consecutive_failure_count',
  /** column name */
  consecutive_missed_syncs = 'consecutive_missed_syncs',
  /** column name */
  consumption_frequency_type_id = 'consumption_frequency_type_id',
  /** column name */
  consumption_type_id = 'consumption_type_id',
  /** column name */
  consumption_unit_id = 'consumption_unit_id',
  /** column name */
  created_at = 'created_at',
  /** column name */
  description = 'description',
  /** column name */
  external_id = 'external_id',
  /** column name */
  id = 'id',
  /** column name */
  last_seen_at = 'last_seen_at',
  /** column name */
  smart_meter_integration_building_id = 'smart_meter_integration_building_id',
  /** column name */
  updated_at = 'updated_at',
}

/** unique or primary key constraints on table "smart_meter_sync_checkpoint" */
export enum smart_meter_sync_checkpoint_constraint {
  /** unique or primary key constraint on columns "id" */
  smart_meter_sync_checkpoint_pkey = 'smart_meter_sync_checkpoint_pkey',
  /** unique or primary key constraint on columns "smart_meter_id" */
  smart_meter_sync_checkpoint_smart_meter_id_key = 'smart_meter_sync_checkpoint_smart_meter_id_key',
}

/** select columns of table "smart_meter_sync_checkpoint" */
export enum smart_meter_sync_checkpoint_select_column {
  /** column name */
  id = 'id',
  /** column name */
  last_synced_to = 'last_synced_to',
  /** column name */
  smart_meter_id = 'smart_meter_id',
  /** column name */
  updated_at = 'updated_at',
}

/** update columns of table "smart_meter_sync_checkpoint" */
export enum smart_meter_sync_checkpoint_update_column {
  /** column name */
  id = 'id',
  /** column name */
  last_synced_to = 'last_synced_to',
  /** column name */
  smart_meter_id = 'smart_meter_id',
  /** column name */
  updated_at = 'updated_at',
}

/** unique or primary key constraints on table "smart_meter_sync_interval" */
export enum smart_meter_sync_interval_constraint {
  /** unique or primary key constraint on columns "id" */
  smart_meter_sync_interval_pkey = 'smart_meter_sync_interval_pkey',
}

export enum smart_meter_sync_interval_enum {
  DAILY = 'DAILY',
  HOURLY = 'HOURLY',
  MONTHLY = 'MONTHLY',
  WEEKLY = 'WEEKLY',
}

/** select columns of table "smart_meter_sync_interval" */
export enum smart_meter_sync_interval_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "smart_meter_sync_interval" */
export enum smart_meter_sync_interval_update_column {
  /** column name */
  id = 'id',
}

/** unique or primary key constraints on table "smart_meter_sync_state" */
export enum smart_meter_sync_state_constraint {
  /** unique or primary key constraint on columns "id" */
  sync_state_pkey = 'sync_state_pkey',
}

export enum smart_meter_sync_state_enum {
  IN_SYNC = 'IN_SYNC',
  OUT_OF_SYNC = 'OUT_OF_SYNC',
  SYNCING = 'SYNCING',
}

/** select columns of table "smart_meter_sync_state" */
export enum smart_meter_sync_state_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "smart_meter_sync_state" */
export enum smart_meter_sync_state_update_column {
  /** column name */
  id = 'id',
}

/** update columns of table "smart_meter" */
export enum smart_meter_update_column {
  /** column name */
  archive_reason_id = 'archive_reason_id',
  /** column name */
  archived_at = 'archived_at',
  /** column name */
  area_id = 'area_id',
  /** column name */
  consecutive_failure_count = 'consecutive_failure_count',
  /** column name */
  consecutive_missed_syncs = 'consecutive_missed_syncs',
  /** column name */
  consumption_frequency_type_id = 'consumption_frequency_type_id',
  /** column name */
  consumption_type_id = 'consumption_type_id',
  /** column name */
  consumption_unit_id = 'consumption_unit_id',
  /** column name */
  created_at = 'created_at',
  /** column name */
  description = 'description',
  /** column name */
  external_id = 'external_id',
  /** column name */
  id = 'id',
  /** column name */
  last_seen_at = 'last_seen_at',
  /** column name */
  smart_meter_integration_building_id = 'smart_meter_integration_building_id',
  /** column name */
  updated_at = 'updated_at',
}

/** Source kind of a consumption overview row (meter readings surface as SMART_METER). */
export enum source_consumption_enum {
  ESTIMATION = 'ESTIMATION',
  INVOICE = 'INVOICE',
  MANUAL_ENTRY = 'MANUAL_ENTRY',
  SMART_METER = 'SMART_METER',
}

/** Status of a consumption overview row. Rows on an inactive building are INACTIVE (overrides all other states); other consumption-backed rows are ACTIVE; invoice rows without complete consumption data are PROCESSING, NEEDS_REVIEW, FAILED or DUPLICATED. */
export enum status_consumption_enum {
  ACTIVE = 'ACTIVE',
  ARCHIVED = 'ARCHIVED',
  DUPLICATED = 'DUPLICATED',
  FAILED = 'FAILED',
  INACTIVE = 'INACTIVE',
  NEEDS_REVIEW = 'NEEDS_REVIEW',
  PROCESSING = 'PROCESSING',
}

/** Which Analysis tab the exported PDF should mirror. */
export enum status_quo_pdf_export_tab_enum {
  CONSUMPTION = 'CONSUMPTION',
  DEMAND = 'DEMAND',
}

/** unique or primary key constraints on table "subsidy_category_type" */
export enum subsidy_category_type_constraint {
  /** unique or primary key constraint on columns "id" */
  subsidy_category_type_pkey = 'subsidy_category_type_pkey',
}

export enum subsidy_category_type_enum {
  ENVELOPE_CEILING = 'ENVELOPE_CEILING',
  ENVELOPE_FLOOR = 'ENVELOPE_FLOOR',
  ENVELOPE_ROOF = 'ENVELOPE_ROOF',
  ENVELOPE_UNCLASSIFIED = 'ENVELOPE_UNCLASSIFIED',
  ENVELOPE_WALL = 'ENVELOPE_WALL',
  ENVELOPE_WINDOW = 'ENVELOPE_WINDOW',
  SYSTEMS_HEATING = 'SYSTEMS_HEATING',
  SYSTEMS_PV = 'SYSTEMS_PV',
  SYSTEMS_VENTILATION = 'SYSTEMS_VENTILATION',
}

/** select columns of table "subsidy_category_type" */
export enum subsidy_category_type_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "subsidy_category_type" */
export enum subsidy_category_type_update_column {
  /** column name */
  id = 'id',
}

/** unique or primary key constraints on table "subsidy" */
export enum subsidy_constraint {
  /** unique or primary key constraint on columns "id" */
  subsidy_pkey = 'subsidy_pkey',
  /** unique or primary key constraint on columns "provider", "provider_id" */
  subsidy_provider_provider_id_key = 'subsidy_provider_provider_id_key',
}

/** unique or primary key constraints on table "subsidy_region_type" */
export enum subsidy_region_type_constraint {
  /** unique or primary key constraint on columns "id" */
  subsidy_region_pkey = 'subsidy_region_pkey',
}

export enum subsidy_region_type_enum {
  FEDERAL = 'FEDERAL',
  LOCAL = 'LOCAL',
  OTHER = 'OTHER',
  STATE = 'STATE',
}

/** select columns of table "subsidy_region_type" */
export enum subsidy_region_type_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "subsidy_region_type" */
export enum subsidy_region_type_update_column {
  /** column name */
  id = 'id',
}

/** select columns of table "subsidy" */
export enum subsidy_select_column {
  /** column name */
  amount_description = 'amount_description',
  /** column name */
  created_at = 'created_at',
  /** column name */
  email = 'email',
  /** column name */
  id = 'id',
  /** column name */
  provider = 'provider',
  /** column name */
  provider_id = 'provider_id',
  /** column name */
  provider_updated_at = 'provider_updated_at',
  /** column name */
  region_type = 'region_type',
  /** column name */
  title = 'title',
  /** column name */
  type = 'type',
  /** column name */
  updated_at = 'updated_at',
  /** column name */
  url = 'url',
}

/** unique or primary key constraints on table "subsidy_subsidy_category" */
export enum subsidy_subsidy_category_constraint {
  /** unique or primary key constraint on columns "id" */
  subsidy_subsidy_category_pkey = 'subsidy_subsidy_category_pkey',
  /** unique or primary key constraint on columns "subsidy_category_type", "subsidy_id" */
  subsidy_subsidy_category_subsidy_id_subsidy_category_type_key = 'subsidy_subsidy_category_subsidy_id_subsidy_category_type_key',
}

/** select columns of table "subsidy_subsidy_category" */
export enum subsidy_subsidy_category_select_column {
  /** column name */
  id = 'id',
  /** column name */
  subsidy_category_type = 'subsidy_category_type',
  /** column name */
  subsidy_id = 'subsidy_id',
}

/** update columns of table "subsidy_subsidy_category" */
export enum subsidy_subsidy_category_update_column {
  /** column name */
  id = 'id',
  /** column name */
  subsidy_category_type = 'subsidy_category_type',
  /** column name */
  subsidy_id = 'subsidy_id',
}

/** unique or primary key constraints on table "subsidy_type" */
export enum subsidy_type_constraint {
  /** unique or primary key constraint on columns "id" */
  subsidy_type_pkey = 'subsidy_type_pkey',
}

export enum subsidy_type_enum {
  BOND = 'BOND',
  GRANT = 'GRANT',
  LOAN = 'LOAN',
}

/** select columns of table "subsidy_type" */
export enum subsidy_type_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "subsidy_type" */
export enum subsidy_type_update_column {
  /** column name */
  id = 'id',
}

/** update columns of table "subsidy" */
export enum subsidy_update_column {
  /** column name */
  amount_description = 'amount_description',
  /** column name */
  created_at = 'created_at',
  /** column name */
  email = 'email',
  /** column name */
  id = 'id',
  /** column name */
  provider = 'provider',
  /** column name */
  provider_id = 'provider_id',
  /** column name */
  provider_updated_at = 'provider_updated_at',
  /** column name */
  region_type = 'region_type',
  /** column name */
  title = 'title',
  /** column name */
  type = 'type',
  /** column name */
  updated_at = 'updated_at',
  /** column name */
  url = 'url',
}

/** unique or primary key constraints on table "target_definition" */
export enum target_definition_constraint {
  /** unique or primary key constraint on columns "target_definition_type_id", "custom_target_id", "year" */
  target_definition_custom_target_id_target_definition_type_i_key = 'target_definition_custom_target_id_target_definition_type_i_key',
  /** unique or primary key constraint on columns "id" */
  target_definition_pkey = 'target_definition_pkey',
}

/** select columns of table "target_definition" */
export enum target_definition_select_column {
  /** column name */
  custom_target_id = 'custom_target_id',
  /** column name */
  id = 'id',
  /** column name */
  target_definition_type_id = 'target_definition_type_id',
  /** column name */
  value = 'value',
  /** column name */
  year = 'year',
}

/** unique or primary key constraints on table "target_definition_type" */
export enum target_definition_type_constraint {
  /** unique or primary key constraint on columns "id" */
  target_definition_type_pkey = 'target_definition_type_pkey',
}

/** Generated enum from hasura. Look at the corresponding TS enum for description. */
export enum target_definition_type_enum {
  CO2_INTENSITY = 'CO2_INTENSITY',
  FINAL_ENERGY = 'FINAL_ENERGY',
  PRIMARY_ENERGY = 'PRIMARY_ENERGY',
}

/** select columns of table "target_definition_type" */
export enum target_definition_type_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "target_definition_type" */
export enum target_definition_type_update_column {
  /** column name */
  id = 'id',
}

/** update columns of table "target_definition" */
export enum target_definition_update_column {
  /** column name */
  custom_target_id = 'custom_target_id',
  /** column name */
  id = 'id',
  /** column name */
  target_definition_type_id = 'target_definition_type_id',
  /** column name */
  value = 'value',
  /** column name */
  year = 'year',
}

/** unique or primary key constraints on table "tax_bracket" */
export enum tax_bracket_constraint {
  /** unique or primary key constraint on columns "id" */
  tax_bracket_pkey = 'tax_bracket_pkey',
}

export enum tax_bracket_enum {
  _10_90 = '_10_90',
  _20_80 = '_20_80',
  _30_70 = '_30_70',
  _40_60 = '_40_60',
  _50_50 = '_50_50',
  _60_40 = '_60_40',
  _70_30 = '_70_30',
  _80_20 = '_80_20',
  _90_10 = '_90_10',
  _100_0 = '_100_0',
}

/** select columns of table "tax_bracket" */
export enum tax_bracket_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "tax_bracket" */
export enum tax_bracket_update_column {
  /** column name */
  id = 'id',
}

/** unique or primary key constraints on table "type_of_use" */
export enum type_of_use_constraint {
  /** unique or primary key constraint on columns "id" */
  type_of_use_pkey = 'type_of_use_pkey',
}

export enum type_of_use_enum {
  ADMINISTRATIVE_BUILDING = 'ADMINISTRATIVE_BUILDING',
  CAFETERIA = 'CAFETERIA',
  CENTRAL_SERVICE_BUILDING = 'CENTRAL_SERVICE_BUILDING',
  COLD_WAREHOUSE = 'COLD_WAREHOUSE',
  COMMUNAL_ACCOMMODATION = 'COMMUNAL_ACCOMMODATION',
  DATA_CENTER = 'DATA_CENTER',
  DOCTOR_OFFICE = 'DOCTOR_OFFICE',
  DORM = 'DORM',
  EDUCATION_INSTITUTION = 'EDUCATION_INSTITUTION',
  ELDERLY_DAYCARE_CENTER = 'ELDERLY_DAYCARE_CENTER',
  EVENT_HALL = 'EVENT_HALL',
  EXHIBITION_BUILDING = 'EXHIBITION_BUILDING',
  EXHIBITION_HALL = 'EXHIBITION_HALL',
  FIRE_DEPARTMENT = 'FIRE_DEPARTMENT',
  GYM = 'GYM',
  HOTEL = 'HOTEL',
  LABORATORY_AND_RESEARCH_BUILDING = 'LABORATORY_AND_RESEARCH_BUILDING',
  LECTURE_AND_TEACHING_BUILDING = 'LECTURE_AND_TEACHING_BUILDING',
  LEISURE_AND_COMMUNITY_FACILITY = 'LEISURE_AND_COMMUNITY_FACILITY',
  LIBRARY = 'LIBRARY',
  LOGISTIC_STORAGE_OPERATION_BUILDING = 'LOGISTIC_STORAGE_OPERATION_BUILDING',
  MEDICAL_AND_NURSING_FACILITY = 'MEDICAL_AND_NURSING_FACILITY',
  MFH = 'MFH',
  OUTDOOR_SPORT_FACILITY = 'OUTDOOR_SPORT_FACILITY',
  PARKING_GARAGE = 'PARKING_GARAGE',
  POLICE_STATION = 'POLICE_STATION',
  PRODUCTION_AND_WORKSHOP_BUILDING = 'PRODUCTION_AND_WORKSHOP_BUILDING',
  REHABILITATION_CENTER = 'REHABILITATION_CENTER',
  RESTAURANT = 'RESTAURANT',
  RETAIL_FOOD = 'RETAIL_FOOD',
  RETAIL_NON_FOOD = 'RETAIL_NON_FOOD',
  RIDING_AND_ICE_SPORT_HALL = 'RIDING_AND_ICE_SPORT_HALL',
  SFH = 'SFH',
  SHOPPING_CENTER = 'SHOPPING_CENTER',
  SPORT_AND_LEISURE_BUILDING = 'SPORT_AND_LEISURE_BUILDING',
  TRANSPORT_TERMINAL = 'TRANSPORT_TERMINAL',
  UNIVERSITY_AND_RESEARCH_INSTITUTION = 'UNIVERSITY_AND_RESEARCH_INSTITUTION',
  UTILITY_AND_INFRASTRUCTURE_BUILDING = 'UTILITY_AND_INFRASTRUCTURE_BUILDING',
  UTILITY_SERVICE_BUILDING = 'UTILITY_SERVICE_BUILDING',
  VEHICLE_FLEET_FACILITY = 'VEHICLE_FLEET_FACILITY',
  VEHICLE_SERVICE_AND_INSPECTION_FACILITY = 'VEHICLE_SERVICE_AND_INSPECTION_FACILITY',
  VEHICLE_STORAGE_HALL = 'VEHICLE_STORAGE_HALL',
  YOUTH_CENTER = 'YOUTH_CENTER',
}

/** select columns of table "type_of_use" */
export enum type_of_use_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "type_of_use" */
export enum type_of_use_update_column {
  /** column name */
  id = 'id',
}

/** unique or primary key constraints on table "user_building_permission" */
export enum user_building_permission_constraint {
  /** unique or primary key constraint on columns "id" */
  user_building_permission_pkey = 'user_building_permission_pkey',
  /** unique or primary key constraint on columns "user_id", "building_id" */
  user_building_permission_user_id_building_id_key = 'user_building_permission_user_id_building_id_key',
}

/** select columns of table "user_building_permission" */
export enum user_building_permission_select_column {
  /** column name */
  building_id = 'building_id',
  /** column name */
  created_at = 'created_at',
  /** column name */
  id = 'id',
  /** column name */
  read = 'read',
  /** column name */
  updated_at = 'updated_at',
  /** column name */
  user_id = 'user_id',
  /** column name */
  write = 'write',
}

/** select "user_building_permission_aggregate_bool_exp_bool_and_arguments_columns" columns of table "user_building_permission" */
export enum user_building_permission_select_column_user_building_permission_aggregate_bool_exp_bool_and_arguments_columns {
  /** column name */
  read = 'read',
  /** column name */
  write = 'write',
}

/** select "user_building_permission_aggregate_bool_exp_bool_or_arguments_columns" columns of table "user_building_permission" */
export enum user_building_permission_select_column_user_building_permission_aggregate_bool_exp_bool_or_arguments_columns {
  /** column name */
  read = 'read',
  /** column name */
  write = 'write',
}

/** update columns of table "user_building_permission" */
export enum user_building_permission_update_column {
  /** column name */
  building_id = 'building_id',
  /** column name */
  created_at = 'created_at',
  /** column name */
  id = 'id',
  /** column name */
  read = 'read',
  /** column name */
  updated_at = 'updated_at',
  /** column name */
  user_id = 'user_id',
  /** column name */
  write = 'write',
}

/** unique or primary key constraints on table "user" */
export enum user_constraint {
  /** unique or primary key constraint on columns "id" */
  users_pkey = 'users_pkey',
}

/** unique or primary key constraints on table "user_portfolio_permission" */
export enum user_portfolio_permission_constraint {
  /** unique or primary key constraint on columns "id" */
  user_portfolio_permission_pkey = 'user_portfolio_permission_pkey',
  /** unique or primary key constraint on columns "user_id", "portfolio_id" */
  user_portfolio_permission_user_id_portfolio_id_key = 'user_portfolio_permission_user_id_portfolio_id_key',
}

/** select columns of table "user_portfolio_permission" */
export enum user_portfolio_permission_select_column {
  /** column name */
  created_at = 'created_at',
  /** column name */
  id = 'id',
  /** column name */
  portfolio_id = 'portfolio_id',
  /** column name */
  read = 'read',
  /** column name */
  updated_at = 'updated_at',
  /** column name */
  user_id = 'user_id',
  /** column name */
  write = 'write',
}

/** select "user_portfolio_permission_aggregate_bool_exp_bool_and_arguments_columns" columns of table "user_portfolio_permission" */
export enum user_portfolio_permission_select_column_user_portfolio_permission_aggregate_bool_exp_bool_and_arguments_columns {
  /** column name */
  read = 'read',
  /** column name */
  write = 'write',
}

/** select "user_portfolio_permission_aggregate_bool_exp_bool_or_arguments_columns" columns of table "user_portfolio_permission" */
export enum user_portfolio_permission_select_column_user_portfolio_permission_aggregate_bool_exp_bool_or_arguments_columns {
  /** column name */
  read = 'read',
  /** column name */
  write = 'write',
}

/** update columns of table "user_portfolio_permission" */
export enum user_portfolio_permission_update_column {
  /** column name */
  created_at = 'created_at',
  /** column name */
  id = 'id',
  /** column name */
  portfolio_id = 'portfolio_id',
  /** column name */
  read = 'read',
  /** column name */
  updated_at = 'updated_at',
  /** column name */
  user_id = 'user_id',
  /** column name */
  write = 'write',
}

/** select columns of table "user" */
export enum user_select_column {
  /** column name */
  auth0_id = 'auth0_id',
  /** column name */
  created_at = 'created_at',
  /** column name */
  email = 'email',
  /** column name */
  first_name = 'first_name',
  /** column name */
  id = 'id',
  /** column name */
  is_active = 'is_active',
  /** column name */
  language = 'language',
  /** column name */
  last_name = 'last_name',
  /** column name */
  org_id = 'org_id',
  /** column name */
  role_description = 'role_description',
  /** column name */
  role_id = 'role_id',
  /** column name */
  updated_at = 'updated_at',
}

/** select "user_aggregate_bool_exp_bool_and_arguments_columns" columns of table "user" */
export enum user_select_column_user_aggregate_bool_exp_bool_and_arguments_columns {
  /** column name */
  is_active = 'is_active',
}

/** select "user_aggregate_bool_exp_bool_or_arguments_columns" columns of table "user" */
export enum user_select_column_user_aggregate_bool_exp_bool_or_arguments_columns {
  /** column name */
  is_active = 'is_active',
}

/** update columns of table "user" */
export enum user_update_column {
  /** column name */
  auth0_id = 'auth0_id',
  /** column name */
  created_at = 'created_at',
  /** column name */
  email = 'email',
  /** column name */
  first_name = 'first_name',
  /** column name */
  id = 'id',
  /** column name */
  is_active = 'is_active',
  /** column name */
  language = 'language',
  /** column name */
  last_name = 'last_name',
  /** column name */
  org_id = 'org_id',
  /** column name */
  role_description = 'role_description',
  /** column name */
  role_id = 'role_id',
  /** column name */
  updated_at = 'updated_at',
}

/** Generated enum from hasura. Look at the corresponding TS enum for description. */
export enum ventilation_supported_technology_enum {
  CENTRAL_EXHAUST = 'CENTRAL_EXHAUST',
  CENTRAL_SUPPLY_EXHAUST_WITH_HRU = 'CENTRAL_SUPPLY_EXHAUST_WITH_HRU',
  DECENTRAL_SINGLE_ROOM_VENTILATION_UNITS_WITH_HRU = 'DECENTRAL_SINGLE_ROOM_VENTILATION_UNITS_WITH_HRU',
}

/** unique or primary key constraints on table "window_type" */
export enum window_type_constraint {
  /** unique or primary key constraint on columns "id" */
  window_type_pkey = 'window_type_pkey',
}

/** select columns of table "window_type" */
export enum window_type_select_column {
  /** column name */
  id = 'id',
}

/** update columns of table "window_type" */
export enum window_type_update_column {
  /** column name */
  id = 'id',
}
